import Long from 'long';
import _m0 from 'protobufjs/minimal';
import { grpc } from '@improbable-eng/grpc-web';
import React, { createContext, useContext, useMemo, useEffect } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

/* eslint-disable */
/** Benchmark name. Ideally, a name should be short and unambiguous with unit included. */
var BenchmarkName;
(function (BenchmarkName) {
    BenchmarkName[BenchmarkName["UNKNOWN_BENCHMARK"] = 0] = "UNKNOWN_BENCHMARK";
    BenchmarkName[BenchmarkName["CPU_GFLOPS"] = 1] = "CPU_GFLOPS";
    /** GPU_GFLOPS - GFLOPS when performing single unit operations */
    BenchmarkName[BenchmarkName["GPU_GFLOPS"] = 2] = "GPU_GFLOPS";
    /** GPU_GFLOPS_VECTOR - GFLOPS when performing vector operations */
    BenchmarkName[BenchmarkName["GPU_GFLOPS_VECTOR"] = 3] = "GPU_GFLOPS_VECTOR";
    /** GPU_SYSTEM_CAPABILITIES - JSON object containing GPU feature support derived from OpenGL/CL API */
    BenchmarkName[BenchmarkName["GPU_SYSTEM_CAPABILITIES"] = 4] = "GPU_SYSTEM_CAPABILITIES";
    /** GPU_VERTEX_THROUGHPUT - Vertices per second */
    BenchmarkName[BenchmarkName["GPU_VERTEX_THROUGHPUT"] = 5] = "GPU_VERTEX_THROUGHPUT";
    /** GPU_SIMPLE_GEOMETRY_THROUGHPUT - Vertices per second */
    BenchmarkName[BenchmarkName["GPU_SIMPLE_GEOMETRY_THROUGHPUT"] = 6] = "GPU_SIMPLE_GEOMETRY_THROUGHPUT";
    /** GPU_GAUSSIAN_BLUR - Sampling radius */
    BenchmarkName[BenchmarkName["GPU_GAUSSIAN_BLUR"] = 7] = "GPU_GAUSSIAN_BLUR";
    /** CPU_MEMORY_THROUGHPUT - MB per second */
    BenchmarkName[BenchmarkName["CPU_MEMORY_THROUGHPUT"] = 8] = "CPU_MEMORY_THROUGHPUT";
    /** GPU_FILL_RATE - Pixels per second */
    BenchmarkName[BenchmarkName["GPU_FILL_RATE"] = 9] = "GPU_FILL_RATE";
    /** MEM_TOTAL_MEMORY - Total memory of the system */
    BenchmarkName[BenchmarkName["MEM_TOTAL_MEMORY"] = 10] = "MEM_TOTAL_MEMORY";
    /** CPU_NUM_PROCESSORS - Gathers the number of CPUs the device has */
    BenchmarkName[BenchmarkName["CPU_NUM_PROCESSORS"] = 11] = "CPU_NUM_PROCESSORS";
    /** DEVICE_INFORMATION_CHIPSET - Returns the Chipset of the device via Build.BOARD */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_CHIPSET"] = 12] = "DEVICE_INFORMATION_CHIPSET";
    /** DEVICE_INFORMATION_BUILD_HARDWARE - Returns the Build.HARDWARE data for a device. This can be the chipset for some devices */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_BUILD_HARDWARE"] = 13] = "DEVICE_INFORMATION_BUILD_HARDWARE";
    /** DEVICE_INFORMATION_BUILD_BRAND - Returns the Build.BRAND for the device. This is normally the marketed brand for a device. */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_BUILD_BRAND"] = 14] = "DEVICE_INFORMATION_BUILD_BRAND";
    /** DEVICE_INFORMATION_BUILD_MANUFACTURER - Returns the Build.MANUFACTURER for the device. This is what company physically assembled the device */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_BUILD_MANUFACTURER"] = 15] = "DEVICE_INFORMATION_BUILD_MANUFACTURER";
    /** DEVICE_INFORMATION_BUILD_MODEL - Returns the Build.MODEL for the device. This is normally how we identify a device as 'SM-G930V' */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_BUILD_MODEL"] = 16] = "DEVICE_INFORMATION_BUILD_MODEL";
    /** DEVICE_INFORMATION_SCREEN_PROPERTIES - Returns the screen configuration from the device such as widthPixels and density. */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_SCREEN_PROPERTIES"] = 17] = "DEVICE_INFORMATION_SCREEN_PROPERTIES";
    /** DEVICE_INFORMATION_CODEC_CAPABILITES - Gathers the codec capabilities for the device. Such as HEVC and 264 profiles supported for encode/decode */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_CODEC_CAPABILITES"] = 18] = "DEVICE_INFORMATION_CODEC_CAPABILITES";
    /** CPU_PRIME_KOTLIN - Calculates primes in Kotlin using sieve */
    BenchmarkName[BenchmarkName["CPU_PRIME_KOTLIN"] = 19] = "CPU_PRIME_KOTLIN";
    /** CPU_PRIME_NATIVE - Calculates primes in C++ using sieve */
    BenchmarkName[BenchmarkName["CPU_PRIME_NATIVE"] = 20] = "CPU_PRIME_NATIVE";
    /** CPU_SQRT_KOTLIN - Calculates sqrt using the babylonian method in kotlin */
    BenchmarkName[BenchmarkName["CPU_SQRT_KOTLIN"] = 21] = "CPU_SQRT_KOTLIN";
    /** CPU_SQRT_NATIVE - Calculates sqrt using the babylonian method in C++ */
    BenchmarkName[BenchmarkName["CPU_SQRT_NATIVE"] = 22] = "CPU_SQRT_NATIVE";
    /** CPU_INT_MATH_KOTLIN - Performs a serious of int ALU computations in kotlin */
    BenchmarkName[BenchmarkName["CPU_INT_MATH_KOTLIN"] = 23] = "CPU_INT_MATH_KOTLIN";
    /** CPU_INT_MATH_NATIVE - Performs a serious of int ALU computations in C++ */
    BenchmarkName[BenchmarkName["CPU_INT_MATH_NATIVE"] = 24] = "CPU_INT_MATH_NATIVE";
    /** DEVICE_INFORMATION_GL_VERSION - OpenGL version running on the device, calculated as (100 * major) + minor, ex. "OpenGL ES 3.2" -> 302 */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GL_VERSION"] = 25] = "DEVICE_INFORMATION_GL_VERSION";
    /** DEVICE_INFORMATION_GLSL_VERSION - GLSL version running on the device, calculated as (100 * major) + minor, ex. "OpenGL ES GLSL ES 3.20" -> 320 */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GLSL_VERSION"] = 26] = "DEVICE_INFORMATION_GLSL_VERSION";
    /** DEVICE_INFORMATION_OPENCL_EXISTS - True if we can run OpenCL on this device, otherwise false */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_OPENCL_EXISTS"] = 27] = "DEVICE_INFORMATION_OPENCL_EXISTS";
    /** DEVICE_INFORMATION_IS_HOST_UNIFIED_MEMORY - True if the device is using a unified memory architecture */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_IS_HOST_UNIFIED_MEMORY"] = 28] = "DEVICE_INFORMATION_IS_HOST_UNIFIED_MEMORY";
    /** DEVICE_INFORMATION_OPENCL_VERSION - OpenCL version running on the device, calculated as (100 * major) + minor, ex. "OpenCL 1.2" -> 102 */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_OPENCL_VERSION"] = 29] = "DEVICE_INFORMATION_OPENCL_VERSION";
    /** DEVICE_INFORMATION_TIMING_RESOLUTION_NS - Timing resolution in nanoseconds for profiling reported by OpenCL */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_TIMING_RESOLUTION_NS"] = 30] = "DEVICE_INFORMATION_TIMING_RESOLUTION_NS";
    /** DEVICE_INFORMATION_ADDRESS_SIZE - Number of bits used to address memory (32-bit, 64-bit) */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_ADDRESS_SIZE"] = 31] = "DEVICE_INFORMATION_ADDRESS_SIZE";
    /** DEVICE_INFORMATION_GPU_MAX_CLOCK_FREQUENCY_MHZ - Maximum clock frequency of the device in MHz */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GPU_MAX_CLOCK_FREQUENCY_MHZ"] = 32] = "DEVICE_INFORMATION_GPU_MAX_CLOCK_FREQUENCY_MHZ";
    /** DEVICE_INFORMATION_COMPUTE_UNITS - Number of available compute units on the device */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_COMPUTE_UNITS"] = 33] = "DEVICE_INFORMATION_COMPUTE_UNITS";
    /** DEVICE_INFORMATION_MAX_WORKGROUP_SIZE - Maximum number of tasks that can be processed at one time by a workgroup */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_MAX_WORKGROUP_SIZE"] = 34] = "DEVICE_INFORMATION_MAX_WORKGROUP_SIZE";
    /** DEVICE_INFORMATION_LOCAL_MEMORY_IS_LOCAL - True if the device has dedicated local memory storage */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_LOCAL_MEMORY_IS_LOCAL"] = 35] = "DEVICE_INFORMATION_LOCAL_MEMORY_IS_LOCAL";
    /** DEVICE_INFORMATION_LOCAL_MEMORY_SIZE - Amount of local memory storage in bytes */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_LOCAL_MEMORY_SIZE"] = 36] = "DEVICE_INFORMATION_LOCAL_MEMORY_SIZE";
    /** DEVICE_INFORMATION_GLOBAL_MEMORY_CACHE_SIZE - Amount of global memory cache in bytes */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GLOBAL_MEMORY_CACHE_SIZE"] = 37] = "DEVICE_INFORMATION_GLOBAL_MEMORY_CACHE_SIZE";
    /** DEVICE_INFORMATION_GLOBAL_MEMORY_CACHELINE_SIZE - Line size for global memory cache in bytes */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GLOBAL_MEMORY_CACHELINE_SIZE"] = 38] = "DEVICE_INFORMATION_GLOBAL_MEMORY_CACHELINE_SIZE";
    /** DEVICE_INFORMATION_GL_HAS_FENCE_SYNC_OBJECT - True if device reports support for GL fence sync objects */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GL_HAS_FENCE_SYNC_OBJECT"] = 39] = "DEVICE_INFORMATION_GL_HAS_FENCE_SYNC_OBJECT";
    /** DEVICE_INFORMATION_HEXAGON_NN_LIBRARY_VERSION - Supported hexagon nn library version. 0 if hexagon nn is not suppoted */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_HEXAGON_NN_LIBRARY_VERSION"] = 40] = "DEVICE_INFORMATION_HEXAGON_NN_LIBRARY_VERSION";
    /** GPU_GFLOPS_V2 - New GPU_GFLOPS benchmark expected to have better convergence properties than GPU_GFLOPS */
    BenchmarkName[BenchmarkName["GPU_GFLOPS_V2"] = 41] = "GPU_GFLOPS_V2";
    /** GPU_GFLOPS_VECTOR_V2 - New GPU_GFLOPS_VECTOR benchmark expected to have better convergence properties than GPU_GFLOPS_VECTOR */
    BenchmarkName[BenchmarkName["GPU_GFLOPS_VECTOR_V2"] = 42] = "GPU_GFLOPS_VECTOR_V2";
    /** DEVICE_INFORMATION_GPU_MODEL - GPU Model, e.g. Adreno (TM) 308, Mali-300 */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_GPU_MODEL"] = 43] = "DEVICE_INFORMATION_GPU_MODEL";
    /** DEVICE_INFORMATION_CODEC_CAPABILITES_V2 - Gathers more comprehensive info about media codec capabilities of the device */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_CODEC_CAPABILITES_V2"] = 44] = "DEVICE_INFORMATION_CODEC_CAPABILITES_V2";
    /** DEVICE_INFORMATION_MEDIA_PERFORMANCE_CLASS - Media performance class, e.g. 12, 13 and details:https://developer.android.com/topic/performance/performance-class */
    BenchmarkName[BenchmarkName["DEVICE_INFORMATION_MEDIA_PERFORMANCE_CLASS"] = 45] = "DEVICE_INFORMATION_MEDIA_PERFORMANCE_CLASS";
    BenchmarkName[BenchmarkName["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(BenchmarkName || (BenchmarkName = {}));
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseTimestamp = { seconds: 0, nanos: 0 };
var Timestamp = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.seconds !== 0) {
            writer.uint32(8).int64(message.seconds);
        }
        if (message.nanos !== 0) {
            writer.uint32(16).int32(message.nanos);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseTimestamp);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.seconds = longToNumber$5(reader.int64());
                    break;
                case 2:
                    message.nanos = reader.int32();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseTimestamp);
        if (object.seconds !== undefined && object.seconds !== null) {
            message.seconds = object.seconds;
        }
        else {
            message.seconds = 0;
        }
        if (object.nanos !== undefined && object.nanos !== null) {
            message.nanos = object.nanos;
        }
        else {
            message.nanos = 0;
        }
        return message;
    },
};
var globalThis$9 = (function () {
    if (typeof globalThis$9 !== "undefined")
        return globalThis$9;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function longToNumber$5(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$9.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseBenchmarkValue = {};
var BenchmarkValue = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.intValue !== undefined) {
            writer.uint32(8).int32(message.intValue);
        }
        if (message.longValue !== undefined) {
            writer.uint32(16).int64(message.longValue);
        }
        if (message.boolValue !== undefined) {
            writer.uint32(24).bool(message.boolValue);
        }
        if (message.floatValue !== undefined) {
            writer.uint32(37).float(message.floatValue);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBenchmarkValue);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.intValue = reader.int32();
                    break;
                case 2:
                    message.longValue = longToNumber$4(reader.int64());
                    break;
                case 3:
                    message.boolValue = reader.bool();
                    break;
                case 4:
                    message.floatValue = reader.float();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseBenchmarkValue);
        if (object.intValue !== undefined && object.intValue !== null) {
            message.intValue = object.intValue;
        }
        else {
            message.intValue = undefined;
        }
        if (object.longValue !== undefined && object.longValue !== null) {
            message.longValue = object.longValue;
        }
        else {
            message.longValue = undefined;
        }
        if (object.boolValue !== undefined && object.boolValue !== null) {
            message.boolValue = object.boolValue;
        }
        else {
            message.boolValue = undefined;
        }
        if (object.floatValue !== undefined && object.floatValue !== null) {
            message.floatValue = object.floatValue;
        }
        else {
            message.floatValue = undefined;
        }
        return message;
    },
};
var baseBenchmarkRequest = { benchmarkNames: 0 };
var BenchmarkRequest = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        writer.uint32(10).fork();
        for (var _i = 0, _a = message.benchmarkNames; _i < _a.length; _i++) {
            var v = _a[_i];
            writer.int32(v);
        }
        writer.ldelim();
        if (message.expirationTimestamp !== undefined) {
            Timestamp.encode(toTimestamp(message.expirationTimestamp), writer.uint32(18).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBenchmarkRequest);
        message.benchmarkNames = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    if ((tag & 7) === 2) {
                        var end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.benchmarkNames.push(reader.int32());
                        }
                    }
                    else {
                        message.benchmarkNames.push(reader.int32());
                    }
                    break;
                case 2:
                    message.expirationTimestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseBenchmarkRequest);
        message.benchmarkNames = [];
        if (object.benchmarkNames !== undefined && object.benchmarkNames !== null) {
            for (var _i = 0, _a = object.benchmarkNames; _i < _a.length; _i++) {
                var e = _a[_i];
                message.benchmarkNames.push(e);
            }
        }
        if (object.expirationTimestamp !== undefined &&
            object.expirationTimestamp !== null) {
            message.expirationTimestamp = object.expirationTimestamp;
        }
        else {
            message.expirationTimestamp = undefined;
        }
        return message;
    },
};
var baseBenchmarkResult = { name: 0 };
var BenchmarkResult = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.name !== 0) {
            writer.uint32(8).int32(message.name);
        }
        if (message.value !== undefined) {
            BenchmarkValue.encode(message.value, writer.uint32(18).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBenchmarkResult);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.name = reader.int32();
                    break;
                case 2:
                    message.value = BenchmarkValue.decode(reader, reader.uint32());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseBenchmarkResult);
        if (object.name !== undefined && object.name !== null) {
            message.name = object.name;
        }
        else {
            message.name = 0;
        }
        if (object.value !== undefined && object.value !== null) {
            message.value = BenchmarkValue.fromPartial(object.value);
        }
        else {
            message.value = undefined;
        }
        return message;
    },
};
var globalThis$8 = (function () {
    if (typeof globalThis$8 !== "undefined")
        return globalThis$8;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function toTimestamp(date) {
    var seconds = date.getTime() / 1000;
    var nanos = (date.getTime() % 1000) * 1000000;
    return { seconds: seconds, nanos: nanos };
}
function fromTimestamp(t) {
    var millis = t.seconds * 1000;
    millis += t.nanos / 1000000;
    return new Date(millis);
}
function longToNumber$4(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$8.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** Randomization units supported for allocation */
var Ruid_Type;
(function (Ruid_Type) {
    /** UNSET - Unknown type */
    Ruid_Type[Ruid_Type["UNSET"] = 0] = "UNSET";
    /** USER - Snapchat user ID */
    Ruid_Type[Ruid_Type["USER"] = 1] = "USER";
    /** DEVICE - Config device ID */
    Ruid_Type[Ruid_Type["DEVICE"] = 2] = "DEVICE";
    /** WEB_CLIENT - Web client ID */
    Ruid_Type[Ruid_Type["WEB_CLIENT"] = 3] = "WEB_CLIENT";
    /** AD_ACCOUNT - Ad account ID */
    Ruid_Type[Ruid_Type["AD_ACCOUNT"] = 4] = "AD_ACCOUNT";
    /** USERNAME - Snapchat username (legacy only) */
    Ruid_Type[Ruid_Type["USERNAME"] = 5] = "USERNAME";
    /** AD_MODERATION_AD - Ad Id used by Ad Moderation backend. */
    Ruid_Type[Ruid_Type["AD_MODERATION_AD"] = 6] = "AD_MODERATION_AD";
    /** WEB_SNAPCHAT_USER - Snapchat user id used on web. */
    Ruid_Type[Ruid_Type["WEB_SNAPCHAT_USER"] = 7] = "WEB_SNAPCHAT_USER";
    /** INTERNAL - Internal test RUID, internal use only and no analysis. */
    Ruid_Type[Ruid_Type["INTERNAL"] = 8] = "INTERNAL";
    /** AM_ORGANIZATION - Ads Manager organization ID */
    Ruid_Type[Ruid_Type["AM_ORGANIZATION"] = 9] = "AM_ORGANIZATION";
    /** AM_MEMBER - Ads Manager member ID */
    Ruid_Type[Ruid_Type["AM_MEMBER"] = 10] = "AM_MEMBER";
    /** AM_SESSION - Ads Manager session ID */
    Ruid_Type[Ruid_Type["AM_SESSION"] = 11] = "AM_SESSION";
    /** AM_PROFILE - Ads Manager profile ID */
    Ruid_Type[Ruid_Type["AM_PROFILE"] = 12] = "AM_PROFILE";
    /** AM_SNAPCHAT_USER - Snapchat user ID on Ads Manager */
    Ruid_Type[Ruid_Type["AM_SNAPCHAT_USER"] = 13] = "AM_SNAPCHAT_USER";
    /** SNAPCHAT_ADVERTISING - Snapchat Advertising ID for Budget A/B */
    Ruid_Type[Ruid_Type["SNAPCHAT_ADVERTISING"] = 14] = "SNAPCHAT_ADVERTISING";
    /** AM_CLIENT - Ads Manager client ID */
    Ruid_Type[Ruid_Type["AM_CLIENT"] = 15] = "AM_CLIENT";
    Ruid_Type[Ruid_Type["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(Ruid_Type || (Ruid_Type = {}));
var baseRuid = { type: 0, stringValue: "", loggingIdValue: "" };
var Ruid = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.type !== 0) {
            writer.uint32(8).int32(message.type);
        }
        if (message.stringValue !== "") {
            writer.uint32(18).string(message.stringValue);
        }
        if (message.loggingIdValue !== "") {
            writer.uint32(26).string(message.loggingIdValue);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseRuid);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.type = reader.int32();
                    break;
                case 2:
                    message.stringValue = reader.string();
                    break;
                case 3:
                    message.loggingIdValue = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseRuid);
        if (object.type !== undefined && object.type !== null) {
            message.type = object.type;
        }
        else {
            message.type = 0;
        }
        if (object.stringValue !== undefined && object.stringValue !== null) {
            message.stringValue = object.stringValue;
        }
        else {
            message.stringValue = "";
        }
        if (object.loggingIdValue !== undefined && object.loggingIdValue !== null) {
            message.loggingIdValue = object.loggingIdValue;
        }
        else {
            message.loggingIdValue = "";
        }
        return message;
    },
};
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseBoolValue = { value: false };
var BoolValue = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.value === true) {
            writer.uint32(8).bool(message.value);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBoolValue);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.value = reader.bool();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseBoolValue);
        if (object.value !== undefined && object.value !== null) {
            message.value = object.value;
        }
        else {
            message.value = false;
        }
        return message;
    },
};
var globalThis$7 = (function () {
    if (typeof globalThis$7 !== "undefined")
        return globalThis$7;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** Indicates what was the state of the app when the config sync was triggered */
var ConfigTargetingTriggerEventType;
(function (ConfigTargetingTriggerEventType) {
    /** UNKNOWN_EVENT_TYPE - Unknown */
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["UNKNOWN_EVENT_TYPE"] = 0] = "UNKNOWN_EVENT_TYPE";
    /** COLD_START - Deprecated because of simplification to FG/BG */
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["COLD_START"] = 1] = "COLD_START";
    /** WARM_START - Deprecated because of simplification to FG/BG */
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["WARM_START"] = 2] = "WARM_START";
    /** FOREGROUND_TRIGGER - Triggered when application is in the foreground */
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["FOREGROUND_TRIGGER"] = 3] = "FOREGROUND_TRIGGER";
    /** BACKGROUND_TRIGGER - Triggered when application is backgrounded */
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["BACKGROUND_TRIGGER"] = 4] = "BACKGROUND_TRIGGER";
    ConfigTargetingTriggerEventType[ConfigTargetingTriggerEventType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ConfigTargetingTriggerEventType || (ConfigTargetingTriggerEventType = {}));
/**
 * Indicates what was the state of the app when the config sync happens
 * The enum is also used as a dynamic client property to populate feature signals
 */
var ConfigTargetingAppState;
(function (ConfigTargetingAppState) {
    /** UNKNOWN_APP_STATE - Unknown */
    ConfigTargetingAppState[ConfigTargetingAppState["UNKNOWN_APP_STATE"] = 0] = "UNKNOWN_APP_STATE";
    /** FOREGROUND - Foreground */
    ConfigTargetingAppState[ConfigTargetingAppState["FOREGROUND"] = 1] = "FOREGROUND";
    /** BACKGROUND - Background */
    ConfigTargetingAppState[ConfigTargetingAppState["BACKGROUND"] = 2] = "BACKGROUND";
    ConfigTargetingAppState[ConfigTargetingAppState["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ConfigTargetingAppState || (ConfigTargetingAppState = {}));
/**
 * We expect this enum to be updated whenever there is a new sync instrumentation: This will help us isolate new changes on
 * server side to avoid post mortems like https://snapchat.quip.com/xGnVAcsBrOeC
 */
var ConfigTargetingInstrumentation;
(function (ConfigTargetingInstrumentation) {
    /** DEFAULT_INSTRUMENTATION - Default */
    ConfigTargetingInstrumentation[ConfigTargetingInstrumentation["DEFAULT_INSTRUMENTATION"] = 0] = "DEFAULT_INSTRUMENTATION";
    /** USER_AUTHENTICATION - User logged in */
    ConfigTargetingInstrumentation[ConfigTargetingInstrumentation["USER_AUTHENTICATION"] = 1] = "USER_AUTHENTICATION";
    ConfigTargetingInstrumentation[ConfigTargetingInstrumentation["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ConfigTargetingInstrumentation || (ConfigTargetingInstrumentation = {}));
/** NetworkType */
var Connectivity_NetworkType;
(function (Connectivity_NetworkType) {
    /** UNKNOWN_NETWORK_TYPE - Unknown */
    Connectivity_NetworkType[Connectivity_NetworkType["UNKNOWN_NETWORK_TYPE"] = 0] = "UNKNOWN_NETWORK_TYPE";
    /** CELLULAR - Cellular */
    Connectivity_NetworkType[Connectivity_NetworkType["CELLULAR"] = 1] = "CELLULAR";
    /** WIFI - Wifi */
    Connectivity_NetworkType[Connectivity_NetworkType["WIFI"] = 2] = "WIFI";
    Connectivity_NetworkType[Connectivity_NetworkType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(Connectivity_NetworkType || (Connectivity_NetworkType = {}));
var baseConfigTargetingRequest = {
    configResultsEtag: "",
    namespaces: 0,
    ghostId: "",
    countryCode: "",
    screenWidth: 0,
    screenHeight: 0,
    maxVideoWidthPx: 0,
    maxVideoHeightPx: 0,
    deltaSync: false,
    userAgent: "",
    triggerEventType: 0,
    appState: 0,
    locale: "",
    deviceId: "",
    userId: "",
    clientIp: "",
    isUnAuthorized: false,
    appLocale: "",
    instrumentation: 0,
    lastSuccessfulSync: 0,
    isLogout: false,
    packageInstaller: "",
    syncTriggerBlizzardSessionId: "",
    syncExecutionBlizzardSessionId: "",
    cofSyncTriggerDelayFromStartupMs: 0,
    cofSyncExecutionDelayFromStartupMs: 0,
    syncTriggerTime: 0,
    snapkitAppId: "",
    lenscoreVersion: 0,
    configNames: "",
    includeTestUserTreatments: false,
    disableExposureLogging: false,
    lensClusterOrig4: 0,
};
var ConfigTargetingRequest = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.configResultsEtag !== "") {
            writer.uint32(10).string(message.configResultsEtag);
        }
        writer.uint32(18).fork();
        for (var _i = 0, _a = message.namespaces; _i < _a.length; _i++) {
            var v = _a[_i];
            writer.int32(v);
        }
        writer.ldelim();
        if (message.ghostId !== "") {
            writer.uint32(26).string(message.ghostId);
        }
        if (message.countryCode !== "") {
            writer.uint32(34).string(message.countryCode);
        }
        if (message.screenWidth !== 0) {
            writer.uint32(40).int32(message.screenWidth);
        }
        if (message.screenHeight !== 0) {
            writer.uint32(48).int32(message.screenHeight);
        }
        if (message.connectivity !== undefined) {
            Connectivity.encode(message.connectivity, writer.uint32(58).fork()).ldelim();
        }
        if (message.maxVideoWidthPx !== 0) {
            writer.uint32(64).int32(message.maxVideoWidthPx);
        }
        if (message.maxVideoHeightPx !== 0) {
            writer.uint32(72).int32(message.maxVideoHeightPx);
        }
        for (var _b = 0, _c = message.benchmarkResults; _b < _c.length; _b++) {
            var v = _c[_b];
            BenchmarkResult.encode(v, writer.uint32(82).fork()).ldelim();
        }
        if (message.deltaSync === true) {
            writer.uint32(88).bool(message.deltaSync);
        }
        if (message.userAgent !== "") {
            writer.uint32(98).string(message.userAgent);
        }
        if (message.triggerEventType !== 0) {
            writer.uint32(104).int32(message.triggerEventType);
        }
        if (message.appState !== 0) {
            writer.uint32(112).int32(message.appState);
        }
        if (message.locale !== "") {
            writer.uint32(122).string(message.locale);
        }
        if (message.deviceId !== "") {
            writer.uint32(130).string(message.deviceId);
        }
        if (message.userId !== "") {
            writer.uint32(138).string(message.userId);
        }
        if (message.clientIp !== "") {
            writer.uint32(146).string(message.clientIp);
        }
        if (message.isUnAuthorized === true) {
            writer.uint32(152).bool(message.isUnAuthorized);
        }
        if (message.appLocale !== "") {
            writer.uint32(162).string(message.appLocale);
        }
        if (message.instrumentation !== 0) {
            writer.uint32(168).int32(message.instrumentation);
        }
        if (message.lastSuccessfulSync !== 0) {
            writer.uint32(176).int64(message.lastSuccessfulSync);
        }
        if (message.isLogout === true) {
            writer.uint32(184).bool(message.isLogout);
        }
        if (message.packageInstaller !== "") {
            writer.uint32(194).string(message.packageInstaller);
        }
        if (message.syncTriggerBlizzardSessionId !== "") {
            writer.uint32(202).string(message.syncTriggerBlizzardSessionId);
        }
        if (message.syncExecutionBlizzardSessionId !== "") {
            writer.uint32(210).string(message.syncExecutionBlizzardSessionId);
        }
        if (message.cofSyncTriggerDelayFromStartupMs !== 0) {
            writer.uint32(216).int32(message.cofSyncTriggerDelayFromStartupMs);
        }
        if (message.cofSyncExecutionDelayFromStartupMs !== 0) {
            writer.uint32(224).int32(message.cofSyncExecutionDelayFromStartupMs);
        }
        if (message.syncTriggerTime !== 0) {
            writer.uint32(232).int64(message.syncTriggerTime);
        }
        if (message.decoderEncoderAvailability !== undefined) {
            DecoderEncoderAvailablity.encode(message.decoderEncoderAvailability, writer.uint32(242).fork()).ldelim();
        }
        if (message.snapkitAppId !== "") {
            writer.uint32(250).string(message.snapkitAppId);
        }
        if (message.lenscoreVersion !== 0) {
            writer.uint32(256).int32(message.lenscoreVersion);
        }
        if (message.ruid !== undefined) {
            Ruid.encode(message.ruid, writer.uint32(266).fork()).ldelim();
        }
        for (var _d = 0, _e = message.configNames; _d < _e.length; _d++) {
            var v = _e[_d];
            writer.uint32(274).string(v);
        }
        if (message.includeTestUserTreatments === true) {
            writer.uint32(288).bool(message.includeTestUserTreatments);
        }
        if (message.disableExposureLogging === true) {
            writer.uint32(296).bool(message.disableExposureLogging);
        }
        if (message.lensClusterOrig4 !== 0) {
            writer.uint32(304).int32(message.lensClusterOrig4);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseConfigTargetingRequest);
        message.namespaces = [];
        message.benchmarkResults = [];
        message.configNames = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.configResultsEtag = reader.string();
                    break;
                case 2:
                    if ((tag & 7) === 2) {
                        var end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.namespaces.push(reader.int32());
                        }
                    }
                    else {
                        message.namespaces.push(reader.int32());
                    }
                    break;
                case 3:
                    message.ghostId = reader.string();
                    break;
                case 4:
                    message.countryCode = reader.string();
                    break;
                case 5:
                    message.screenWidth = reader.int32();
                    break;
                case 6:
                    message.screenHeight = reader.int32();
                    break;
                case 7:
                    message.connectivity = Connectivity.decode(reader, reader.uint32());
                    break;
                case 8:
                    message.maxVideoWidthPx = reader.int32();
                    break;
                case 9:
                    message.maxVideoHeightPx = reader.int32();
                    break;
                case 10:
                    message.benchmarkResults.push(BenchmarkResult.decode(reader, reader.uint32()));
                    break;
                case 11:
                    message.deltaSync = reader.bool();
                    break;
                case 12:
                    message.userAgent = reader.string();
                    break;
                case 13:
                    message.triggerEventType = reader.int32();
                    break;
                case 14:
                    message.appState = reader.int32();
                    break;
                case 15:
                    message.locale = reader.string();
                    break;
                case 16:
                    message.deviceId = reader.string();
                    break;
                case 17:
                    message.userId = reader.string();
                    break;
                case 18:
                    message.clientIp = reader.string();
                    break;
                case 19:
                    message.isUnAuthorized = reader.bool();
                    break;
                case 20:
                    message.appLocale = reader.string();
                    break;
                case 21:
                    message.instrumentation = reader.int32();
                    break;
                case 22:
                    message.lastSuccessfulSync = longToNumber$3(reader.int64());
                    break;
                case 23:
                    message.isLogout = reader.bool();
                    break;
                case 24:
                    message.packageInstaller = reader.string();
                    break;
                case 25:
                    message.syncTriggerBlizzardSessionId = reader.string();
                    break;
                case 26:
                    message.syncExecutionBlizzardSessionId = reader.string();
                    break;
                case 27:
                    message.cofSyncTriggerDelayFromStartupMs = reader.int32();
                    break;
                case 28:
                    message.cofSyncExecutionDelayFromStartupMs = reader.int32();
                    break;
                case 29:
                    message.syncTriggerTime = longToNumber$3(reader.int64());
                    break;
                case 30:
                    message.decoderEncoderAvailability = DecoderEncoderAvailablity.decode(reader, reader.uint32());
                    break;
                case 31:
                    message.snapkitAppId = reader.string();
                    break;
                case 32:
                    message.lenscoreVersion = reader.int32();
                    break;
                case 33:
                    message.ruid = Ruid.decode(reader, reader.uint32());
                    break;
                case 34:
                    message.configNames.push(reader.string());
                    break;
                case 36:
                    message.includeTestUserTreatments = reader.bool();
                    break;
                case 37:
                    message.disableExposureLogging = reader.bool();
                    break;
                case 38:
                    message.lensClusterOrig4 = reader.int32();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseConfigTargetingRequest);
        message.namespaces = [];
        message.benchmarkResults = [];
        message.configNames = [];
        if (object.configResultsEtag !== undefined &&
            object.configResultsEtag !== null) {
            message.configResultsEtag = object.configResultsEtag;
        }
        else {
            message.configResultsEtag = "";
        }
        if (object.namespaces !== undefined && object.namespaces !== null) {
            for (var _i = 0, _a = object.namespaces; _i < _a.length; _i++) {
                var e = _a[_i];
                message.namespaces.push(e);
            }
        }
        if (object.ghostId !== undefined && object.ghostId !== null) {
            message.ghostId = object.ghostId;
        }
        else {
            message.ghostId = "";
        }
        if (object.countryCode !== undefined && object.countryCode !== null) {
            message.countryCode = object.countryCode;
        }
        else {
            message.countryCode = "";
        }
        if (object.screenWidth !== undefined && object.screenWidth !== null) {
            message.screenWidth = object.screenWidth;
        }
        else {
            message.screenWidth = 0;
        }
        if (object.screenHeight !== undefined && object.screenHeight !== null) {
            message.screenHeight = object.screenHeight;
        }
        else {
            message.screenHeight = 0;
        }
        if (object.connectivity !== undefined && object.connectivity !== null) {
            message.connectivity = Connectivity.fromPartial(object.connectivity);
        }
        else {
            message.connectivity = undefined;
        }
        if (object.maxVideoWidthPx !== undefined &&
            object.maxVideoWidthPx !== null) {
            message.maxVideoWidthPx = object.maxVideoWidthPx;
        }
        else {
            message.maxVideoWidthPx = 0;
        }
        if (object.maxVideoHeightPx !== undefined &&
            object.maxVideoHeightPx !== null) {
            message.maxVideoHeightPx = object.maxVideoHeightPx;
        }
        else {
            message.maxVideoHeightPx = 0;
        }
        if (object.benchmarkResults !== undefined &&
            object.benchmarkResults !== null) {
            for (var _b = 0, _c = object.benchmarkResults; _b < _c.length; _b++) {
                var e = _c[_b];
                message.benchmarkResults.push(BenchmarkResult.fromPartial(e));
            }
        }
        if (object.deltaSync !== undefined && object.deltaSync !== null) {
            message.deltaSync = object.deltaSync;
        }
        else {
            message.deltaSync = false;
        }
        if (object.userAgent !== undefined && object.userAgent !== null) {
            message.userAgent = object.userAgent;
        }
        else {
            message.userAgent = "";
        }
        if (object.triggerEventType !== undefined &&
            object.triggerEventType !== null) {
            message.triggerEventType = object.triggerEventType;
        }
        else {
            message.triggerEventType = 0;
        }
        if (object.appState !== undefined && object.appState !== null) {
            message.appState = object.appState;
        }
        else {
            message.appState = 0;
        }
        if (object.locale !== undefined && object.locale !== null) {
            message.locale = object.locale;
        }
        else {
            message.locale = "";
        }
        if (object.deviceId !== undefined && object.deviceId !== null) {
            message.deviceId = object.deviceId;
        }
        else {
            message.deviceId = "";
        }
        if (object.userId !== undefined && object.userId !== null) {
            message.userId = object.userId;
        }
        else {
            message.userId = "";
        }
        if (object.clientIp !== undefined && object.clientIp !== null) {
            message.clientIp = object.clientIp;
        }
        else {
            message.clientIp = "";
        }
        if (object.isUnAuthorized !== undefined && object.isUnAuthorized !== null) {
            message.isUnAuthorized = object.isUnAuthorized;
        }
        else {
            message.isUnAuthorized = false;
        }
        if (object.appLocale !== undefined && object.appLocale !== null) {
            message.appLocale = object.appLocale;
        }
        else {
            message.appLocale = "";
        }
        if (object.instrumentation !== undefined &&
            object.instrumentation !== null) {
            message.instrumentation = object.instrumentation;
        }
        else {
            message.instrumentation = 0;
        }
        if (object.lastSuccessfulSync !== undefined &&
            object.lastSuccessfulSync !== null) {
            message.lastSuccessfulSync = object.lastSuccessfulSync;
        }
        else {
            message.lastSuccessfulSync = 0;
        }
        if (object.isLogout !== undefined && object.isLogout !== null) {
            message.isLogout = object.isLogout;
        }
        else {
            message.isLogout = false;
        }
        if (object.packageInstaller !== undefined &&
            object.packageInstaller !== null) {
            message.packageInstaller = object.packageInstaller;
        }
        else {
            message.packageInstaller = "";
        }
        if (object.syncTriggerBlizzardSessionId !== undefined &&
            object.syncTriggerBlizzardSessionId !== null) {
            message.syncTriggerBlizzardSessionId =
                object.syncTriggerBlizzardSessionId;
        }
        else {
            message.syncTriggerBlizzardSessionId = "";
        }
        if (object.syncExecutionBlizzardSessionId !== undefined &&
            object.syncExecutionBlizzardSessionId !== null) {
            message.syncExecutionBlizzardSessionId =
                object.syncExecutionBlizzardSessionId;
        }
        else {
            message.syncExecutionBlizzardSessionId = "";
        }
        if (object.cofSyncTriggerDelayFromStartupMs !== undefined &&
            object.cofSyncTriggerDelayFromStartupMs !== null) {
            message.cofSyncTriggerDelayFromStartupMs =
                object.cofSyncTriggerDelayFromStartupMs;
        }
        else {
            message.cofSyncTriggerDelayFromStartupMs = 0;
        }
        if (object.cofSyncExecutionDelayFromStartupMs !== undefined &&
            object.cofSyncExecutionDelayFromStartupMs !== null) {
            message.cofSyncExecutionDelayFromStartupMs =
                object.cofSyncExecutionDelayFromStartupMs;
        }
        else {
            message.cofSyncExecutionDelayFromStartupMs = 0;
        }
        if (object.syncTriggerTime !== undefined &&
            object.syncTriggerTime !== null) {
            message.syncTriggerTime = object.syncTriggerTime;
        }
        else {
            message.syncTriggerTime = 0;
        }
        if (object.decoderEncoderAvailability !== undefined &&
            object.decoderEncoderAvailability !== null) {
            message.decoderEncoderAvailability =
                DecoderEncoderAvailablity.fromPartial(object.decoderEncoderAvailability);
        }
        else {
            message.decoderEncoderAvailability = undefined;
        }
        if (object.snapkitAppId !== undefined && object.snapkitAppId !== null) {
            message.snapkitAppId = object.snapkitAppId;
        }
        else {
            message.snapkitAppId = "";
        }
        if (object.lenscoreVersion !== undefined &&
            object.lenscoreVersion !== null) {
            message.lenscoreVersion = object.lenscoreVersion;
        }
        else {
            message.lenscoreVersion = 0;
        }
        if (object.ruid !== undefined && object.ruid !== null) {
            message.ruid = Ruid.fromPartial(object.ruid);
        }
        else {
            message.ruid = undefined;
        }
        if (object.configNames !== undefined && object.configNames !== null) {
            for (var _d = 0, _e = object.configNames; _d < _e.length; _d++) {
                var e = _e[_d];
                message.configNames.push(e);
            }
        }
        if (object.includeTestUserTreatments !== undefined &&
            object.includeTestUserTreatments !== null) {
            message.includeTestUserTreatments = object.includeTestUserTreatments;
        }
        else {
            message.includeTestUserTreatments = false;
        }
        if (object.disableExposureLogging !== undefined &&
            object.disableExposureLogging !== null) {
            message.disableExposureLogging = object.disableExposureLogging;
        }
        else {
            message.disableExposureLogging = false;
        }
        if (object.lensClusterOrig4 !== undefined &&
            object.lensClusterOrig4 !== null) {
            message.lensClusterOrig4 = object.lensClusterOrig4;
        }
        else {
            message.lensClusterOrig4 = 0;
        }
        return message;
    },
};
var baseConnectivity = { networkType: 0, carrier: "" };
var Connectivity = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.networkType !== 0) {
            writer.uint32(8).int32(message.networkType);
        }
        if (message.isMetered !== undefined) {
            BoolValue.encode({ value: message.isMetered }, writer.uint32(18).fork()).ldelim();
        }
        if (message.isRoaming !== undefined) {
            BoolValue.encode({ value: message.isRoaming }, writer.uint32(26).fork()).ldelim();
        }
        if (message.carrier !== "") {
            writer.uint32(34).string(message.carrier);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseConnectivity);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.networkType = reader.int32();
                    break;
                case 2:
                    message.isMetered = BoolValue.decode(reader, reader.uint32()).value;
                    break;
                case 3:
                    message.isRoaming = BoolValue.decode(reader, reader.uint32()).value;
                    break;
                case 4:
                    message.carrier = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseConnectivity);
        if (object.networkType !== undefined && object.networkType !== null) {
            message.networkType = object.networkType;
        }
        else {
            message.networkType = 0;
        }
        if (object.isMetered !== undefined && object.isMetered !== null) {
            message.isMetered = object.isMetered;
        }
        else {
            message.isMetered = undefined;
        }
        if (object.isRoaming !== undefined && object.isRoaming !== null) {
            message.isRoaming = object.isRoaming;
        }
        else {
            message.isRoaming = undefined;
        }
        if (object.carrier !== undefined && object.carrier !== null) {
            message.carrier = object.carrier;
        }
        else {
            message.carrier = "";
        }
        return message;
    },
};
var baseDecoderEncoderAvailablity = {
    isSnapVp9DecoderAvailable: false,
    isSnapAv1DecoderAvailable: false,
};
var DecoderEncoderAvailablity = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.isSnapVp9DecoderAvailable === true) {
            writer.uint32(8).bool(message.isSnapVp9DecoderAvailable);
        }
        if (message.isSnapAv1DecoderAvailable === true) {
            writer.uint32(16).bool(message.isSnapAv1DecoderAvailable);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseDecoderEncoderAvailablity);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.isSnapVp9DecoderAvailable = reader.bool();
                    break;
                case 2:
                    message.isSnapAv1DecoderAvailable = reader.bool();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseDecoderEncoderAvailablity);
        if (object.isSnapVp9DecoderAvailable !== undefined &&
            object.isSnapVp9DecoderAvailable !== null) {
            message.isSnapVp9DecoderAvailable = object.isSnapVp9DecoderAvailable;
        }
        else {
            message.isSnapVp9DecoderAvailable = false;
        }
        if (object.isSnapAv1DecoderAvailable !== undefined &&
            object.isSnapAv1DecoderAvailable !== null) {
            message.isSnapAv1DecoderAvailable = object.isSnapAv1DecoderAvailable;
        }
        else {
            message.isSnapAv1DecoderAvailable = false;
        }
        return message;
    },
};
var globalThis$6 = (function () {
    if (typeof globalThis$6 !== "undefined")
        return globalThis$6;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function longToNumber$3(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$6.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseAny = { typeUrl: "" };
var Any = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.typeUrl !== "") {
            writer.uint32(10).string(message.typeUrl);
        }
        if (message.value.length !== 0) {
            writer.uint32(18).bytes(message.value);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseAny);
        message.value = new Uint8Array();
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.typeUrl = reader.string();
                    break;
                case 2:
                    message.value = reader.bytes();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseAny);
        if (object.typeUrl !== undefined && object.typeUrl !== null) {
            message.typeUrl = object.typeUrl;
        }
        else {
            message.typeUrl = "";
        }
        if (object.value !== undefined && object.value !== null) {
            message.value = object.value;
        }
        else {
            message.value = new Uint8Array();
        }
        return message;
    },
};
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseMapRecord = { key: "", value: "" };
var MapRecord = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.key !== "") {
            writer.uint32(10).string(message.key);
        }
        if (message.value !== "") {
            writer.uint32(18).string(message.value);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseMapRecord);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.key = reader.string();
                    break;
                case 2:
                    message.value = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseMapRecord);
        if (object.key !== undefined && object.key !== null) {
            message.key = object.key;
        }
        else {
            message.key = "";
        }
        if (object.value !== undefined && object.value !== null) {
            message.value = object.value;
        }
        else {
            message.value = "";
        }
        return message;
    },
};
var baseMapRecords = {};
var MapRecords = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        for (var _i = 0, _a = message.records; _i < _a.length; _i++) {
            var v = _a[_i];
            MapRecord.encode(v, writer.uint32(10).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseMapRecords);
        message.records = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.records.push(MapRecord.decode(reader, reader.uint32()));
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseMapRecords);
        message.records = [];
        if (object.records !== undefined && object.records !== null) {
            for (var _i = 0, _a = object.records; _i < _a.length; _i++) {
                var e = _a[_i];
                message.records.push(MapRecord.fromPartial(e));
            }
        }
        return message;
    },
};
var baseValue = {};
var Value = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.intValue !== undefined) {
            writer.uint32(8).int32(message.intValue);
        }
        if (message.longValue !== undefined) {
            writer.uint32(16).int64(message.longValue);
        }
        if (message.floatValue !== undefined) {
            writer.uint32(29).float(message.floatValue);
        }
        if (message.doubleValue !== undefined) {
            writer.uint32(73).double(message.doubleValue);
        }
        if (message.boolValue !== undefined) {
            writer.uint32(32).bool(message.boolValue);
        }
        if (message.stringValue !== undefined) {
            writer.uint32(42).string(message.stringValue);
        }
        if (message.anyValue !== undefined) {
            Any.encode(message.anyValue, writer.uint32(50).fork()).ldelim();
        }
        if (message.mapValue !== undefined) {
            MapRecords.encode(message.mapValue, writer.uint32(58).fork()).ldelim();
        }
        if (message.intPairValue !== undefined) {
            writer.uint32(65).fixed64(message.intPairValue);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseValue);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.intValue = reader.int32();
                    break;
                case 2:
                    message.longValue = longToNumber$2(reader.int64());
                    break;
                case 3:
                    message.floatValue = reader.float();
                    break;
                case 9:
                    message.doubleValue = reader.double();
                    break;
                case 4:
                    message.boolValue = reader.bool();
                    break;
                case 5:
                    message.stringValue = reader.string();
                    break;
                case 6:
                    message.anyValue = Any.decode(reader, reader.uint32());
                    break;
                case 7:
                    message.mapValue = MapRecords.decode(reader, reader.uint32());
                    break;
                case 8:
                    message.intPairValue = longToNumber$2(reader.fixed64());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseValue);
        if (object.intValue !== undefined && object.intValue !== null) {
            message.intValue = object.intValue;
        }
        else {
            message.intValue = undefined;
        }
        if (object.longValue !== undefined && object.longValue !== null) {
            message.longValue = object.longValue;
        }
        else {
            message.longValue = undefined;
        }
        if (object.floatValue !== undefined && object.floatValue !== null) {
            message.floatValue = object.floatValue;
        }
        else {
            message.floatValue = undefined;
        }
        if (object.doubleValue !== undefined && object.doubleValue !== null) {
            message.doubleValue = object.doubleValue;
        }
        else {
            message.doubleValue = undefined;
        }
        if (object.boolValue !== undefined && object.boolValue !== null) {
            message.boolValue = object.boolValue;
        }
        else {
            message.boolValue = undefined;
        }
        if (object.stringValue !== undefined && object.stringValue !== null) {
            message.stringValue = object.stringValue;
        }
        else {
            message.stringValue = undefined;
        }
        if (object.anyValue !== undefined && object.anyValue !== null) {
            message.anyValue = Any.fromPartial(object.anyValue);
        }
        else {
            message.anyValue = undefined;
        }
        if (object.mapValue !== undefined && object.mapValue !== null) {
            message.mapValue = MapRecords.fromPartial(object.mapValue);
        }
        else {
            message.mapValue = undefined;
        }
        if (object.intPairValue !== undefined && object.intPairValue !== null) {
            message.intPairValue = object.intPairValue;
        }
        else {
            message.intPairValue = undefined;
        }
        return message;
    },
};
var globalThis$5 = (function () {
    if (typeof globalThis$5 !== "undefined")
        return globalThis$5;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function longToNumber$2(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$5.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** The suffix of "_PLATFORM" is appended, due to C++ declared enums with global scope, which causes issues in Snapchat/client */
var ConfigResult_ServePlatform;
(function (ConfigResult_ServePlatform) {
    ConfigResult_ServePlatform[ConfigResult_ServePlatform["UNKNOWN_CONTEXT_PLATFORM"] = 0] = "UNKNOWN_CONTEXT_PLATFORM";
    ConfigResult_ServePlatform[ConfigResult_ServePlatform["IOS_PLATFORM"] = 1] = "IOS_PLATFORM";
    ConfigResult_ServePlatform[ConfigResult_ServePlatform["ANDROID_PLATFORM"] = 2] = "ANDROID_PLATFORM";
    ConfigResult_ServePlatform[ConfigResult_ServePlatform["SERVER_PLATFORM"] = 3] = "SERVER_PLATFORM";
    ConfigResult_ServePlatform[ConfigResult_ServePlatform["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ConfigResult_ServePlatform || (ConfigResult_ServePlatform = {}));
var ClientTargetingExpression_Operator;
(function (ClientTargetingExpression_Operator) {
    ClientTargetingExpression_Operator[ClientTargetingExpression_Operator["UNKNOWN_OPERATOR"] = 0] = "UNKNOWN_OPERATOR";
    ClientTargetingExpression_Operator[ClientTargetingExpression_Operator["AND"] = 1] = "AND";
    ClientTargetingExpression_Operator[ClientTargetingExpression_Operator["OR"] = 2] = "OR";
    ClientTargetingExpression_Operator[ClientTargetingExpression_Operator["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ClientTargetingExpression_Operator || (ClientTargetingExpression_Operator = {}));
var ClientTargetingExpression_PredicateOperator;
(function (ClientTargetingExpression_PredicateOperator) {
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["UNKNOWN_PREDICATE_OPERATOR"] = 0] = "UNKNOWN_PREDICATE_OPERATOR";
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["EQUAL"] = 1] = "EQUAL";
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["NOT_EQUAL"] = 2] = "NOT_EQUAL";
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["GREATER_THAN_OR_EQUAL_TO"] = 3] = "GREATER_THAN_OR_EQUAL_TO";
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["LESS_THAN_OR_EQUAL_TO"] = 4] = "LESS_THAN_OR_EQUAL_TO";
    ClientTargetingExpression_PredicateOperator[ClientTargetingExpression_PredicateOperator["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ClientTargetingExpression_PredicateOperator || (ClientTargetingExpression_PredicateOperator = {}));
var ClientTargetingExpression_Property;
(function (ClientTargetingExpression_Property) {
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["UNKNOWN_PROPERTY"] = 0] = "UNKNOWN_PROPERTY";
    /**
     * BATTERY_LEVEL - A enum ordinal indicating the battery level is LOW/MID/HIGH.
     * The ordinals are defined in snapchat.cdp.cof.battery_level.proto
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BATTERY_LEVEL"] = 1] = "BATTERY_LEVEL";
    /** IS_OFFLINE - A boolean indicating whether the device is offline. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_OFFLINE"] = 2] = "IS_OFFLINE";
    /** IS_CHARGING - A boolean indicating whether the device is charging. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_CHARGING"] = 3] = "IS_CHARGING";
    /** BANDWIDTH - An integer representing the download bandwidth in bps. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BANDWIDTH"] = 4] = "BANDWIDTH";
    /** DISK_SIZE - Disk size in mb */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DISK_SIZE"] = 5] = "DISK_SIZE";
    /** DISK_AVAILABLE - Disk space available in mb */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DISK_AVAILABLE"] = 6] = "DISK_AVAILABLE";
    /**
     * MEDIA_TYPE_DEPRECATED - An enum ordinal indicating the type of media being processed.
     * The ordinals are defined in snapchat.snapdoc.MediaMetadata.MediaType.
     *
     * @deprecated
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MEDIA_TYPE_DEPRECATED"] = 7] = "MEDIA_TYPE_DEPRECATED";
    /** IS_PUBLIC_STORY - A boolean indicating whether the story is shared publicly. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_PUBLIC_STORY"] = 8] = "IS_PUBLIC_STORY";
    /** IS_OFFICIAL_STORY - A boolean indicating whether the story is posted from offcial account and is public. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_OFFICIAL_STORY"] = 9] = "IS_OFFICIAL_STORY";
    /** CAMERA_DIRECTION - An enum for camera direction */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_DIRECTION"] = 10] = "CAMERA_DIRECTION";
    /** CAMERA_CONTEXT - An enum for camera context */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_CONTEXT"] = 11] = "CAMERA_CONTEXT";
    /** CAMERA_API - An enum for camera API */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_API"] = 12] = "CAMERA_API";
    /** CAMERA_FLASH_STATE - An enum for camera flash state */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_FLASH_STATE"] = 13] = "CAMERA_FLASH_STATE";
    /** SNAP_SOURCE - An enum for where the snap orginates */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAP_SOURCE"] = 14] = "SNAP_SOURCE";
    /** LEGACY_MUSHROOM_CONTENT_TYPE - An enum for what content type is being used */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LEGACY_MUSHROOM_CONTENT_TYPE"] = 15] = "LEGACY_MUSHROOM_CONTENT_TYPE";
    /** UPLOAD_BANDWIDTH - An integer representing the upload bandwidth in bps. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["UPLOAD_BANDWIDTH"] = 16] = "UPLOAD_BANDWIDTH";
    /** CAMERA2_LEVEL - An enum for camera2 hardware support level. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA2_LEVEL"] = 17] = "CAMERA2_LEVEL";
    /** CAMERA_NIGHT_MODE_STATE - An enum for Camera night model state. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_NIGHT_MODE_STATE"] = 18] = "CAMERA_NIGHT_MODE_STATE";
    /** USER_LANGUAGE - The language user chooses the app to display */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_LANGUAGE"] = 19] = "USER_LANGUAGE";
    /** VIDEO_DURATION - The video duration in ms. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["VIDEO_DURATION"] = 20] = "VIDEO_DURATION";
    /** REALTIME_MOBILE_NETWORK_GENERATION - Mobile network generation(3G, 4G, etc) defined in snapchat.cdp.cof.mobile_network_generation.proto */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REALTIME_MOBILE_NETWORK_GENERATION"] = 21] = "REALTIME_MOBILE_NETWORK_GENERATION";
    /** REALTIME_NETWORK_TYPE - Mobile networktype defined via Connectivity.NetworkType in snapchat.cdp.cof.circumstance_engine.proto */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REALTIME_NETWORK_TYPE"] = 22] = "REALTIME_NETWORK_TYPE";
    /** REALTIME_NETWORK_IS_METERED - A boolean for if the current network is metered. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REALTIME_NETWORK_IS_METERED"] = 23] = "REALTIME_NETWORK_IS_METERED";
    /** REALTIME_NETWORK_IS_ROAMING - A boolean for if the current network is roaming. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REALTIME_NETWORK_IS_ROAMING"] = 24] = "REALTIME_NETWORK_IS_ROAMING";
    /** CAMERA_IS_FIRST_CAPTURE - A boolean indicating whether this is the first capture after app cold start */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA_IS_FIRST_CAPTURE"] = 25] = "CAMERA_IS_FIRST_CAPTURE";
    /** PICTURE_MODE - A enum indicating which type of image capture this was [GPU or JPEG] */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PICTURE_MODE"] = 26] = "PICTURE_MODE";
    /** BOLT_USE_CASE - An integer representing what BOLT usecase the caller is using. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BOLT_USE_CASE"] = 27] = "BOLT_USE_CASE";
    /** BOLT_CDN_EXPERIMENTATION_ID - An integer representing what BOLT cdn should be experimented with */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BOLT_CDN_EXPERIMENTATION_ID"] = 28] = "BOLT_CDN_EXPERIMENTATION_ID";
    /** MINS_SINCE_LAST_LENS_ACTIVATION - Num of mins since a user last activates the Lens. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MINS_SINCE_LAST_LENS_ACTIVATION"] = 29] = "MINS_SINCE_LAST_LENS_ACTIVATION";
    /** MINS_SINCE_LAST_SNAPPABLE_LENS_ACTIVATION - Num of mins since user activated snappable lens for the last time. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MINS_SINCE_LAST_SNAPPABLE_LENS_ACTIVATION"] = 30] = "MINS_SINCE_LAST_SNAPPABLE_LENS_ACTIVATION";
    /** DAYS_SINCE_LAST_LOGIN_OR_OPEN - Integer between 0 .. N which is the number of days since the user logged in or opened the app */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_LAST_LOGIN_OR_OPEN"] = 31] = "DAYS_SINCE_LAST_LOGIN_OR_OPEN";
    /** SPECTACLES_VERSION - An enum for Spectacles version */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SPECTACLES_VERSION"] = 32] = "SPECTACLES_VERSION";
    /** STICKY_MAX_CONNECTION_TYPE - A client side property for evaluating the highest tier connection a client has recently used. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STICKY_MAX_CONNECTION_TYPE"] = 33] = "STICKY_MAX_CONNECTION_TYPE";
    /** WITH_ANIMATED_OVERLAY - A boolean indicating whether there is animated overlay with video snap. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["WITH_ANIMATED_OVERLAY"] = 34] = "WITH_ANIMATED_OVERLAY";
    /** ESTIMATED_DURATION_FOR_EVENT_MS - Estimated duration for a config-specific event */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ESTIMATED_DURATION_FOR_EVENT_MS"] = 35] = "ESTIMATED_DURATION_FOR_EVENT_MS";
    /** URL - A URL */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["URL"] = 36] = "URL";
    /** MEDIA_SOURCE - content’s origin prior to being injected into the MDP stack */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MEDIA_SOURCE"] = 37] = "MEDIA_SOURCE";
    /** ASSET_TYPE - how to compose an individual asset into one larger logical asset */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ASSET_TYPE"] = 38] = "ASSET_TYPE";
    /** BOLT_IS_CONTENT_POPULAR - A boolean representing whether the content being resolved is popular or not. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BOLT_IS_CONTENT_POPULAR"] = 39] = "BOLT_IS_CONTENT_POPULAR";
    /** CAPTURE_MODE - A enum indicating which mode of capture this was [Timeline or Unknown] */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAPTURE_MODE"] = 40] = "CAPTURE_MODE";
    /** VP9_SOFTWARE_DECODING_SUPPORTED - A boolean representing if VP9 software decoding is supported */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["VP9_SOFTWARE_DECODING_SUPPORTED"] = 41] = "VP9_SOFTWARE_DECODING_SUPPORTED";
    /** AV1_SOFTWARE_DECODING_SUPPORTED - A boolean representing if AV1 software decoding is supported */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["AV1_SOFTWARE_DECODING_SUPPORTED"] = 42] = "AV1_SOFTWARE_DECODING_SUPPORTED";
    /** WITH_MUSIC - A boolean to indicate whether there is music edits (e.g. music sticker). */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["WITH_MUSIC"] = 43] = "WITH_MUSIC";
    /**
     * FEATURE_PROVIDED_SIZE - A client provided integer representing size. The units is decided by the caller.
     * This will be evaluated using FeatureProvidedSignals.size
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FEATURE_PROVIDED_SIZE"] = 44] = "FEATURE_PROVIDED_SIZE";
    /**
     * MEDIA_TYPE - An enum ordinal indicating the type of media being processed.
     * The ordinals are defined in snapchat.cdp.cof.media_provided_signals.proto
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MEDIA_TYPE"] = 45] = "MEDIA_TYPE";
    /** SCANNED_CATEGORY_IDS - The ids of the categories that are currently being scanned for (e.g. Amazon, Photomath, etc) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SCANNED_CATEGORY_IDS"] = 46] = "SCANNED_CATEGORY_IDS";
    /**
     * CONTENT_VIEW_SOURCE - Numerical value of ContentViewSource (Blizzard enum) which identified Opera session
     * On the client the value is provided via FeatureProvidedSignals.OperaSignals
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CONTENT_VIEW_SOURCE"] = 47] = "CONTENT_VIEW_SOURCE";
    /** RECIPIENTS_SUPPORT_HEVC - A boolean indicating whether a group of recipients support High Efficiency Video Encoding (HEVC) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["RECIPIENTS_SUPPORT_HEVC"] = 48] = "RECIPIENTS_SUPPORT_HEVC";
    /** COGNAC_APP_ID - A Cognac game/mini id that a user is playing */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["COGNAC_APP_ID"] = 49] = "COGNAC_APP_ID";
    /** EMAIL_VERIFIED - Whether user's email has verified */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["EMAIL_VERIFIED"] = 50] = "EMAIL_VERIFIED";
    /** PHONE_VERIFIED - Whether user's phone number has verified */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PHONE_VERIFIED"] = 51] = "PHONE_VERIFIED";
    /** NOTIFICATION_PERM_GRANTED - Whether notification permission has granted */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NOTIFICATION_PERM_GRANTED"] = 52] = "NOTIFICATION_PERM_GRANTED";
    /** CONTACT_PERM_GRANTED - Whether contact permission has granted */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CONTACT_PERM_GRANTED"] = 53] = "CONTACT_PERM_GRANTED";
    /** MUTUAL_FRIENDS_COUNT - Mutual friends count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MUTUAL_FRIENDS_COUNT"] = 54] = "MUTUAL_FRIENDS_COUNT";
    /** GROUPS_COUNT - Groups count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GROUPS_COUNT"] = 55] = "GROUPS_COUNT";
    /** HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS - Whether user has non-team snapchat conversations */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS"] = 56] = "HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS";
    /**
     * PLAYBACK_ITEM_TYPE - Numerical value of PlaybackItemType (Blizzard enum) which identifies an Opera playback item type
     * On the client the value is provided via PlaybackItemType
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PLAYBACK_ITEM_TYPE"] = 57] = "PLAYBACK_ITEM_TYPE";
    /**
     * SUP_BOOLEAN_PROPERTY - To get the corresponding SUP property values, check on property_metadata.sup_item_id.item_id
     * Sup boolean property
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_BOOLEAN_PROPERTY"] = 58] = "SUP_BOOLEAN_PROPERTY";
    /** SUP_INTEGER_PROPERTY - Sup integer property */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_INTEGER_PROPERTY"] = 59] = "SUP_INTEGER_PROPERTY";
    /** SUP_LONG_PROPERTY - Sup long property */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_LONG_PROPERTY"] = 60] = "SUP_LONG_PROPERTY";
    /** SUP_STRING_PROPERTY - Sup string property */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_STRING_PROPERTY"] = 61] = "SUP_STRING_PROPERTY";
    /** SUP_DOUBLE_PROPERTY - Sup double property */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_DOUBLE_PROPERTY"] = 62] = "SUP_DOUBLE_PROPERTY";
    /** SUP_MILLIS_PROPERTY_TO_NOW - Sup long property as timestamp in millis to get time elapsed */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_MILLIS_PROPERTY_TO_NOW"] = 63] = "SUP_MILLIS_PROPERTY_TO_NOW";
    /** HAS_USED_SPECTACLES - Whether the user has used Spectacles (incl. Cheerios) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_USED_SPECTACLES"] = 64] = "HAS_USED_SPECTACLES";
    /**
     * SUP_SECS_PROPERTY_TO_NOW - To get the corresponding SUP property values, check on property_metadata.sup_item_id.item_id
     * Sup long property as timestamp in seconds to get time elapsed
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_SECS_PROPERTY_TO_NOW"] = 65] = "SUP_SECS_PROPERTY_TO_NOW";
    /** BOLT_CLIENT_APP_STATE - Enum indicating client App State (Used by BOLT to create targeted network rules) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BOLT_CLIENT_APP_STATE"] = 66] = "BOLT_CLIENT_APP_STATE";
    /** EMAIL_SET - Whether user's email has been set (might or might not be verified) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["EMAIL_SET"] = 67] = "EMAIL_SET";
    /** BITMOJI_SET - Whether user's bitmoji has been set */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BITMOJI_SET"] = 68] = "BITMOJI_SET";
    /** QUICK_TAP_CAMERA_SUPPORTED_ENABLED - Quick tap on camera is supported and enabled */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["QUICK_TAP_CAMERA_SUPPORTED_ENABLED"] = 69] = "QUICK_TAP_CAMERA_SUPPORTED_ENABLED";
    /** HAS_ADD_FRIENDS_REQUEST - Whether user has add friends request */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_ADD_FRIENDS_REQUEST"] = 70] = "HAS_ADD_FRIENDS_REQUEST";
    /** APP_LAUNCH_FROM_PUSH - App launched from push notification */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_LAUNCH_FROM_PUSH"] = 71] = "APP_LAUNCH_FROM_PUSH";
    /** APP_LAUNCH_TYPE - App launch type: cold start, warm start, registration and login */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_LAUNCH_TYPE"] = 72] = "APP_LAUNCH_TYPE";
    /** BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT - Billboard campaign level locally tracked impression count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT"] = 73] = "BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked last impression time in secs to now */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 74] = "BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /** BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT - Billboard campaign level locally tracked continuous dismiss count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT"] = 75] = "BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT";
    /** FST_NUM_IN_APP_WARNINGS - FST number of in app warnings to show */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FST_NUM_IN_APP_WARNINGS"] = 76] = "FST_NUM_IN_APP_WARNINGS";
    /** FST_SHOULD_CHANGE_PASSWORD - FST user should change password */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FST_SHOULD_CHANGE_PASSWORD"] = 77] = "FST_SHOULD_CHANGE_PASSWORD";
    /** FST_IS_BACKGROUND_CHECK - Whether it's a background check or foreground check. Android specific */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FST_IS_BACKGROUND_CHECK"] = 78] = "FST_IS_BACKGROUND_CHECK";
    /** AUDIO_RECORD_PERM_GRANTED - Whether audio record permission has granted */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["AUDIO_RECORD_PERM_GRANTED"] = 79] = "AUDIO_RECORD_PERM_GRANTED";
    /** MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT - Mutual Friends with birthday count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT"] = 80] = "MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT - Billboard campaign level locally tracked dismiss count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT"] = 81] = "BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT";
    /** USE_VERTICAL_NAVIGATION - A boolean to indicate whether Opera uses vertical navigation or not. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USE_VERTICAL_NAVIGATION"] = 82] = "USE_VERTICAL_NAVIGATION";
    /**
     * FREEABLE_DISK_AVAILABLE - Freeable disk space available in mb. This included Content Manager freeable
     * bytes + DISK_AVAILABLE
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FREEABLE_DISK_AVAILABLE"] = 83] = "FREEABLE_DISK_AVAILABLE";
    /** MEMORIES_COUNT - Num of memories snaps user has */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MEMORIES_COUNT"] = 84] = "MEMORIES_COUNT";
    /** DEVICE_MODEL - DEVICE_MODEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_MODEL"] = 85] = "DEVICE_MODEL";
    /** COUNTRY - COUNTRY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["COUNTRY"] = 86] = "COUNTRY";
    /** OS - OS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["OS"] = 87] = "OS";
    /** OS_VERSION - OS_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["OS_VERSION"] = 88] = "OS_VERSION";
    /** APP_VERSION - APP_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_VERSION"] = 89] = "APP_VERSION";
    /** BUILD_FLAVOR - BUILD_FLAVOR (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BUILD_FLAVOR"] = 90] = "BUILD_FLAVOR";
    /** USER_ID - USER_ID (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_ID"] = 91] = "USER_ID";
    /** LOCALE - LOCALE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LOCALE"] = 92] = "LOCALE";
    /** DEVICE_CLUSTER - DEVICE_CLUSTER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_CLUSTER"] = 93] = "DEVICE_CLUSTER";
    /** DEVICE_BRAND - DEVICE_BRAND (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_BRAND"] = 94] = "DEVICE_BRAND";
    /** IS_EMPLOYEE - IS_EMPLOYEE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_EMPLOYEE"] = 95] = "IS_EMPLOYEE";
    /** USERNAME - USERNAME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USERNAME"] = 96] = "USERNAME";
    /** IS_TEST_USER - IS_TEST_USER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_TEST_USER"] = 97] = "IS_TEST_USER";
    /** USER_PROFILE - USER_PROFILE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_PROFILE"] = 98] = "USER_PROFILE";
    /** SCREEN_WIDTH - SCREEN_WIDTH (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SCREEN_WIDTH"] = 99] = "SCREEN_WIDTH";
    /** SCREEN_HEIGHT - SCREEN_HEIGHT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SCREEN_HEIGHT"] = 100] = "SCREEN_HEIGHT";
    /** HEVC_SUPPORT - HEVC_SUPPORT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HEVC_SUPPORT"] = 101] = "HEVC_SUPPORT";
    /** NETWORK_TYPE - NETWORK_TYPE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NETWORK_TYPE"] = 102] = "NETWORK_TYPE";
    /** MAX_VIDEO_WIDTH - MAX_VIDEO_WIDTH (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MAX_VIDEO_WIDTH"] = 103] = "MAX_VIDEO_WIDTH";
    /** MAX_VIDEO_HEIGHT - MAX_VIDEO_HEIGHT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MAX_VIDEO_HEIGHT"] = 104] = "MAX_VIDEO_HEIGHT";
    /** IS_NETWORK_METERED - IS_NETWORK_METERED (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_NETWORK_METERED"] = 105] = "IS_NETWORK_METERED";
    /** IS_ROAMING - IS_ROAMING (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_ROAMING"] = 106] = "IS_ROAMING";
    /** APP_ENGAGEMENT_LEVEL - APP_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_ENGAGEMENT_LEVEL"] = 107] = "APP_ENGAGEMENT_LEVEL";
    /** COMMUNICATION_ENGAGEMENT_LEVEL - COMMUNICATION_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["COMMUNICATION_ENGAGEMENT_LEVEL"] = 108] = "COMMUNICATION_ENGAGEMENT_LEVEL";
    /** FRIEND_STORY_ENGAGEMENT_LEVEL - FRIEND_STORY_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FRIEND_STORY_ENGAGEMENT_LEVEL"] = 109] = "FRIEND_STORY_ENGAGEMENT_LEVEL";
    /** PUBLIC_USER_STORY_ENGAGEMENT_LEVEL - PUBLIC_USER_STORY_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PUBLIC_USER_STORY_ENGAGEMENT_LEVEL"] = 110] = "PUBLIC_USER_STORY_ENGAGEMENT_LEVEL";
    /** PUBLISHER_STORY_ENGAGEMENT_LEVEL - PUBLISHER_STORY_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PUBLISHER_STORY_ENGAGEMENT_LEVEL"] = 111] = "PUBLISHER_STORY_ENGAGEMENT_LEVEL";
    /** LENS_ENGAGEMENT_LEVEL - LENS_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_ENGAGEMENT_LEVEL"] = 112] = "LENS_ENGAGEMENT_LEVEL";
    /** NON_FRIEND_STORY_ENGAGEMENT_LEVEL - NON_FRIEND_STORY_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NON_FRIEND_STORY_ENGAGEMENT_LEVEL"] = 113] = "NON_FRIEND_STORY_ENGAGEMENT_LEVEL";
    /** FOLLOWER_SIZE_LEVEL - FOLLOWER_SIZE_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FOLLOWER_SIZE_LEVEL"] = 114] = "FOLLOWER_SIZE_LEVEL";
    /** DAYS_SINCE_CREATION - DAYS_SINCE_CREATION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_CREATION"] = 115] = "DAYS_SINCE_CREATION";
    /** USER_PERSONA - USER_PERSONA (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_PERSONA"] = 116] = "USER_PERSONA";
    /** USER_CREATION_TIME - USER_CREATION_TIME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_CREATION_TIME"] = 117] = "USER_CREATION_TIME";
    /** MOBILE_NETWORK_TYPE - MOBILE_NETWORK_TYPE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MOBILE_NETWORK_TYPE"] = 118] = "MOBILE_NETWORK_TYPE";
    /** AGGREGATED_USER_BANDWIDTH - AGGREGATED_USER_BANDWIDTH (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["AGGREGATED_USER_BANDWIDTH"] = 119] = "AGGREGATED_USER_BANDWIDTH";
    /** LENS_CLUSTER_BIASED - LENS_CLUSTER_BIASED (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_CLUSTER_BIASED"] = 120] = "LENS_CLUSTER_BIASED";
    /** LENS_CLUSTER_LOG - LENS_CLUSTER_LOG (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_CLUSTER_LOG"] = 121] = "LENS_CLUSTER_LOG";
    /** LENS_CLUSTER_ORIG - LENS_CLUSTER_ORIG (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_CLUSTER_ORIG"] = 122] = "LENS_CLUSTER_ORIG";
    /** LENS_CLUSTER_ORIG_4 - LENS_CLUSTER_ORIG_4 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_CLUSTER_ORIG_4"] = 123] = "LENS_CLUSTER_ORIG_4";
    /** LENS_CLUSTER_BIASED_LOG - LENS_CLUSTER_BIASED_LOG (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_CLUSTER_BIASED_LOG"] = 124] = "LENS_CLUSTER_BIASED_LOG";
    /** APP_LOCALE - APP_LOCALE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_LOCALE"] = 125] = "APP_LOCALE";
    /** DEVICE_COMMON_NAME - DEVICE_COMMON_NAME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_COMMON_NAME"] = 126] = "DEVICE_COMMON_NAME";
    /** USER_FRIEND_COUNT - USER_FRIEND_COUNT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_FRIEND_COUNT"] = 127] = "USER_FRIEND_COUNT";
    /** L90_COUNTRY - L90_COUNTRY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["L90_COUNTRY"] = 128] = "L90_COUNTRY";
    /** STUB - STUB (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STUB"] = 129] = "STUB";
    /** IS_TRUE - IS_TRUE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_TRUE"] = 130] = "IS_TRUE";
    /** STORY_POST_RATIO - STORY_POST_RATIO (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STORY_POST_RATIO"] = 131] = "STORY_POST_RATIO";
    /** GENDER - GENDER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GENDER"] = 132] = "GENDER";
    /** INFERRED_AGE_BUCKET - INFERRED_AGE_BUCKET (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["INFERRED_AGE_BUCKET"] = 133] = "INFERRED_AGE_BUCKET";
    /** STORIES - STORIES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STORIES"] = 134] = "STORIES";
    /** SENDS - SENDS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SENDS"] = 135] = "SENDS";
    /** SNAP_CREATE_L7 - SNAP_CREATE_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAP_CREATE_L7"] = 136] = "SNAP_CREATE_L7";
    /** STORY_POST_L7 - STORY_POST_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STORY_POST_L7"] = 137] = "STORY_POST_L7";
    /** COMMUNICATION_L7 - COMMUNICATION_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["COMMUNICATION_L7"] = 138] = "COMMUNICATION_L7";
    /** DF_L7 - DF_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DF_L7"] = 139] = "DF_L7";
    /** APP_L7 - APP_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_L7"] = 140] = "APP_L7";
    /** DAYS_SINCE_CREATION_BUCKET - DAYS_SINCE_CREATION_BUCKET (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_CREATION_BUCKET"] = 141] = "DAYS_SINCE_CREATION_BUCKET";
    /** BIDIRECTIONAL_FRIEND_STATUS - BIDIRECTIONAL_FRIEND_STATUS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BIDIRECTIONAL_FRIEND_STATUS"] = 142] = "BIDIRECTIONAL_FRIEND_STATUS";
    /** STORY_POST_PREDICTION - STORY_POST_PREDICTION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STORY_POST_PREDICTION"] = 143] = "STORY_POST_PREDICTION";
    /** APP_OPENS - APP_OPENS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPENS"] = 144] = "APP_OPENS";
    /** LENS_SWIPES - LENS_SWIPES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_SWIPES"] = 145] = "LENS_SWIPES";
    /** LENS_SNAPS - LENS_SNAPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_SNAPS"] = 146] = "LENS_SNAPS";
    /** LENS_OPS - LENS_OPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_OPS"] = 147] = "LENS_OPS";
    /** W1_APP_OPENS - W1_APP_OPENS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W1_APP_OPENS"] = 148] = "W1_APP_OPENS";
    /** W1_LENS_SWIPES - W1_LENS_SWIPES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W1_LENS_SWIPES"] = 149] = "W1_LENS_SWIPES";
    /** W1_LENS_SNAPS - W1_LENS_SNAPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W1_LENS_SNAPS"] = 150] = "W1_LENS_SNAPS";
    /** W1_LENS_OPS - W1_LENS_OPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W1_LENS_OPS"] = 151] = "W1_LENS_OPS";
    /** W2_APP_OPENS - W2_APP_OPENS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W2_APP_OPENS"] = 152] = "W2_APP_OPENS";
    /** W2_LENS_SWIPES - W2_LENS_SWIPES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W2_LENS_SWIPES"] = 153] = "W2_LENS_SWIPES";
    /** W2_LENS_SNAPS - W2_LENS_SNAPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W2_LENS_SNAPS"] = 154] = "W2_LENS_SNAPS";
    /** W2_LENS_OPS - W2_LENS_OPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W2_LENS_OPS"] = 155] = "W2_LENS_OPS";
    /** W34_APP_OPENS - W34_APP_OPENS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W34_APP_OPENS"] = 156] = "W34_APP_OPENS";
    /** W34_LENS_SWIPES - W34_LENS_SWIPES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W34_LENS_SWIPES"] = 157] = "W34_LENS_SWIPES";
    /** W34_LENS_SNAPS - W34_LENS_SNAPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W34_LENS_SNAPS"] = 158] = "W34_LENS_SNAPS";
    /** W34_LENS_OPS - W34_LENS_OPS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["W34_LENS_OPS"] = 159] = "W34_LENS_OPS";
    /** LENS_SWIPES_PREDICTION - LENS_SWIPES_PREDICTION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENS_SWIPES_PREDICTION"] = 160] = "LENS_SWIPES_PREDICTION";
    /** REGISTRATION_COUNTRY - REGISTRATION_COUNTRY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REGISTRATION_COUNTRY"] = 161] = "REGISTRATION_COUNTRY";
    /** IP_ASN - IP_ASN (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IP_ASN"] = 162] = "IP_ASN";
    /** IP_REGION - IP_REGION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IP_REGION"] = 163] = "IP_REGION";
    /** IP_CITY - IP_CITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IP_CITY"] = 164] = "IP_CITY";
    /** HAS_USER_ID - HAS_USER_ID (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_USER_ID"] = 165] = "HAS_USER_ID";
    /** HAS_BITMOJI - HAS_BITMOJI (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_BITMOJI"] = 166] = "HAS_BITMOJI";
    /** NUM_APP_OPENS_LAST_8_DAYS - NUM_APP_OPENS_LAST_8_DAYS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NUM_APP_OPENS_LAST_8_DAYS"] = 167] = "NUM_APP_OPENS_LAST_8_DAYS";
    /** NUM_FEATURE_STORY_VIEW_DAYS_L7 - NUM_FEATURE_STORY_VIEW_DAYS_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NUM_FEATURE_STORY_VIEW_DAYS_L7"] = 168] = "NUM_FEATURE_STORY_VIEW_DAYS_L7";
    /** NUM_FEATURE_STORY_SYNC_DAYS_L7 - NUM_FEATURE_STORY_SYNC_DAYS_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NUM_FEATURE_STORY_SYNC_DAYS_L7"] = 169] = "NUM_FEATURE_STORY_SYNC_DAYS_L7";
    /** HEXAGON_NN_SUPPORTED_VERSION - HEXAGON_NN_SUPPORTED_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HEXAGON_NN_SUPPORTED_VERSION"] = 170] = "HEXAGON_NN_SUPPORTED_VERSION";
    /** NETWORK_QUALITY - NETWORK_QUALITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["NETWORK_QUALITY"] = 171] = "NETWORK_QUALITY";
    /** DEVICE_MEMORY_MB - DEVICE_MEMORY_MB (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_MEMORY_MB"] = 172] = "DEVICE_MEMORY_MB";
    /** DEVICE_GL_VERSION - DEVICE_GL_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_GL_VERSION"] = 173] = "DEVICE_GL_VERSION";
    /** SNAP_PRO_STATUS - SNAP_PRO_STATUS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAP_PRO_STATUS"] = 174] = "SNAP_PRO_STATUS";
    /** DEVICE_VP9_DECODING_SUPPORT - DEVICE_VP9_DECODING_SUPPORT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_VP9_DECODING_SUPPORT"] = 175] = "DEVICE_VP9_DECODING_SUPPORT";
    /** AVG_FRIEND_STORY_VIEW_COUNT_L7 - AVG_FRIEND_STORY_VIEW_COUNT_L7 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["AVG_FRIEND_STORY_VIEW_COUNT_L7"] = 176] = "AVG_FRIEND_STORY_VIEW_COUNT_L7";
    /** GAME_JOIN_TIME - GAME_JOIN_TIME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GAME_JOIN_TIME"] = 177] = "GAME_JOIN_TIME";
    /** GAME_LAST_ACTIVE_TIME - GAME_LAST_ACTIVE_TIME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GAME_LAST_ACTIVE_TIME"] = 178] = "GAME_LAST_ACTIVE_TIME";
    /** DAYS_SINCE_FIRST_GAME_ACTIVITY - DAYS_SINCE_FIRST_GAME_ACTIVITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_FIRST_GAME_ACTIVITY"] = 179] = "DAYS_SINCE_FIRST_GAME_ACTIVITY";
    /** DAYS_SINCE_LAST_GAME_ACTIVITY - DAYS_SINCE_LAST_GAME_ACTIVITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_LAST_GAME_ACTIVITY"] = 180] = "DAYS_SINCE_LAST_GAME_ACTIVITY";
    /** PF_PAGE_SESSIONS_WITH_LONG_IMP - PF_PAGE_SESSIONS_WITH_LONG_IMP (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["PF_PAGE_SESSIONS_WITH_LONG_IMP"] = 181] = "PF_PAGE_SESSIONS_WITH_LONG_IMP";
    /** WIRELESS_CARRIER - WIRELESS_CARRIER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["WIRELESS_CARRIER"] = 182] = "WIRELESS_CARRIER";
    /** MINIS_JOIN_TIME - MINIS_JOIN_TIME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MINIS_JOIN_TIME"] = 183] = "MINIS_JOIN_TIME";
    /** MINIS_LAST_ACTIVE_TIME - MINIS_LAST_ACTIVE_TIME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MINIS_LAST_ACTIVE_TIME"] = 184] = "MINIS_LAST_ACTIVE_TIME";
    /** DAYS_SINCE_FIRST_MINIS_ACTIVITY - DAYS_SINCE_FIRST_MINIS_ACTIVITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_FIRST_MINIS_ACTIVITY"] = 185] = "DAYS_SINCE_FIRST_MINIS_ACTIVITY";
    /** DAYS_SINCE_LAST_MINIS_ACTIVITY - DAYS_SINCE_LAST_MINIS_ACTIVITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_SINCE_LAST_MINIS_ACTIVITY"] = 186] = "DAYS_SINCE_LAST_MINIS_ACTIVITY";
    /** DEVICE_AV1_DECODING_SUPPORT - DEVICE_AV1_DECODING_SUPPORT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DEVICE_AV1_DECODING_SUPPORT"] = 187] = "DEVICE_AV1_DECODING_SUPPORT";
    /** APP_PACKAGE_INSTALLER - APP_PACKAGE_INSTALLER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_PACKAGE_INSTALLER"] = 188] = "APP_PACKAGE_INSTALLER";
    /** STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL - STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL"] = 189] = "STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL";
    /** REPORTED_AGE - REPORTED_AGE (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["REPORTED_AGE"] = 190] = "REPORTED_AGE";
    /** ANDROID_MOBILE_SERVICES_PROVIDER - ANDROID_MOBILE_SERVICES_PROVIDER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ANDROID_MOBILE_SERVICES_PROVIDER"] = 191] = "ANDROID_MOBILE_SERVICES_PROVIDER";
    /** IS_ACQUIRED_USER - IS_ACQUIRED_USER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_ACQUIRED_USER"] = 192] = "IS_ACQUIRED_USER";
    /** YDPI - YDPI (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["YDPI"] = 193] = "YDPI";
    /** BIDIRECTIONAL_FRIEND_STATUS_VELLUM - BIDIRECTIONAL_FRIEND_STATUS_VELLUM (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BIDIRECTIONAL_FRIEND_STATUS_VELLUM"] = 194] = "BIDIRECTIONAL_FRIEND_STATUS_VELLUM";
    /** ORIGIN - ORIGIN (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ORIGIN"] = 195] = "ORIGIN";
    /** LENSCORE_VERSION - LENSCORE_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LENSCORE_VERSION"] = 196] = "LENSCORE_VERSION";
    /** SNAPKIT_APP_ID - SNAPKIT_APP_ID (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAPKIT_APP_ID"] = 197] = "SNAPKIT_APP_ID";
    /** GPU - GPU (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GPU"] = 198] = "GPU";
    /** CHIPSET_NAME - CHIPSET_NAME (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CHIPSET_NAME"] = 199] = "CHIPSET_NAME";
    /** CHIPSET_VERSION - CHIPSET_VERSION (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CHIPSET_VERSION"] = 200] = "CHIPSET_VERSION";
    /** HAS_ZERO_IDFA - HAS_ZERO_IDFA (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_ZERO_IDFA"] = 201] = "HAS_ZERO_IDFA";
    /** LIMIT_AD_TRACKING - LIMIT_AD_TRACKING (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LIMIT_AD_TRACKING"] = 202] = "LIMIT_AD_TRACKING";
    /** ATT_AUTH_STATUS - ATT_AUTH_STATUS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ATT_AUTH_STATUS"] = 203] = "ATT_AUTH_STATUS";
    /** CAMERA2_FRONT_SS_GAIN_OVER_TPA - CAMERA2_FRONT_SS_GAIN_OVER_TPA (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA2_FRONT_SS_GAIN_OVER_TPA"] = 204] = "CAMERA2_FRONT_SS_GAIN_OVER_TPA";
    /** ATTACHMENT_TOOL_V2 - ATTACHMENT_TOOL_V2 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ATTACHMENT_TOOL_V2"] = 205] = "ATTACHMENT_TOOL_V2";
    /** USER_PERSONA_V3 - USER_PERSONA_V3 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["USER_PERSONA_V3"] = 206] = "USER_PERSONA_V3";
    /** SNAPS_SEND_WITH_HEVC - SNAPS_SEND_WITH_HEVC (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAPS_SEND_WITH_HEVC"] = 207] = "SNAPS_SEND_WITH_HEVC";
    /** SNAPS_SEND_WITHOUT_HEVC - SNAPS_SEND_WITHOUT_HEVC (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAPS_SEND_WITHOUT_HEVC"] = 208] = "SNAPS_SEND_WITHOUT_HEVC";
    /** CAMERA2_NATIVE_CRASH_OVER_CAMERA1 - CAMERA2_NATIVE_CRASH_OVER_CAMERA1 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA2_NATIVE_CRASH_OVER_CAMERA1"] = 209] = "CAMERA2_NATIVE_CRASH_OVER_CAMERA1";
    /** CAMERA2_G2S_LATENCY_OVER_CAMERA1 - CAMERA2_G2S_LATENCY_OVER_CAMERA1 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CAMERA2_G2S_LATENCY_OVER_CAMERA1"] = 210] = "CAMERA2_G2S_LATENCY_OVER_CAMERA1";
    /** IS_INTERNAL - IS_INTERNAL (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_INTERNAL"] = 211] = "IS_INTERNAL";
    /** IS_WEB - IS_WEB (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_WEB"] = 212] = "IS_WEB";
    /** APP_OPEN_TO_MAP - APP_OPEN_TO_MAP (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_MAP"] = 213] = "APP_OPEN_TO_MAP";
    /** APP_OPEN_TO_FRIENDSFEED - APP_OPEN_TO_FRIENDSFEED (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_FRIENDSFEED"] = 214] = "APP_OPEN_TO_FRIENDSFEED";
    /** APP_OPEN_TO_LENSES - APP_OPEN_TO_LENSES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_LENSES"] = 215] = "APP_OPEN_TO_LENSES";
    /** APP_OPEN_TO_MEMORIES - APP_OPEN_TO_MEMORIES (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_MEMORIES"] = 216] = "APP_OPEN_TO_MEMORIES";
    /** APP_OPEN_TO_COMMUNITY - APP_OPEN_TO_COMMUNITY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_COMMUNITY"] = 217] = "APP_OPEN_TO_COMMUNITY";
    /** APP_OPEN_TO_SPOTLIGHT - APP_OPEN_TO_SPOTLIGHT (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["APP_OPEN_TO_SPOTLIGHT"] = 218] = "APP_OPEN_TO_SPOTLIGHT";
    /** IS_IMPACTED_BY_PINC_893 - IS_IMPACTED_BY_PINC_893 (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["IS_IMPACTED_BY_PINC_893"] = 219] = "IS_IMPACTED_BY_PINC_893";
    /** DAYS_BEFORE_BIRTHDAY - DAYS_BEFORE_BIRTHDAY (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["DAYS_BEFORE_BIRTHDAY"] = 220] = "DAYS_BEFORE_BIRTHDAY";
    /** HAS_BIPA - HAS_BIPA (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_BIPA"] = 221] = "HAS_BIPA";
    /** SPOTLIGHT_STORY_ENGAGEMENT_STATUS - SPOTLIGHT_STORY_ENGAGEMENT_STATUS (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SPOTLIGHT_STORY_ENGAGEMENT_STATUS"] = 222] = "SPOTLIGHT_STORY_ENGAGEMENT_STATUS";
    /** INCLUSION_PANEL_MEMBER - INCLUSION_PANEL_MEMBER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["INCLUSION_PANEL_MEMBER"] = 223] = "INCLUSION_PANEL_MEMBER";
    /** HEVC_HW_DECODER - HEVC_HW_DECODER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HEVC_HW_DECODER"] = 224] = "HEVC_HW_DECODER";
    /** HEVC_SW_DECODER - HEVC_SW_DECODER (variant copied from TargetingProperty.Property) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HEVC_SW_DECODER"] = 225] = "HEVC_SW_DECODER";
    /**
     * HASH_MURMUR3_128_MOD_100 - Value of a Murmur3 hash of one of the hash_signals fields modulo 100
     * PropertyMetadata.signal_to_hash determines which field should be hashed
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HASH_MURMUR3_128_MOD_100"] = 226] = "HASH_MURMUR3_128_MOD_100";
    /** HAS_ACTIVE_SNAPCHAT_PLUS - Whether user has active Snapchat+ subscription in Atlas's UserData plus_subscription_info property */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["HAS_ACTIVE_SNAPCHAT_PLUS"] = 227] = "HAS_ACTIVE_SNAPCHAT_PLUS";
    /** SNAP_KIT_OAUTH_ID - OAuth Client ID identifying the source third party application for Snap Kit content */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAP_KIT_OAUTH_ID"] = 228] = "SNAP_KIT_OAUTH_ID";
    /** ORGANIZATION_TYPE - Organization type of advertiser purchasing ads */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ORGANIZATION_TYPE"] = 229] = "ORGANIZATION_TYPE";
    /**
     * CHUNK_UPLOAD_SUPPORT_REQUIRED - Client provided boolean that represent whether the config
     * needs to support uploading in a chunked/streaming manner
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CHUNK_UPLOAD_SUPPORT_REQUIRED"] = 230] = "CHUNK_UPLOAD_SUPPORT_REQUIRED";
    /** CLOUDFRONT_POP - The last known CloudFront Point of Presence used */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["CLOUDFRONT_POP"] = 231] = "CLOUDFRONT_POP";
    /** BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked first impresstion time to now */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW"] = 232] = "BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW";
    /** BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT - Billboard campaign level locally tracked click count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT"] = 233] = "BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT - Billboard campaign level locally tracked interaction count */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT"] = 234] = "BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked last interaction time to now */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW"] = 235] = "BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW";
    /** FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH - Lock screen widgets billboard - app open via push notification */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH"] = 236] = "FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH";
    /** WEB_SCREEN_WIDTH - Web browser screen width in CSS pixels */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["WEB_SCREEN_WIDTH"] = 237] = "WEB_SCREEN_WIDTH";
    /** WEB_SCREEN_HEIGHT - Web browser screen height in CSS pixels */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["WEB_SCREEN_HEIGHT"] = 238] = "WEB_SCREEN_HEIGHT";
    /** SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED - iOS 16 lock screen widgets enabled */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED"] = 239] = "SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED";
    /** SNAP_PRIVACY - Atlas enum property that indicates a user's snap privacy settings (Friends vs. Everyone) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SNAP_PRIVACY"] = 240] = "SNAP_PRIVACY";
    /** ADS_INTERFACES_IS_NEW_ORGANIZATION - Is a new organization in ads interfaces UI */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["ADS_INTERFACES_IS_NEW_ORGANIZATION"] = 241] = "ADS_INTERFACES_IS_NEW_ORGANIZATION";
    /** SERVER_ONLY - if true only server callers can get the config */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["SERVER_ONLY"] = 242] = "SERVER_ONLY";
    /** GHE_ORGANIZATION - The Github Enterprise organization */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GHE_ORGANIZATION"] = 243] = "GHE_ORGANIZATION";
    /** GHE_REPOSITORY - The Github Enterprise Repository, which is contained by a GHE organization */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GHE_REPOSITORY"] = 244] = "GHE_REPOSITORY";
    /** GHE_USER_EMAIL - In the context of Machamp, the user identifier (email) */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["GHE_USER_EMAIL"] = 245] = "GHE_USER_EMAIL";
    /** BUILD_DEFINITION_ID - In the context of Machamp, the build definition identifier is a UUID hash of the GHE organization and and repository. */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BUILD_DEFINITION_ID"] = 246] = "BUILD_DEFINITION_ID";
    /** BUILD_DEFINITION_NAME - In the context of Machamp, the build definition name is name of the build from the build.yaml */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["BUILD_DEFINITION_NAME"] = 247] = "BUILD_DEFINITION_NAME";
    /** LCA_PRINCIPAL - LCA Principal of the service calling Circumstance Engine */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["LCA_PRINCIPAL"] = 248] = "LCA_PRINCIPAL";
    /**
     * MEDIA_PERFORMANCE_CLASS - A performance class defines a set of device capabilities by Android
     * e.g. 30, 31, See https://android-developers.googleblog.com/2022/03/using-performance-class-to-optimize.html
     */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["MEDIA_PERFORMANCE_CLASS"] = 249] = "MEDIA_PERFORMANCE_CLASS";
    /** COMMUNITY_COUNT - Number of Communities user is in */
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["COMMUNITY_COUNT"] = 250] = "COMMUNITY_COUNT";
    ClientTargetingExpression_Property[ClientTargetingExpression_Property["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ClientTargetingExpression_Property || (ClientTargetingExpression_Property = {}));
/**
 * Which field of feature_provided_signals.hash_signals to hash
 * To be used by the HASH_MURMUR3_128_MOD_100 property only
 */
var ClientTargetingExpression_PropertyMetadata_SignalToHash;
(function (ClientTargetingExpression_PropertyMetadata_SignalToHash) {
    /** UNKNOWN_SIGNAL_TO_HASH - Unset variant */
    ClientTargetingExpression_PropertyMetadata_SignalToHash[ClientTargetingExpression_PropertyMetadata_SignalToHash["UNKNOWN_SIGNAL_TO_HASH"] = 0] = "UNKNOWN_SIGNAL_TO_HASH";
    /**
     * HASH_SEED_AND_USER_ID - Indicates that seed+user_id string should be hashed
     * (namespace is supplied by the server, user_id - by the client)
     */
    ClientTargetingExpression_PropertyMetadata_SignalToHash[ClientTargetingExpression_PropertyMetadata_SignalToHash["HASH_SEED_AND_USER_ID"] = 1] = "HASH_SEED_AND_USER_ID";
    /**
     * HASH_USER_ID_AND_NAMESPACE - Indicates that used_id+namespace string should be hashed
     * (seed is supplied by the server, user_id - by the client)
     */
    ClientTargetingExpression_PropertyMetadata_SignalToHash[ClientTargetingExpression_PropertyMetadata_SignalToHash["HASH_USER_ID_AND_NAMESPACE"] = 2] = "HASH_USER_ID_AND_NAMESPACE";
    ClientTargetingExpression_PropertyMetadata_SignalToHash[ClientTargetingExpression_PropertyMetadata_SignalToHash["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(ClientTargetingExpression_PropertyMetadata_SignalToHash || (ClientTargetingExpression_PropertyMetadata_SignalToHash = {}));
var baseConfigResult = {
    configId: "",
    ttlSeconds: 0,
    priority: 0,
    namespace: 0,
    studyName: "",
    experimentId: 0,
    delete: false,
    servePlatforms: 0,
    sequenceId: 0,
    ruidType: 0,
    segmentOrdinal: 0,
    generatedFromAbAllowlists: false,
};
var ConfigResult = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.configId !== "") {
            writer.uint32(10).string(message.configId);
        }
        if (message.value !== undefined) {
            Value.encode(message.value, writer.uint32(18).fork()).ldelim();
        }
        if (message.targetingExpression !== undefined) {
            ClientTargetingExpression.encode(message.targetingExpression, writer.uint32(26).fork()).ldelim();
        }
        if (message.ttlSeconds !== 0) {
            writer.uint32(32).int64(message.ttlSeconds);
        }
        if (message.configRuleUuidBytes.length !== 0) {
            writer.uint32(42).bytes(message.configRuleUuidBytes);
        }
        if (message.priority !== 0) {
            writer.uint32(48).int32(message.priority);
        }
        if (message.namespace !== 0) {
            writer.uint32(56).int32(message.namespace);
        }
        if (message.studyName !== "") {
            writer.uint32(66).string(message.studyName);
        }
        if (message.experimentId !== 0) {
            writer.uint32(72).int32(message.experimentId);
        }
        if (message.delete === true) {
            writer.uint32(80).bool(message.delete);
        }
        writer.uint32(90).fork();
        for (var _i = 0, _a = message.servePlatforms; _i < _a.length; _i++) {
            var v = _a[_i];
            writer.int32(v);
        }
        writer.ldelim();
        if (message.sequenceId !== 0) {
            writer.uint32(96).int32(message.sequenceId);
        }
        if (message.ruidType !== 0) {
            writer.uint32(104).int32(message.ruidType);
        }
        if (message.segmentOrdinal !== 0) {
            writer.uint32(112).int32(message.segmentOrdinal);
        }
        if (message.generatedFromAbAllowlists === true) {
            writer.uint32(120).bool(message.generatedFromAbAllowlists);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseConfigResult);
        message.servePlatforms = [];
        message.configRuleUuidBytes = new Uint8Array();
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.configId = reader.string();
                    break;
                case 2:
                    message.value = Value.decode(reader, reader.uint32());
                    break;
                case 3:
                    message.targetingExpression = ClientTargetingExpression.decode(reader, reader.uint32());
                    break;
                case 4:
                    message.ttlSeconds = longToNumber$1(reader.int64());
                    break;
                case 5:
                    message.configRuleUuidBytes = reader.bytes();
                    break;
                case 6:
                    message.priority = reader.int32();
                    break;
                case 7:
                    message.namespace = reader.int32();
                    break;
                case 8:
                    message.studyName = reader.string();
                    break;
                case 9:
                    message.experimentId = reader.int32();
                    break;
                case 10:
                    message.delete = reader.bool();
                    break;
                case 11:
                    if ((tag & 7) === 2) {
                        var end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.servePlatforms.push(reader.int32());
                        }
                    }
                    else {
                        message.servePlatforms.push(reader.int32());
                    }
                    break;
                case 12:
                    message.sequenceId = reader.int32();
                    break;
                case 13:
                    message.ruidType = reader.int32();
                    break;
                case 14:
                    message.segmentOrdinal = reader.int32();
                    break;
                case 15:
                    message.generatedFromAbAllowlists = reader.bool();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseConfigResult);
        message.servePlatforms = [];
        if (object.configId !== undefined && object.configId !== null) {
            message.configId = object.configId;
        }
        else {
            message.configId = "";
        }
        if (object.value !== undefined && object.value !== null) {
            message.value = Value.fromPartial(object.value);
        }
        else {
            message.value = undefined;
        }
        if (object.targetingExpression !== undefined &&
            object.targetingExpression !== null) {
            message.targetingExpression = ClientTargetingExpression.fromPartial(object.targetingExpression);
        }
        else {
            message.targetingExpression = undefined;
        }
        if (object.ttlSeconds !== undefined && object.ttlSeconds !== null) {
            message.ttlSeconds = object.ttlSeconds;
        }
        else {
            message.ttlSeconds = 0;
        }
        if (object.configRuleUuidBytes !== undefined &&
            object.configRuleUuidBytes !== null) {
            message.configRuleUuidBytes = object.configRuleUuidBytes;
        }
        else {
            message.configRuleUuidBytes = new Uint8Array();
        }
        if (object.priority !== undefined && object.priority !== null) {
            message.priority = object.priority;
        }
        else {
            message.priority = 0;
        }
        if (object.namespace !== undefined && object.namespace !== null) {
            message.namespace = object.namespace;
        }
        else {
            message.namespace = 0;
        }
        if (object.studyName !== undefined && object.studyName !== null) {
            message.studyName = object.studyName;
        }
        else {
            message.studyName = "";
        }
        if (object.experimentId !== undefined && object.experimentId !== null) {
            message.experimentId = object.experimentId;
        }
        else {
            message.experimentId = 0;
        }
        if (object.delete !== undefined && object.delete !== null) {
            message.delete = object.delete;
        }
        else {
            message.delete = false;
        }
        if (object.servePlatforms !== undefined && object.servePlatforms !== null) {
            for (var _i = 0, _a = object.servePlatforms; _i < _a.length; _i++) {
                var e = _a[_i];
                message.servePlatforms.push(e);
            }
        }
        if (object.sequenceId !== undefined && object.sequenceId !== null) {
            message.sequenceId = object.sequenceId;
        }
        else {
            message.sequenceId = 0;
        }
        if (object.ruidType !== undefined && object.ruidType !== null) {
            message.ruidType = object.ruidType;
        }
        else {
            message.ruidType = 0;
        }
        if (object.segmentOrdinal !== undefined && object.segmentOrdinal !== null) {
            message.segmentOrdinal = object.segmentOrdinal;
        }
        else {
            message.segmentOrdinal = 0;
        }
        if (object.generatedFromAbAllowlists !== undefined &&
            object.generatedFromAbAllowlists !== null) {
            message.generatedFromAbAllowlists = object.generatedFromAbAllowlists;
        }
        else {
            message.generatedFromAbAllowlists = false;
        }
        return message;
    },
};
var baseClientTargetingExpression = {
    operator: 0,
    property: 0,
    predicateOperator: 0,
};
var ClientTargetingExpression = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.operator !== 0) {
            writer.uint32(8).int32(message.operator);
        }
        for (var _i = 0, _a = message.children; _i < _a.length; _i++) {
            var v = _a[_i];
            ClientTargetingExpression.encode(v, writer.uint32(18).fork()).ldelim();
        }
        if (message.property !== 0) {
            writer.uint32(24).int32(message.property);
        }
        if (message.predicateOperator !== 0) {
            writer.uint32(32).int32(message.predicateOperator);
        }
        if (message.value !== undefined) {
            Value.encode(message.value, writer.uint32(42).fork()).ldelim();
        }
        if (message.propertyMetadata !== undefined) {
            ClientTargetingExpression_PropertyMetadata.encode(message.propertyMetadata, writer.uint32(50).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseClientTargetingExpression);
        message.children = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.operator = reader.int32();
                    break;
                case 2:
                    message.children.push(ClientTargetingExpression.decode(reader, reader.uint32()));
                    break;
                case 3:
                    message.property = reader.int32();
                    break;
                case 4:
                    message.predicateOperator = reader.int32();
                    break;
                case 5:
                    message.value = Value.decode(reader, reader.uint32());
                    break;
                case 6:
                    message.propertyMetadata =
                        ClientTargetingExpression_PropertyMetadata.decode(reader, reader.uint32());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseClientTargetingExpression);
        message.children = [];
        if (object.operator !== undefined && object.operator !== null) {
            message.operator = object.operator;
        }
        else {
            message.operator = 0;
        }
        if (object.children !== undefined && object.children !== null) {
            for (var _i = 0, _a = object.children; _i < _a.length; _i++) {
                var e = _a[_i];
                message.children.push(ClientTargetingExpression.fromPartial(e));
            }
        }
        if (object.property !== undefined && object.property !== null) {
            message.property = object.property;
        }
        else {
            message.property = 0;
        }
        if (object.predicateOperator !== undefined &&
            object.predicateOperator !== null) {
            message.predicateOperator = object.predicateOperator;
        }
        else {
            message.predicateOperator = 0;
        }
        if (object.value !== undefined && object.value !== null) {
            message.value = Value.fromPartial(object.value);
        }
        else {
            message.value = undefined;
        }
        if (object.propertyMetadata !== undefined &&
            object.propertyMetadata !== null) {
            message.propertyMetadata =
                ClientTargetingExpression_PropertyMetadata.fromPartial(object.propertyMetadata);
        }
        else {
            message.propertyMetadata = undefined;
        }
        return message;
    },
};
var baseClientTargetingExpression_PropertyMetadata = {
    itemId: 0,
    signalToHash: 0,
    abNamespaceForHashing: "",
    abSeedForHashing: "",
};
var ClientTargetingExpression_PropertyMetadata = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.itemId !== 0) {
            writer.uint32(8).int32(message.itemId);
        }
        if (message.signalToHash !== 0) {
            writer.uint32(16).int32(message.signalToHash);
        }
        if (message.abNamespaceForHashing !== "") {
            writer.uint32(26).string(message.abNamespaceForHashing);
        }
        if (message.abSeedForHashing !== "") {
            writer.uint32(34).string(message.abSeedForHashing);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseClientTargetingExpression_PropertyMetadata);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.itemId = reader.int32();
                    break;
                case 2:
                    message.signalToHash = reader.int32();
                    break;
                case 3:
                    message.abNamespaceForHashing = reader.string();
                    break;
                case 4:
                    message.abSeedForHashing = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseClientTargetingExpression_PropertyMetadata);
        if (object.itemId !== undefined && object.itemId !== null) {
            message.itemId = object.itemId;
        }
        else {
            message.itemId = 0;
        }
        if (object.signalToHash !== undefined && object.signalToHash !== null) {
            message.signalToHash = object.signalToHash;
        }
        else {
            message.signalToHash = 0;
        }
        if (object.abNamespaceForHashing !== undefined &&
            object.abNamespaceForHashing !== null) {
            message.abNamespaceForHashing = object.abNamespaceForHashing;
        }
        else {
            message.abNamespaceForHashing = "";
        }
        if (object.abSeedForHashing !== undefined &&
            object.abSeedForHashing !== null) {
            message.abSeedForHashing = object.abSeedForHashing;
        }
        else {
            message.abSeedForHashing = "";
        }
        return message;
    },
};
var globalThis$4 = (function () {
    if (typeof globalThis$4 !== "undefined")
        return globalThis$4;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function longToNumber$1(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$4.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var baseConfigTargetingResponseDebugData = {
    abResultChecksumInput: "",
};
var ConfigTargetingResponseDebugData = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.abResultChecksumInput !== "") {
            writer.uint32(10).string(message.abResultChecksumInput);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseConfigTargetingResponseDebugData);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.abResultChecksumInput = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseConfigTargetingResponseDebugData);
        if (object.abResultChecksumInput !== undefined &&
            object.abResultChecksumInput !== null) {
            message.abResultChecksumInput = object.abResultChecksumInput;
        }
        else {
            message.abResultChecksumInput = "";
        }
        return message;
    },
};
var baseConfigTargetingResponse = {
    configResultsEtag: "",
    fullResults: false,
    abResultChecksum: 0,
    iso3166Alpha2CountryCodeFromRequestIp: "",
};
var ConfigTargetingResponse = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        for (var _i = 0, _a = message.configResults; _i < _a.length; _i++) {
            var v = _a[_i];
            ConfigResult.encode(v, writer.uint32(10).fork()).ldelim();
        }
        if (message.configResultsEtag !== "") {
            writer.uint32(18).string(message.configResultsEtag);
        }
        if (message.benchmarkRequest !== undefined) {
            BenchmarkRequest.encode(message.benchmarkRequest, writer.uint32(26).fork()).ldelim();
        }
        if (message.fullResults === true) {
            writer.uint32(32).bool(message.fullResults);
        }
        if (message.abResultChecksum !== 0) {
            writer.uint32(40).int32(message.abResultChecksum);
        }
        if (message.iso3166Alpha2CountryCodeFromRequestIp !== "") {
            writer.uint32(50).string(message.iso3166Alpha2CountryCodeFromRequestIp);
        }
        if (message.debugData !== undefined) {
            ConfigTargetingResponseDebugData.encode(message.debugData, writer.uint32(58).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseConfigTargetingResponse);
        message.configResults = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.configResults.push(ConfigResult.decode(reader, reader.uint32()));
                    break;
                case 2:
                    message.configResultsEtag = reader.string();
                    break;
                case 3:
                    message.benchmarkRequest = BenchmarkRequest.decode(reader, reader.uint32());
                    break;
                case 4:
                    message.fullResults = reader.bool();
                    break;
                case 5:
                    message.abResultChecksum = reader.int32();
                    break;
                case 6:
                    message.iso3166Alpha2CountryCodeFromRequestIp = reader.string();
                    break;
                case 7:
                    message.debugData = ConfigTargetingResponseDebugData.decode(reader, reader.uint32());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseConfigTargetingResponse);
        message.configResults = [];
        if (object.configResults !== undefined && object.configResults !== null) {
            for (var _i = 0, _a = object.configResults; _i < _a.length; _i++) {
                var e = _a[_i];
                message.configResults.push(ConfigResult.fromPartial(e));
            }
        }
        if (object.configResultsEtag !== undefined &&
            object.configResultsEtag !== null) {
            message.configResultsEtag = object.configResultsEtag;
        }
        else {
            message.configResultsEtag = "";
        }
        if (object.benchmarkRequest !== undefined &&
            object.benchmarkRequest !== null) {
            message.benchmarkRequest = BenchmarkRequest.fromPartial(object.benchmarkRequest);
        }
        else {
            message.benchmarkRequest = undefined;
        }
        if (object.fullResults !== undefined && object.fullResults !== null) {
            message.fullResults = object.fullResults;
        }
        else {
            message.fullResults = false;
        }
        if (object.abResultChecksum !== undefined &&
            object.abResultChecksum !== null) {
            message.abResultChecksum = object.abResultChecksum;
        }
        else {
            message.abResultChecksum = 0;
        }
        if (object.iso3166Alpha2CountryCodeFromRequestIp !== undefined &&
            object.iso3166Alpha2CountryCodeFromRequestIp !== null) {
            message.iso3166Alpha2CountryCodeFromRequestIp =
                object.iso3166Alpha2CountryCodeFromRequestIp;
        }
        else {
            message.iso3166Alpha2CountryCodeFromRequestIp = "";
        }
        if (object.debugData !== undefined && object.debugData !== null) {
            message.debugData = ConfigTargetingResponseDebugData.fromPartial(object.debugData);
        }
        else {
            message.debugData = undefined;
        }
        return message;
    },
};
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/* eslint-disable */
/** Namespace for grouping COF entities. */
var Namespace;
(function (Namespace) {
    Namespace[Namespace["DEFAULT"] = 0] = "DEFAULT";
    Namespace[Namespace["CAMERA"] = 1] = "CAMERA";
    Namespace[Namespace["COF"] = 2] = "COF";
    Namespace[Namespace["MDP"] = 3] = "MDP";
    Namespace[Namespace["MONETIZATION"] = 4] = "MONETIZATION";
    Namespace[Namespace["LEGACY_AB"] = 5] = "LEGACY_AB";
    /** AB_STUDY_VERSION - The value of a AB config is study version. */
    Namespace[Namespace["AB_STUDY_VERSION"] = 6] = "AB_STUDY_VERSION";
    Namespace[Namespace["CAMERA_PLATFORM"] = 7] = "CAMERA_PLATFORM";
    Namespace[Namespace["PLATFORM_UI"] = 8] = "PLATFORM_UI";
    Namespace[Namespace["PLATFORM_CORE"] = 9] = "PLATFORM_CORE";
    Namespace[Namespace["MONETIZATION_UI"] = 10] = "MONETIZATION_UI";
    Namespace[Namespace["MONETIZATION_RANKING"] = 11] = "MONETIZATION_RANKING";
    Namespace[Namespace["USERS_AND_FRIENDS_UI"] = 12] = "USERS_AND_FRIENDS_UI";
    Namespace[Namespace["USERS_AND_FRIENDS_CORE"] = 13] = "USERS_AND_FRIENDS_CORE";
    Namespace[Namespace["USERS_AND_FRIENDS_RANKING"] = 14] = "USERS_AND_FRIENDS_RANKING";
    Namespace[Namespace["MESSAGING_UI"] = 15] = "MESSAGING_UI";
    Namespace[Namespace["MESSAGING_CORE"] = 16] = "MESSAGING_CORE";
    /** @deprecated */
    Namespace[Namespace["STORIES_UI"] = 17] = "STORIES_UI";
    Namespace[Namespace["STORIES_CORE"] = 18] = "STORIES_CORE";
    Namespace[Namespace["STORIES_RANKING"] = 19] = "STORIES_RANKING";
    Namespace[Namespace["DISCOVER_UI"] = 20] = "DISCOVER_UI";
    Namespace[Namespace["DISCOVER_CORE"] = 21] = "DISCOVER_CORE";
    Namespace[Namespace["DISCOVER_RANKING"] = 22] = "DISCOVER_RANKING";
    Namespace[Namespace["PREMIUM_CONTENT_UI"] = 23] = "PREMIUM_CONTENT_UI";
    Namespace[Namespace["PREMIUM_CONTENT_CORE"] = 24] = "PREMIUM_CONTENT_CORE";
    Namespace[Namespace["PREMIUM_CONTENT_RANKING"] = 25] = "PREMIUM_CONTENT_RANKING";
    Namespace[Namespace["COMMUNITY_UI"] = 26] = "COMMUNITY_UI";
    Namespace[Namespace["COMMUNITY_CORE"] = 27] = "COMMUNITY_CORE";
    Namespace[Namespace["COMMUNITY_RANKING"] = 28] = "COMMUNITY_RANKING";
    Namespace[Namespace["CAMERA_UI"] = 29] = "CAMERA_UI";
    Namespace[Namespace["CREATIVE_TOOLS_UI"] = 30] = "CREATIVE_TOOLS_UI";
    Namespace[Namespace["CREATIVE_TOOLS_CORE"] = 31] = "CREATIVE_TOOLS_CORE";
    Namespace[Namespace["CAMERA_PLATFORM_UI"] = 32] = "CAMERA_PLATFORM_UI";
    Namespace[Namespace["CAMERA_PLATFORM_RANKING"] = 33] = "CAMERA_PLATFORM_RANKING";
    Namespace[Namespace["PERCEPTION_UI"] = 34] = "PERCEPTION_UI";
    Namespace[Namespace["PERCEPTION_CORE"] = 35] = "PERCEPTION_CORE";
    Namespace[Namespace["PERCEPTION_RANKING"] = 36] = "PERCEPTION_RANKING";
    Namespace[Namespace["MEDIA_UI"] = 37] = "MEDIA_UI";
    Namespace[Namespace["MEMORIES_UI"] = 38] = "MEMORIES_UI";
    Namespace[Namespace["MEMORIES_CORE"] = 39] = "MEMORIES_CORE";
    Namespace[Namespace["MAP_UI"] = 40] = "MAP_UI";
    Namespace[Namespace["MAP_CORE"] = 41] = "MAP_CORE";
    Namespace[Namespace["SEARCH_UI"] = 42] = "SEARCH_UI";
    Namespace[Namespace["SEARCH_CORE"] = 43] = "SEARCH_CORE";
    Namespace[Namespace["SEARCH_RANKING"] = 44] = "SEARCH_RANKING";
    Namespace[Namespace["COGNAC_UI"] = 45] = "COGNAC_UI";
    Namespace[Namespace["COGNAC_CORE"] = 46] = "COGNAC_CORE";
    Namespace[Namespace["BITMOJI_UI"] = 47] = "BITMOJI_UI";
    Namespace[Namespace["BITMOJI_CORE"] = 48] = "BITMOJI_CORE";
    Namespace[Namespace["SPECTACLES_UI"] = 49] = "SPECTACLES_UI";
    Namespace[Namespace["SPECTACLES_CORE"] = 50] = "SPECTACLES_CORE";
    Namespace[Namespace["SNAP_KIT_UI"] = 51] = "SNAP_KIT_UI";
    Namespace[Namespace["SNAP_KIT_CORE"] = 52] = "SNAP_KIT_CORE";
    Namespace[Namespace["SNAP_PRO_UI"] = 53] = "SNAP_PRO_UI";
    Namespace[Namespace["SNAP_PRO_CORE"] = 54] = "SNAP_PRO_CORE";
    Namespace[Namespace["NGS_UI"] = 55] = "NGS_UI";
    Namespace[Namespace["CAMEOS_UI"] = 56] = "CAMEOS_UI";
    Namespace[Namespace["CAMEOS_CORE"] = 57] = "CAMEOS_CORE";
    Namespace[Namespace["CAMEOS_RANKING"] = 58] = "CAMEOS_RANKING";
    Namespace[Namespace["BLIZZARD_CLIENT"] = 59] = "BLIZZARD_CLIENT";
    Namespace[Namespace["BLIZZARD_BACKEND"] = 60] = "BLIZZARD_BACKEND";
    Namespace[Namespace["AB_SEGMENT"] = 61] = "AB_SEGMENT";
    Namespace[Namespace["CONFIG_ROLLOUT"] = 62] = "CONFIG_ROLLOUT";
    Namespace[Namespace["COMMERCE_BACKEND"] = 63] = "COMMERCE_BACKEND";
    Namespace[Namespace["LENS_CORE"] = 64] = "LENS_CORE";
    Namespace[Namespace["CAMERA_KIT_CORE"] = 65] = "CAMERA_KIT_CORE";
    Namespace[Namespace["MDP_CONTENT_MANAGER"] = 66] = "MDP_CONTENT_MANAGER";
    Namespace[Namespace["REQUEST_DOMAIN_ROUTING"] = 67] = "REQUEST_DOMAIN_ROUTING";
    Namespace[Namespace["CDN_ROUTING"] = 68] = "CDN_ROUTING";
    Namespace[Namespace["GRAPHENE_SAMPLING"] = 69] = "GRAPHENE_SAMPLING";
    Namespace[Namespace["WEB_STORY_CORE"] = 70] = "WEB_STORY_CORE";
    Namespace[Namespace["WEB_STORY_UI"] = 71] = "WEB_STORY_UI";
    Namespace[Namespace["CLIENT_SWITCHBOARD"] = 72] = "CLIENT_SWITCHBOARD";
    /** MCS - Messaging core service, aka Arroyo.  See go/mcs-quick-exps */
    Namespace[Namespace["MCS"] = 73] = "MCS";
    Namespace[Namespace["SHARING_UI"] = 74] = "SHARING_UI";
    Namespace[Namespace["SHARING_CORE"] = 75] = "SHARING_CORE";
    Namespace[Namespace["SHARING_RANKING"] = 76] = "SHARING_RANKING";
    Namespace[Namespace["MDP_PLAYBACK"] = 77] = "MDP_PLAYBACK";
    Namespace[Namespace["CAMERA_BUSINESS"] = 78] = "CAMERA_BUSINESS";
    Namespace[Namespace["ADDLIVE"] = 79] = "ADDLIVE";
    /** AD_KIT_CORE - Ad Kit Core */
    Namespace[Namespace["AD_KIT_CORE"] = 80] = "AD_KIT_CORE";
    Namespace[Namespace["ADDLIVE_UI"] = 81] = "ADDLIVE_UI";
    Namespace[Namespace["ADDLIVE_CORE"] = 82] = "ADDLIVE_CORE";
    Namespace[Namespace["ADDLIVE_RANKING"] = 83] = "ADDLIVE_RANKING";
    Namespace[Namespace["STORY_STUDIO_LITE_UI"] = 84] = "STORY_STUDIO_LITE_UI";
    Namespace[Namespace["STORY_STUDIO_LITE_CORE"] = 85] = "STORY_STUDIO_LITE_CORE";
    Namespace[Namespace["COMMERCE_UI"] = 86] = "COMMERCE_UI";
    Namespace[Namespace["COMMERCE_CORE"] = 87] = "COMMERCE_CORE";
    Namespace[Namespace["COMMERCE_RANKING"] = 88] = "COMMERCE_RANKING";
    Namespace[Namespace["STORIES_UX"] = 89] = "STORIES_UX";
    /** CONTEXT_UI - Context / Interactions */
    Namespace[Namespace["CONTEXT_UI"] = 90] = "CONTEXT_UI";
    Namespace[Namespace["CONTEXT_CORE"] = 91] = "CONTEXT_CORE";
    Namespace[Namespace["LENS_TARGETING"] = 92] = "LENS_TARGETING";
    Namespace[Namespace["SPOTLIGHT_RANKING"] = 93] = "SPOTLIGHT_RANKING";
    Namespace[Namespace["SPOTLIGHT_CORE"] = 94] = "SPOTLIGHT_CORE";
    Namespace[Namespace["SPOTLIGHT_UI"] = 95] = "SPOTLIGHT_UI";
    Namespace[Namespace["ACTIVATION_CORE"] = 96] = "ACTIVATION_CORE";
    Namespace[Namespace["FRIENDING"] = 97] = "FRIENDING";
    /** SAFETY - Platform Integrity Engineering */
    Namespace[Namespace["SAFETY"] = 98] = "SAFETY";
    /** LOGIN - Login Processing */
    Namespace[Namespace["LOGIN"] = 99] = "LOGIN";
    Namespace[Namespace["MYLENSES_WEB_CORE"] = 100] = "MYLENSES_WEB_CORE";
    Namespace[Namespace["SECURITY"] = 101] = "SECURITY";
    Namespace[Namespace["AB_CONSOLE"] = 102] = "AB_CONSOLE";
    Namespace[Namespace["AD_MODERATION"] = 103] = "AD_MODERATION";
    Namespace[Namespace["CONTENT_LIFT_MEASUREMENT"] = 104] = "CONTENT_LIFT_MEASUREMENT";
    Namespace[Namespace["CONTEXT_BACKEND"] = 105] = "CONTEXT_BACKEND";
    Namespace[Namespace["GAMES_FEED_CORE"] = 106] = "GAMES_FEED_CORE";
    Namespace[Namespace["RECIPIENT_DEVICE_CAPABILITIES"] = 107] = "RECIPIENT_DEVICE_CAPABILITIES";
    /** ADS_GATEWAY_CORE - Ads Manager */
    Namespace[Namespace["ADS_GATEWAY_CORE"] = 108] = "ADS_GATEWAY_CORE";
    Namespace[Namespace["ADS_INTERFACES_UI"] = 109] = "ADS_INTERFACES_UI";
    Namespace[Namespace["ADS_MANAGER_UI"] = 110] = "ADS_MANAGER_UI";
    Namespace[Namespace["BUSINESS_MANAGER_UI"] = 111] = "BUSINESS_MANAGER_UI";
    Namespace[Namespace["PROFILE_MANAGER_UI"] = 112] = "PROFILE_MANAGER_UI";
    /** NOTIFICATION - Notification */
    Namespace[Namespace["NOTIFICATION"] = 113] = "NOTIFICATION";
    /** BILLBOARD - Billboard */
    Namespace[Namespace["BILLBOARD"] = 114] = "BILLBOARD";
    Namespace[Namespace["LENS_DELIVERY_CORE"] = 115] = "LENS_DELIVERY_CORE";
    /** TIER_0_LAUNCH - Namespace for major launches */
    Namespace[Namespace["TIER_0_LAUNCH"] = 116] = "TIER_0_LAUNCH";
    /** FRIENDING_REG - Namespace for friending suggestion in registration */
    Namespace[Namespace["FRIENDING_REG"] = 117] = "FRIENDING_REG";
    /** CAMERA_KIT_WEB_CORE - Namespace for @snapchat/camera-kit npm package */
    Namespace[Namespace["CAMERA_KIT_WEB_CORE"] = 118] = "CAMERA_KIT_WEB_CORE";
    Namespace[Namespace["MDP_OPERA_CORE"] = 119] = "MDP_OPERA_CORE";
    Namespace[Namespace["SHARED_WEB_UI"] = 120] = "SHARED_WEB_UI";
    /** CODELABS_SANDBOX - Sandbox namespace for A/B Codelabs. */
    Namespace[Namespace["CODELABS_SANDBOX"] = 121] = "CODELABS_SANDBOX";
    Namespace[Namespace["FRIENDING_QA_RANKING"] = 122] = "FRIENDING_QA_RANKING";
    Namespace[Namespace["FRIENDING_REG_RANKING"] = 123] = "FRIENDING_REG_RANKING";
    Namespace[Namespace["SEARCH_CSL"] = 124] = "SEARCH_CSL";
    Namespace[Namespace["WEB3"] = 125] = "WEB3";
    Namespace[Namespace["PLUS_CORE"] = 126] = "PLUS_CORE";
    Namespace[Namespace["WEB_STORYTELLER_UI"] = 127] = "WEB_STORYTELLER_UI";
    Namespace[Namespace["PLATFORM_SERVICES"] = 128] = "PLATFORM_SERVICES";
    Namespace[Namespace["WEB_ACCOUNTS"] = 129] = "WEB_ACCOUNTS";
    Namespace[Namespace["SHOPKIT_WEB"] = 130] = "SHOPKIT_WEB";
    Namespace[Namespace["SNAPGRAPH_MODSERVER"] = 131] = "SNAPGRAPH_MODSERVER";
    /** MUSIC - MUSIC is not a valid namespace name due to a conflict with another Enum. Use SOUNDS instead. */
    Namespace[Namespace["MUSIC"] = 132] = "MUSIC";
    Namespace[Namespace["SOUNDS"] = 133] = "SOUNDS";
    Namespace[Namespace["MACHAMP"] = 134] = "MACHAMP";
    Namespace[Namespace["PIE_AUTOBOT"] = 135] = "PIE_AUTOBOT";
    Namespace[Namespace["NOTIFICATION_RANKING"] = 136] = "NOTIFICATION_RANKING";
    Namespace[Namespace["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(Namespace || (Namespace = {}));
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** Checks if the targeting expression is the proto default. */
function isDefaultTargetingExpression(targetingExpression) {
    return (targetingExpression.operator ===
        ClientTargetingExpression_Operator.UNKNOWN_OPERATOR &&
        targetingExpression.predicateOperator ===
            ClientTargetingExpression_PredicateOperator.UNKNOWN_PREDICATE_OPERATOR &&
        targetingExpression.property ===
            ClientTargetingExpression_Property.UNKNOWN_PROPERTY);
}

/**
 * Map for developer-provided callbacks on how to retrieve values for client targeting properties.
 */
var targetingPropertyValueProviders = new Map();
/**
 * Registers providers for targeting property values. These providers will be used when evaluating config rules
 * using client-side targeting expressions.
 */
function registerPropertyValueProvider(targetingProperty, provider) {
    targetingPropertyValueProviders.set(targetingProperty, provider);
}
/**
 * Takes a list of config rules and returns the highest priority one that matches the configId and that has a
 * satisfied targeting expression. If a provided config rule does not have a targeting expression, that means that
 * the targeting expression was evaluated server-side and already deemed to be satisfied. If no config rules
 * have targeting expressions that are satisfied or no config rule with the config name was returned, undefined is
 * returned.
 * @param configResults - List of ConfigResult-like objects (only configId and targetingExpression must be present)
 * @param configName
 */
function getEvaluatedCofConfig(configResults, configName) {
    var configRulesForConfigId = configResults.filter(function (configRule) { return configRule.configId === configName; });
    var noExpressionConfigRules = configRulesForConfigId.filter(function (configRule) {
        // Depending on the language for the generated proto code, targetingExpression may either be undefined or the
        // default proto message.
        return !configRule.targetingExpression ||
            (configRule.targetingExpression &&
                isDefaultTargetingExpression(configRule.targetingExpression));
    });
    var clientTargetingExpressionConfigRules = configRulesForConfigId.filter(function (configRule) {
        return configRule.targetingExpression &&
            !isDefaultTargetingExpression(configRule.targetingExpression);
    });
    var satisfiedClientConfigs = clientTargetingExpressionConfigRules.filter(function (configRule) {
        return isTargetingExpressionSatisfied(configRule.targetingExpression);
    });
    var sortedConfigs = __spreadArray(__spreadArray([], satisfiedClientConfigs), noExpressionConfigRules).sort(function (a, b) {
        return b.priority - a.priority;
    });
    return sortedConfigs.length ? sortedConfigs[0] : undefined;
}
function isTargetingExpressionSatisfied(targetingExpression) {
    if (targetingExpression.children.length > 0) {
        if (targetingExpression.operator === ClientTargetingExpression_Operator.AND) {
            return targetingExpression.children.every(function (childExpression) {
                return isTargetingExpressionSatisfied(childExpression);
            });
        }
        else if (targetingExpression.operator === ClientTargetingExpression_Operator.OR) {
            return targetingExpression.children.some(function (childExpression) {
                return isTargetingExpressionSatisfied(childExpression);
            });
        }
        else {
            return false;
        }
    }
    return evaluateLeafNode(targetingExpression);
}
function evaluateLeafNode(leaf) {
    var _a, _b, _c, _d, _e, _f;
    // If it's not actually a leaf node for whatever reason, consider the evaluation result to be false.
    if (!leaf.property ||
        !leaf.value ||
        !leaf.predicateOperator ||
        leaf.children.length > 0) {
        return false;
    }
    var propertyValueProvider = targetingPropertyValueProviders.get(leaf.property);
    if (!propertyValueProvider) {
        return false;
    }
    var propertyValue = propertyValueProvider();
    var comparisonValue = (_f = (_e = (_d = (_c = (_b = (_a = leaf.value.boolValue) !== null && _a !== void 0 ? _a : leaf.value.doubleValue) !== null && _b !== void 0 ? _b : leaf.value.floatValue) !== null && _c !== void 0 ? _c : leaf.value.intPairValue) !== null && _d !== void 0 ? _d : leaf.value.intValue) !== null && _e !== void 0 ? _e : leaf.value.longValue) !== null && _f !== void 0 ? _f : leaf.value.stringValue;
    // >= or <= operators can only operate on numbers (or proto enums)
    if (leaf.predicateOperator ===
        ClientTargetingExpression_PredicateOperator.GREATER_THAN_OR_EQUAL_TO ||
        leaf.predicateOperator ===
            ClientTargetingExpression_PredicateOperator.LESS_THAN_OR_EQUAL_TO) {
        if (typeof comparisonValue !== "number") {
            return false;
        }
        if (typeof propertyValue !== "number") {
            return false;
        }
        if (leaf.predicateOperator ===
            ClientTargetingExpression_PredicateOperator.LESS_THAN_OR_EQUAL_TO) {
            return propertyValue <= comparisonValue;
        }
        else {
            return propertyValue >= comparisonValue;
        }
    }
    if (leaf.predicateOperator ===
        ClientTargetingExpression_PredicateOperator.NOT_EQUAL) {
        return propertyValue !== comparisonValue;
    }
    if (leaf.predicateOperator === ClientTargetingExpression_PredicateOperator.EQUAL) {
        return propertyValue === comparisonValue;
    }
    return false;
}

var browserHeaders = {};

(function (exports) {
(function(e, a) { for(var i in a) e[i] = a[i]; }(exports, /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// identity function for calling harmony imports with the correct context
/******/ 	__webpack_require__.i = function(value) { return value; };
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 1);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ (function(module, exports, __webpack_require__) {

Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = __webpack_require__(3);
function isBrowserHeaders(arg) {
    return typeof arg === "object" && typeof arg.headersMap === "object" && typeof arg.forEach === "function";
}
var BrowserHeaders = (function () {
    function BrowserHeaders(init, options) {
        if (init === void 0) { init = {}; }
        if (options === void 0) { options = { splitValues: false }; }
        var _this = this;
        this.headersMap = {};
        if (init) {
            if (typeof Headers !== "undefined" && init instanceof Headers) {
                var keys = util_1.getHeaderKeys(init);
                keys.forEach(function (key) {
                    var values = util_1.getHeaderValues(init, key);
                    values.forEach(function (value) {
                        if (options.splitValues) {
                            _this.append(key, util_1.splitHeaderValue(value));
                        }
                        else {
                            _this.append(key, value);
                        }
                    });
                });
            }
            else if (isBrowserHeaders(init)) {
                init.forEach(function (key, values) {
                    _this.append(key, values);
                });
            }
            else if (typeof Map !== "undefined" && init instanceof Map) {
                var asMap = init;
                asMap.forEach(function (value, key) {
                    _this.append(key, value);
                });
            }
            else if (typeof init === "string") {
                this.appendFromString(init);
            }
            else if (typeof init === "object") {
                Object.getOwnPropertyNames(init).forEach(function (key) {
                    var asObject = init;
                    var values = asObject[key];
                    if (Array.isArray(values)) {
                        values.forEach(function (value) {
                            _this.append(key, value);
                        });
                    }
                    else {
                        _this.append(key, values);
                    }
                });
            }
        }
    }
    BrowserHeaders.prototype.appendFromString = function (str) {
        var pairs = str.split("\r\n");
        for (var i = 0; i < pairs.length; i++) {
            var p = pairs[i];
            var index = p.indexOf(":");
            if (index > 0) {
                var key = p.substring(0, index).trim();
                var value = p.substring(index + 1).trim();
                this.append(key, value);
            }
        }
    };
    BrowserHeaders.prototype.delete = function (key, value) {
        var normalizedKey = util_1.normalizeName(key);
        if (value === undefined) {
            delete this.headersMap[normalizedKey];
        }
        else {
            var existing = this.headersMap[normalizedKey];
            if (existing) {
                var index = existing.indexOf(value);
                if (index >= 0) {
                    existing.splice(index, 1);
                }
                if (existing.length === 0) {
                    delete this.headersMap[normalizedKey];
                }
            }
        }
    };
    BrowserHeaders.prototype.append = function (key, value) {
        var _this = this;
        var normalizedKey = util_1.normalizeName(key);
        if (!Array.isArray(this.headersMap[normalizedKey])) {
            this.headersMap[normalizedKey] = [];
        }
        if (Array.isArray(value)) {
            value.forEach(function (arrayValue) {
                _this.headersMap[normalizedKey].push(util_1.normalizeValue(arrayValue));
            });
        }
        else {
            this.headersMap[normalizedKey].push(util_1.normalizeValue(value));
        }
    };
    BrowserHeaders.prototype.set = function (key, value) {
        var normalizedKey = util_1.normalizeName(key);
        if (Array.isArray(value)) {
            var normalized_1 = [];
            value.forEach(function (arrayValue) {
                normalized_1.push(util_1.normalizeValue(arrayValue));
            });
            this.headersMap[normalizedKey] = normalized_1;
        }
        else {
            this.headersMap[normalizedKey] = [util_1.normalizeValue(value)];
        }
    };
    BrowserHeaders.prototype.has = function (key, value) {
        var keyArray = this.headersMap[util_1.normalizeName(key)];
        var keyExists = Array.isArray(keyArray);
        if (!keyExists) {
            return false;
        }
        if (value !== undefined) {
            var normalizedValue = util_1.normalizeValue(value);
            return keyArray.indexOf(normalizedValue) >= 0;
        }
        else {
            return true;
        }
    };
    BrowserHeaders.prototype.get = function (key) {
        var values = this.headersMap[util_1.normalizeName(key)];
        if (values !== undefined) {
            return values.concat();
        }
        return [];
    };
    BrowserHeaders.prototype.forEach = function (callback) {
        var _this = this;
        Object.getOwnPropertyNames(this.headersMap)
            .forEach(function (key) {
            callback(key, _this.headersMap[key]);
        }, this);
    };
    BrowserHeaders.prototype.toHeaders = function () {
        if (typeof Headers !== "undefined") {
            var headers_1 = new Headers();
            this.forEach(function (key, values) {
                values.forEach(function (value) {
                    headers_1.append(key, value);
                });
            });
            return headers_1;
        }
        else {
            throw new Error("Headers class is not defined");
        }
    };
    return BrowserHeaders;
}());
exports.BrowserHeaders = BrowserHeaders;


/***/ }),
/* 1 */
/***/ (function(module, exports, __webpack_require__) {

Object.defineProperty(exports, "__esModule", { value: true });
var BrowserHeaders_1 = __webpack_require__(0);
exports.BrowserHeaders = BrowserHeaders_1.BrowserHeaders;


/***/ }),
/* 2 */
/***/ (function(module, exports, __webpack_require__) {

Object.defineProperty(exports, "__esModule", { value: true });
function iterateHeaders(headers, callback) {
    var iterator = headers[Symbol.iterator]();
    var entry = iterator.next();
    while (!entry.done) {
        callback(entry.value[0]);
        entry = iterator.next();
    }
}
exports.iterateHeaders = iterateHeaders;
function iterateHeadersKeys(headers, callback) {
    var iterator = headers.keys();
    var entry = iterator.next();
    while (!entry.done) {
        callback(entry.value);
        entry = iterator.next();
    }
}
exports.iterateHeadersKeys = iterateHeadersKeys;


/***/ }),
/* 3 */
/***/ (function(module, exports, __webpack_require__) {

Object.defineProperty(exports, "__esModule", { value: true });
var iterateHeaders_1 = __webpack_require__(2);
function normalizeName(name) {
    if (typeof name !== "string") {
        name = String(name);
    }
    if (/[^a-z0-9\-#$%&'*+.\^_`|~]/i.test(name)) {
        throw new TypeError("Invalid character in header field name");
    }
    return name.toLowerCase();
}
exports.normalizeName = normalizeName;
function normalizeValue(value) {
    if (typeof value !== "string") {
        value = String(value);
    }
    return value;
}
exports.normalizeValue = normalizeValue;
function getHeaderValues(headersAsNative, key) {
    var headers = toWindowHeaders(headersAsNative);
    if (headers instanceof Headers && headers.getAll) {
        return headers.getAll(key);
    }
    var getValue = headers.get(key);
    if (getValue && typeof getValue === "string") {
        return [getValue];
    }
    return getValue;
}
exports.getHeaderValues = getHeaderValues;
function toWindowHeaders(headersAsNative) {
    return headersAsNative;
}
function getHeaderKeys(headersAsNative) {
    var headers = toWindowHeaders(headersAsNative);
    var asMap = {};
    var keys = [];
    if (headers.keys) {
        iterateHeaders_1.iterateHeadersKeys(headers, function (key) {
            if (!asMap[key]) {
                asMap[key] = true;
                keys.push(key);
            }
        });
    }
    else if (headers.forEach) {
        headers.forEach(function (_, key) {
            if (!asMap[key]) {
                asMap[key] = true;
                keys.push(key);
            }
        });
    }
    else {
        iterateHeaders_1.iterateHeaders(headers, function (entry) {
            var key = entry[0];
            if (!asMap[key]) {
                asMap[key] = true;
                keys.push(key);
            }
        });
    }
    return keys;
}
exports.getHeaderKeys = getHeaderKeys;
function splitHeaderValue(str) {
    var values = [];
    var commaSpaceValues = str.split(", ");
    commaSpaceValues.forEach(function (commaSpaceValue) {
        commaSpaceValue.split(",").forEach(function (commaValue) {
            values.push(commaValue);
        });
    });
    return values;
}
exports.splitHeaderValue = splitHeaderValue;


/***/ })
/******/ ])));
}(browserHeaders));

// NOTE: GrpcWebImpl below is generated by ts-proto, extracted and put here for bundle size optimization purposes.
var GrpcWebImpl = /** @class */ (function () {
    function GrpcWebImpl(host, options) {
        this.host = host;
        this.options = options;
    }
    GrpcWebImpl.prototype.unary = function (methodDesc, _request, metadata) {
        var _this = this;
        var _a;
        var request = __assign(__assign({}, _request), methodDesc.requestType);
        var maybeCombinedMetadata = metadata && this.options.metadata
            ? new browserHeaders.BrowserHeaders(__assign(__assign({}, (_a = this.options) === null || _a === void 0 ? void 0 : _a.metadata), metadata))
            : metadata || this.options.metadata;
        return new Promise(function (resolve, reject) {
            grpc.unary(methodDesc, {
                request: request,
                host: _this.host,
                metadata: maybeCombinedMetadata,
                transport: _this.options.transport,
                debug: _this.options.debug,
                onEnd: function (response) {
                    if (response.status === grpc.Code.OK) {
                        resolve(response.message);
                    }
                    else {
                        var err = new Error(response.statusMessage);
                        err.code = response.status;
                        err.metadata = response.trailers;
                        reject(err);
                    }
                },
            });
        });
    };
    return GrpcWebImpl;
}());

// key to store library context on window object
var CONTEXT_KEY = Symbol("context key");
// default settings
var DEFAULT_SETTINGS = {
    host: "https://aws.api.snapchat.com",
};
// store settings on a global object to avoid issues,
// when for some reason this modules is loaded more than once
if (typeof window !== "undefined") {
    Object.defineProperty(window, CONTEXT_KEY, {
        enumerable: false,
        writable: true,
    });
}
/**
 * Stores settings in globally.
 * @internal
 */
function setContext(settings) {
    if (typeof window !== "undefined") {
        window[CONTEXT_KEY] = settings;
    }
}
/**
 * Reads previously stored settings.
 */
function getContext() {
    if (typeof window !== "undefined") {
        return window[CONTEXT_KEY];
    }
}
/**
 * Returns settings object previously initialized with initConfigsClient().
 * @internal
 */
function getContextValidated() {
    var context = getContext();
    if (!context) {
        throw new Error("initConfigsClient() has to be called on app startup");
    }
    return context;
}
/**
 * Initializes COF configs client.
 * The function has to be called once on app startup.
 */
function initConfigsClient(settings) {
    if (settings === void 0) { settings = DEFAULT_SETTINGS; }
    var existingContext = getContext();
    if (existingContext) {
        // there is a chance that bundlers include initConfigsClient() into different chunks,
        // we are good if the same settigs object is passed,
        // otherwise throw an error;
        // JSON.stringify approach is simple and good to catch exactly that issue:
        // the same settings objects are created in the same way in different chunks
        if (JSON.stringify(settings) !== JSON.stringify(existingContext.settings)) {
            throw new Error("initConfigsClient() has already been called");
        }
    }
    else {
        setContext({
            settings: settings,
            rpc: new GrpcWebImpl(settings.host, { metadata: settings.metadata }),
        });
        // Add default providers for browser height and width. Can be overrided by re-registering these properties.
        registerPropertyValueProvider(ClientTargetingExpression_Property.WEB_SCREEN_HEIGHT, function () { return window.innerHeight; });
        registerPropertyValueProvider(ClientTargetingExpression_Property.WEB_SCREEN_WIDTH, function () { return window.innerWidth; });
    }
}

var CircumstancesServiceTargetingQueryDesc = {
    methodName: "targetingQuery",
    service: {
        serviceName: "snapchat.cdp.cof.CircumstancesService",
    },
    requestStream: false,
    responseStream: false,
    requestType: {
        serializeBinary: function () {
            return ConfigTargetingRequest.encode(this).finish();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    },
    responseType: {
        deserializeBinary: function (data) {
            return __assign(__assign({}, ConfigTargetingResponse.decode(data)), { toObject: function () {
                    return this;
                } });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    },
};
/**
 * Fetches COF configs.
 * Wrapper around https://github.sc-corp.net/Snapchat/pb_schema/blob/507ec1a8029de217836e29778ddd95ab8b5b8de4/proto/cdp/cof/circumstance_service.proto#L18
 **/
function targetingQuery(request, metadata) {
    var rpc = getContextValidated().rpc;
    return rpc.unary(CircumstancesServiceTargetingQueryDesc, ConfigTargetingRequest.fromPartial(request), metadata);
}

var baseGuid = { value: 0 };
var Guid = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.value !== 0) {
            writer.uint32(8).uint64(message.value);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseGuid);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.value = longToNumber(reader.uint64());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseGuid);
        if (object.value !== undefined && object.value !== null) {
            message.value = object.value;
        }
        else {
            message.value = 0;
        }
        return message;
    },
};
var globalThis$3 = (function () {
    if (typeof globalThis$3 !== "undefined")
        return globalThis$3;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
function longToNumber(long) {
    if (long.gt(Number.MAX_SAFE_INTEGER)) {
        throw new globalThis$3.Error("Value is larger than Number.MAX_SAFE_INTEGER");
    }
    return long.toNumber();
}
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** Steps for AB process. */
var AbStep;
(function (AbStep) {
    AbStep[AbStep["USER_WHITELISTED"] = 0] = "USER_WHITELISTED";
    AbStep[AbStep["USER_GROUP_WHITELISTED"] = 1] = "USER_GROUP_WHITELISTED";
    AbStep[AbStep["FILTER_CONDITIONS"] = 2] = "FILTER_CONDITIONS";
    AbStep[AbStep["SLICE_RANGE"] = 3] = "SLICE_RANGE";
    AbStep[AbStep["TRAFFIC_ALLOCATED"] = 4] = "TRAFFIC_ALLOCATED";
    AbStep[AbStep["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(AbStep || (AbStep = {}));
/** Steps for COF process. */
var CofStep;
(function (CofStep) {
    CofStep[CofStep["VALIDATE_REQUEST"] = 0] = "VALIDATE_REQUEST";
    CofStep[CofStep["REQUEST_ATLAS"] = 1] = "REQUEST_ATLAS";
    CofStep[CofStep["BUILD_TARGETING_INFO"] = 2] = "BUILD_TARGETING_INFO";
    CofStep[CofStep["QUERY_INDEX"] = 3] = "QUERY_INDEX";
    CofStep[CofStep["REQUEST_AB"] = 4] = "REQUEST_AB";
    CofStep[CofStep["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(CofStep || (CofStep = {}));
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var FieldDescriptorProto_Type;
(function (FieldDescriptorProto_Type) {
    /**
     * TYPE_DOUBLE - 0 is reserved for errors.
     * Order is weird for historical reasons.
     */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_DOUBLE"] = 1] = "TYPE_DOUBLE";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_FLOAT"] = 2] = "TYPE_FLOAT";
    /**
     * TYPE_INT64 - Not ZigZag encoded.  Negative numbers take 10 bytes.  Use TYPE_SINT64 if
     * negative values are likely.
     */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_INT64"] = 3] = "TYPE_INT64";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_UINT64"] = 4] = "TYPE_UINT64";
    /**
     * TYPE_INT32 - Not ZigZag encoded.  Negative numbers take 10 bytes.  Use TYPE_SINT32 if
     * negative values are likely.
     */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_INT32"] = 5] = "TYPE_INT32";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_FIXED64"] = 6] = "TYPE_FIXED64";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_FIXED32"] = 7] = "TYPE_FIXED32";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_BOOL"] = 8] = "TYPE_BOOL";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_STRING"] = 9] = "TYPE_STRING";
    /**
     * TYPE_GROUP - Tag-delimited aggregate.
     * Group type is deprecated and not supported in proto3. However, Proto3
     * implementations should still be able to parse the group wire format and
     * treat group fields as unknown fields.
     */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_GROUP"] = 10] = "TYPE_GROUP";
    /** TYPE_MESSAGE - Length-delimited aggregate. */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_MESSAGE"] = 11] = "TYPE_MESSAGE";
    /** TYPE_BYTES - New in version 2. */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_BYTES"] = 12] = "TYPE_BYTES";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_UINT32"] = 13] = "TYPE_UINT32";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_ENUM"] = 14] = "TYPE_ENUM";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_SFIXED32"] = 15] = "TYPE_SFIXED32";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_SFIXED64"] = 16] = "TYPE_SFIXED64";
    /** TYPE_SINT32 - Uses ZigZag encoding. */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_SINT32"] = 17] = "TYPE_SINT32";
    /** TYPE_SINT64 - Uses ZigZag encoding. */
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["TYPE_SINT64"] = 18] = "TYPE_SINT64";
    FieldDescriptorProto_Type[FieldDescriptorProto_Type["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(FieldDescriptorProto_Type || (FieldDescriptorProto_Type = {}));
var FieldDescriptorProto_Label;
(function (FieldDescriptorProto_Label) {
    /** LABEL_OPTIONAL - 0 is reserved for errors */
    FieldDescriptorProto_Label[FieldDescriptorProto_Label["LABEL_OPTIONAL"] = 1] = "LABEL_OPTIONAL";
    FieldDescriptorProto_Label[FieldDescriptorProto_Label["LABEL_REQUIRED"] = 2] = "LABEL_REQUIRED";
    FieldDescriptorProto_Label[FieldDescriptorProto_Label["LABEL_REPEATED"] = 3] = "LABEL_REPEATED";
    FieldDescriptorProto_Label[FieldDescriptorProto_Label["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(FieldDescriptorProto_Label || (FieldDescriptorProto_Label = {}));
/** Generated classes can be optimized for speed or code size. */
var FileOptions_OptimizeMode;
(function (FileOptions_OptimizeMode) {
    /** SPEED - Generate complete code for parsing, serialization, */
    FileOptions_OptimizeMode[FileOptions_OptimizeMode["SPEED"] = 1] = "SPEED";
    /** CODE_SIZE - etc. */
    FileOptions_OptimizeMode[FileOptions_OptimizeMode["CODE_SIZE"] = 2] = "CODE_SIZE";
    /** LITE_RUNTIME - Generate code using MessageLite and the lite runtime. */
    FileOptions_OptimizeMode[FileOptions_OptimizeMode["LITE_RUNTIME"] = 3] = "LITE_RUNTIME";
    FileOptions_OptimizeMode[FileOptions_OptimizeMode["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(FileOptions_OptimizeMode || (FileOptions_OptimizeMode = {}));
var FieldOptions_CType;
(function (FieldOptions_CType) {
    /** STRING - Default mode. */
    FieldOptions_CType[FieldOptions_CType["STRING"] = 0] = "STRING";
    FieldOptions_CType[FieldOptions_CType["CORD"] = 1] = "CORD";
    FieldOptions_CType[FieldOptions_CType["STRING_PIECE"] = 2] = "STRING_PIECE";
    FieldOptions_CType[FieldOptions_CType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(FieldOptions_CType || (FieldOptions_CType = {}));
var FieldOptions_JSType;
(function (FieldOptions_JSType) {
    /** JS_NORMAL - Use the default type. */
    FieldOptions_JSType[FieldOptions_JSType["JS_NORMAL"] = 0] = "JS_NORMAL";
    /** JS_STRING - Use JavaScript strings. */
    FieldOptions_JSType[FieldOptions_JSType["JS_STRING"] = 1] = "JS_STRING";
    /** JS_NUMBER - Use JavaScript numbers. */
    FieldOptions_JSType[FieldOptions_JSType["JS_NUMBER"] = 2] = "JS_NUMBER";
    FieldOptions_JSType[FieldOptions_JSType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(FieldOptions_JSType || (FieldOptions_JSType = {}));
/**
 * Is this method side-effect-free (or safe in HTTP parlance), or idempotent,
 * or neither? HTTP based RPC implementation may choose GET verb for safe
 * methods, and PUT verb for idempotent methods instead of the default POST.
 */
var MethodOptions_IdempotencyLevel;
(function (MethodOptions_IdempotencyLevel) {
    MethodOptions_IdempotencyLevel[MethodOptions_IdempotencyLevel["IDEMPOTENCY_UNKNOWN"] = 0] = "IDEMPOTENCY_UNKNOWN";
    /** NO_SIDE_EFFECTS - implies idempotent */
    MethodOptions_IdempotencyLevel[MethodOptions_IdempotencyLevel["NO_SIDE_EFFECTS"] = 1] = "NO_SIDE_EFFECTS";
    /** IDEMPOTENT - idempotent, but may have side effects */
    MethodOptions_IdempotencyLevel[MethodOptions_IdempotencyLevel["IDEMPOTENT"] = 2] = "IDEMPOTENT";
    MethodOptions_IdempotencyLevel[MethodOptions_IdempotencyLevel["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(MethodOptions_IdempotencyLevel || (MethodOptions_IdempotencyLevel = {}));
var globalThis$2 = (function () {
    if (typeof globalThis$2 !== "undefined")
        return globalThis$2;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** ProtoBuf API definition for AB config service. */
/**
 * Mirror of Atlas schema here:
 * https://github.sc-corp.net/Snapchat/atlas/blob/master/user-service/user-service-interface/src/main/proto/com/snapchat/atlas/user/user-data.proto#L287
 */
var SnapProStatus;
(function (SnapProStatus) {
    SnapProStatus[SnapProStatus["UNKNOWN_SNAP_PRO"] = 0] = "UNKNOWN_SNAP_PRO";
    SnapProStatus[SnapProStatus["NOT_SNAP_PRO"] = 1] = "NOT_SNAP_PRO";
    SnapProStatus[SnapProStatus["STANDARD_TIER"] = 2] = "STANDARD_TIER";
    SnapProStatus[SnapProStatus["OFFICIAL_TIER"] = 3] = "OFFICIAL_TIER";
    SnapProStatus[SnapProStatus["PUBLIC_TIER"] = 4] = "PUBLIC_TIER";
    SnapProStatus[SnapProStatus["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(SnapProStatus || (SnapProStatus = {}));
var GetConfigsValueByPropertiesRequest_SourceOfOrigin;
(function (GetConfigsValueByPropertiesRequest_SourceOfOrigin) {
    GetConfigsValueByPropertiesRequest_SourceOfOrigin[GetConfigsValueByPropertiesRequest_SourceOfOrigin["UNKNOWN_ORIGIN"] = 0] = "UNKNOWN_ORIGIN";
    GetConfigsValueByPropertiesRequest_SourceOfOrigin[GetConfigsValueByPropertiesRequest_SourceOfOrigin["SERVER"] = 1] = "SERVER";
    GetConfigsValueByPropertiesRequest_SourceOfOrigin[GetConfigsValueByPropertiesRequest_SourceOfOrigin["CLIENT"] = 2] = "CLIENT";
    GetConfigsValueByPropertiesRequest_SourceOfOrigin[GetConfigsValueByPropertiesRequest_SourceOfOrigin["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(GetConfigsValueByPropertiesRequest_SourceOfOrigin || (GetConfigsValueByPropertiesRequest_SourceOfOrigin = {}));
var StudyMetadata_ServePlatform;
(function (StudyMetadata_ServePlatform) {
    StudyMetadata_ServePlatform[StudyMetadata_ServePlatform["UNKNOWN_PLATFORM"] = 0] = "UNKNOWN_PLATFORM";
    StudyMetadata_ServePlatform[StudyMetadata_ServePlatform["SERVER"] = 1] = "SERVER";
    StudyMetadata_ServePlatform[StudyMetadata_ServePlatform["CLIENT"] = 2] = "CLIENT";
    StudyMetadata_ServePlatform[StudyMetadata_ServePlatform["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_ServePlatform || (StudyMetadata_ServePlatform = {}));
/** Status of the study */
var StudyMetadata_Status;
(function (StudyMetadata_Status) {
    /** UNKNOWN_STATUS - Placeholder, should not happen. */
    StudyMetadata_Status[StudyMetadata_Status["UNKNOWN_STATUS"] = 0] = "UNKNOWN_STATUS";
    /** DEPLOYED - Deployed study. */
    StudyMetadata_Status[StudyMetadata_Status["DEPLOYED"] = 1] = "DEPLOYED";
    /** COMPLETED - Completed study. */
    StudyMetadata_Status[StudyMetadata_Status["COMPLETED"] = 2] = "COMPLETED";
    StudyMetadata_Status[StudyMetadata_Status["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_Status || (StudyMetadata_Status = {}));
/** Type of the segment. */
var StudyMetadata_SegmentType;
(function (StudyMetadata_SegmentType) {
    /** UNKNOWN_SEGMENT - Unknoww/Error */
    StudyMetadata_SegmentType[StudyMetadata_SegmentType["UNKNOWN_SEGMENT"] = 0] = "UNKNOWN_SEGMENT";
    /** STANDARD_SEGMENT - Segment using standard A/B randomization and not used in rollout phase. */
    StudyMetadata_SegmentType[StudyMetadata_SegmentType["STANDARD_SEGMENT"] = 1] = "STANDARD_SEGMENT";
    /** BUDGET_SEGMENT - Segment using Budget AB allocation. */
    StudyMetadata_SegmentType[StudyMetadata_SegmentType["BUDGET_SEGMENT"] = 2] = "BUDGET_SEGMENT";
    /** ROLLOUT_SEGMENT - Segment in rollout phase. */
    StudyMetadata_SegmentType[StudyMetadata_SegmentType["ROLLOUT_SEGMENT"] = 3] = "ROLLOUT_SEGMENT";
    StudyMetadata_SegmentType[StudyMetadata_SegmentType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_SegmentType || (StudyMetadata_SegmentType = {}));
var StudyMetadata_TargetingExpression_Operator;
(function (StudyMetadata_TargetingExpression_Operator) {
    StudyMetadata_TargetingExpression_Operator[StudyMetadata_TargetingExpression_Operator["UNKNOWN_OPERATOR"] = 0] = "UNKNOWN_OPERATOR";
    StudyMetadata_TargetingExpression_Operator[StudyMetadata_TargetingExpression_Operator["AND"] = 1] = "AND";
    StudyMetadata_TargetingExpression_Operator[StudyMetadata_TargetingExpression_Operator["OR"] = 2] = "OR";
    StudyMetadata_TargetingExpression_Operator[StudyMetadata_TargetingExpression_Operator["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_TargetingExpression_Operator || (StudyMetadata_TargetingExpression_Operator = {}));
var StudyMetadata_TargetingExpression_PredicateOperator;
(function (StudyMetadata_TargetingExpression_PredicateOperator) {
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["UNKNOWN_PREDICATE_OPERATOR"] = 0] = "UNKNOWN_PREDICATE_OPERATOR";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["EQUAL"] = 1] = "EQUAL";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["NOT_EQUAL"] = 2] = "NOT_EQUAL";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["GREATER_THAN_OR_EQUAL_TO"] = 3] = "GREATER_THAN_OR_EQUAL_TO";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["LESS_THAN_OR_EQUAL_TO"] = 4] = "LESS_THAN_OR_EQUAL_TO";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["IN"] = 5] = "IN";
    StudyMetadata_TargetingExpression_PredicateOperator[StudyMetadata_TargetingExpression_PredicateOperator["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_TargetingExpression_PredicateOperator || (StudyMetadata_TargetingExpression_PredicateOperator = {}));
var StudyMetadata_TargetingExpression_Property;
(function (StudyMetadata_TargetingExpression_Property) {
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["UNKNOWN_PROPERTY"] = 0] = "UNKNOWN_PROPERTY";
    /**
     * DEVICE_MODEL - The model of the device that runs snapchat app.
     * e.g. SM-G960U
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_MODEL"] = 1] = "DEVICE_MODEL";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["COUNTRY"] = 2] = "COUNTRY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["OS"] = 3] = "OS";
    /**
     * OS_VERSION - Version of the operating system on which the Snapchat app is running.
     * e.g. 4.4.2
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["OS_VERSION"] = 4] = "OS_VERSION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_VERSION"] = 5] = "APP_VERSION";
    /**
     * BUILD_FLAVOR - Build flavor of the snapchat app user is running.
     * e.g. DEBUG, ALPHA, MASTER.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BUILD_FLAVOR"] = 6] = "BUILD_FLAVOR";
    /**
     * USER_ID - The Snapchat userId of the user. This is mainly used for whitelisting users to a specific value.
     * e.g. PII, no example
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_ID"] = 7] = "USER_ID";
    /**
     * LOCALE - The Locale of the users device. This may not be the locale that the app is displaying to the user.
     * See APP_LOCALE
     * e.g. en_us, en_gb, zh_cn
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LOCALE"] = 8] = "LOCALE";
    /**
     * IS_CHARGING - Signal indicating if the device is charging or not, needs to be evaluated on CLIENT side.
     * e.g. TRUE, FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_CHARGING"] = 9] = "IS_CHARGING";
    /**
     * BATTERY_LEVEL - Battery level of user's phone.
     * e.g. HIGH, LOW
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BATTERY_LEVEL"] = 10] = "BATTERY_LEVEL";
    /**
     * DEVICE_CLUSTER - Cluster of the device that runs snapchat app.
     * e.g. 0, 1, 2, 3, 4, 5.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_CLUSTER"] = 11] = "DEVICE_CLUSTER";
    /**
     * IS_OFFLINE - Signal indicating if the device is offline or not, needs to be evaluated on CLIENT side.
     * e.g. TRUE, FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_OFFLINE"] = 12] = "IS_OFFLINE";
    /**
     * BANDWIDTH - Available device internal storage in bps.
     * e.g. 800000, 1600000, 4000000, 16000000
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BANDWIDTH"] = 13] = "BANDWIDTH";
    /**
     * DEVICE_BRAND - The brand of the device mapped from the DEVICE_MODEL of the device.
     * e.g. Samsung, Huawei, Sony
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_BRAND"] = 14] = "DEVICE_BRAND";
    /**
     * IS_EMPLOYEE - Returns true when the user of the request is marked as an employee in Atlas.
     * e.g. TRUE, FALSE.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_EMPLOYEE"] = 15] = "IS_EMPLOYEE";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USERNAME"] = 16] = "USERNAME";
    /**
     * IS_TEST_USER - Returns true when the user of the request is marked as a test user in Atlas.
     * e.g. TRUE/FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_TEST_USER"] = 17] = "IS_TEST_USER";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_PROFILE"] = 18] = "USER_PROFILE";
    /**
     * SCREEN_WIDTH - The width of screen resolution in pixels
     * e.g. 1024
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SCREEN_WIDTH"] = 19] = "SCREEN_WIDTH";
    /**
     * SCREEN_HEIGHT - The height of screen resolution in pixels
     * e.g. 1920
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SCREEN_HEIGHT"] = 20] = "SCREEN_HEIGHT";
    /**
     * HEVC_SUPPORT - Whether the device support HEVC
     * e.g. true, false.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HEVC_SUPPORT"] = 21] = "HEVC_SUPPORT";
    /**
     * DISK_SIZE - Total size of the device internal storage in MB
     * e.g. 1000
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DISK_SIZE"] = 22] = "DISK_SIZE";
    /**
     * DISK_AVAILABLE - Available device internal storage in MB.
     * e.g. 1000
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DISK_AVAILABLE"] = 23] = "DISK_AVAILABLE";
    /**
     * NETWORK_TYPE - Type of network the user is currently using when the request is handled by our service.
     * This property is evaluated on the server and will not change between sync calls.
     * e.g. CELLULAR, WIFI
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NETWORK_TYPE"] = 24] = "NETWORK_TYPE";
    /**
     * MAX_VIDEO_WIDTH - The maximum playable video width (assumes portrait orientation) in pixels
     * e.g. 1024
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MAX_VIDEO_WIDTH"] = 25] = "MAX_VIDEO_WIDTH";
    /**
     * MAX_VIDEO_HEIGHT - The maximum playable video height (assumes portrait orientation) in pixels
     * e.g. 1920
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MAX_VIDEO_HEIGHT"] = 26] = "MAX_VIDEO_HEIGHT";
    /**
     * MEDIA_TYPE_DEPRECATED - Type of media currently being processed.
     * e.g. IMAGE, VIDEO, GIF, AUDIO
     *
     * @deprecated
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MEDIA_TYPE_DEPRECATED"] = 27] = "MEDIA_TYPE_DEPRECATED";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_PUBLIC_STORY"] = 28] = "IS_PUBLIC_STORY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_OFFICIAL_STORY"] = 29] = "IS_OFFICIAL_STORY";
    /**
     * IS_NETWORK_METERED - Returns true when the network is metered at the time of the request.
     * This will not change between sync calls.
     * e.g. TRUE/FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_NETWORK_METERED"] = 30] = "IS_NETWORK_METERED";
    /**
     * IS_ROAMING - Returns true when the network is metered at the time of the request.
     * This property is evaluated on the server and will not change in value between sync calls.
     * e.g. TRUE/FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_ROAMING"] = 31] = "IS_ROAMING";
    /**
     * APP_ENGAGEMENT_LEVEL - Overall engagement with the app. Based on app_open_count
     * See definitions of engagement level properties here:
     * https://docs.google.com/document/d/1goLlXMdDGXN0B3zER_dPhl9vFk_TIjeNCqQb4zIQyBM/edit?pli=1
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_ENGAGEMENT_LEVEL"] = 32] = "APP_ENGAGEMENT_LEVEL";
    /**
     * COMMUNICATION_ENGAGEMENT_LEVEL - Users engagement with the chat. Based on send direct snap or chat engagement level
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["COMMUNICATION_ENGAGEMENT_LEVEL"] = 33] = "COMMUNICATION_ENGAGEMENT_LEVEL";
    /**
     * FRIEND_STORY_ENGAGEMENT_LEVEL - Users engagement with their friends stories.
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FRIEND_STORY_ENGAGEMENT_LEVEL"] = 34] = "FRIEND_STORY_ENGAGEMENT_LEVEL";
    /**
     * PUBLIC_USER_STORY_ENGAGEMENT_LEVEL - Public user story view engagement level
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PUBLIC_USER_STORY_ENGAGEMENT_LEVEL"] = 35] = "PUBLIC_USER_STORY_ENGAGEMENT_LEVEL";
    /**
     * PUBLISHER_STORY_ENGAGEMENT_LEVEL - Publisher story view engagement level
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PUBLISHER_STORY_ENGAGEMENT_LEVEL"] = 36] = "PUBLISHER_STORY_ENGAGEMENT_LEVEL";
    /**
     * LENS_ENGAGEMENT_LEVEL - Lens engagement level based on number of lens swipes a user has
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_ENGAGEMENT_LEVEL"] = 37] = "LENS_ENGAGEMENT_LEVEL";
    /**
     * CAMERA_CONTEXT - The context the user has opened the camera from.
     * This is a realtme property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. MAIN, REPLY, PROFILE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA_CONTEXT"] = 38] = "CAMERA_CONTEXT";
    /**
     * CAMERA_DIRECTION - The direction of the camera.
     * This is a realtme property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. FRONT, BACK
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA_DIRECTION"] = 39] = "CAMERA_DIRECTION";
    /**
     * CAMERA_FLASH_STATE - The current state of the flash when a Snap was captured.
     * This is a realtme property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. OFF, ON
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA_FLASH_STATE"] = 40] = "CAMERA_FLASH_STATE";
    /**
     * CAMERA_API - The current camera API stack that is being used on the device.
     * This is a realtme property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. CAMERA_1, CAMERA_2, ARCORE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA_API"] = 41] = "CAMERA_API";
    /**
     * NON_FRIEND_STORY_ENGAGEMENT_LEVEL - Non-friend story (public + publisher) view engagement level
     * e.g. IDLE, CASUAL, REGULAR, POWER
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NON_FRIEND_STORY_ENGAGEMENT_LEVEL"] = 42] = "NON_FRIEND_STORY_ENGAGEMENT_LEVEL";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FOLLOWER_SIZE_LEVEL"] = 43] = "FOLLOWER_SIZE_LEVEL";
    /**
     * LEGACY_MUSHROOM_CONTENT_TYPE - The legacy content type that is being used in content manager.
     * See LegacyMushroomContent type for values:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/master/proto/cdp/cof/content_manager_provided_signals.proto
     * e.g. AD_SNAP, SNAP, STORY_SNAP, LENS_ICON, .....
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LEGACY_MUSHROOM_CONTENT_TYPE"] = 44] = "LEGACY_MUSHROOM_CONTENT_TYPE";
    /**
     * SNAP_SOURCE - The source of the snap that was captured. Required feature_provided signals to be evaluated.
     * Definition is snapchat.cdp.media.SnapSource
     * e.g. CAMERA, CAMERA_ROLL, GALLERY, IN_CHAT
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAP_SOURCE"] = 45] = "SNAP_SOURCE";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["UPLOAD_BANDWIDTH"] = 46] = "UPLOAD_BANDWIDTH";
    /**
     * DAYS_SINCE_CREATION - The number of days since this users account was created.
     * This is useful for rules that you only want to apply to new or old users.
     * e.g. [0..MAX_INT]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_CREATION"] = 47] = "DAYS_SINCE_CREATION";
    /**
     * CAMERA2_LEVEL - The Camera2 API support level of the device.
     * This is a realtime property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. LEGACY, LIMITED, FULL, LEVEL_3
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA2_LEVEL"] = 48] = "CAMERA2_LEVEL";
    /**
     * CAMERA_NIGHT_MODE_STATE - The night mode state of the camera.
     * This is a realtme property that evaluated on each use and requires CameraProvided signals for evaluation.
     * e.g. ON, OFF
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA_NIGHT_MODE_STATE"] = 49] = "CAMERA_NIGHT_MODE_STATE";
    /** USER_PERSONA - Persona of the user */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_PERSONA"] = 50] = "USER_PERSONA";
    /** USER_LANGUAGE - Language used by user. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_LANGUAGE"] = 51] = "USER_LANGUAGE";
    /**
     * USER_CREATION_TIME - The unix epoch time at which the user was created.
     * This is useful if you want to lock a cohort of users created at the same time for cohort testing.
     * e.g. unix_epoch time
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_CREATION_TIME"] = 52] = "USER_CREATION_TIME";
    /**
     * VIDEO_DURATION - The duration of a video in ms. This requires a feature provided signal to be passed
     * e.g. 6870, 1001
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["VIDEO_DURATION"] = 53] = "VIDEO_DURATION";
    /** MOBILE_NETWORK_TYPE - Deprecated */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MOBILE_NETWORK_TYPE"] = 54] = "MOBILE_NETWORK_TYPE";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["AGGREGATED_USER_BANDWIDTH"] = 55] = "AGGREGATED_USER_BANDWIDTH";
    /**
     * REALTIME_MOBILE_NETWORK_GENERATION - The type of mobile network the user is on.
     * This is evaluated in realtime by CirumsstanceEngine.
     * e.g. 3G, 4G, 5G
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REALTIME_MOBILE_NETWORK_GENERATION"] = 56] = "REALTIME_MOBILE_NETWORK_GENERATION";
    /**
     * REALTIME_NETWORK_TYPE - The type network the user is on.
     * This is evaluated in realtime by CirumsstanceEngine.
     * e.g. WiFi, WWAN
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REALTIME_NETWORK_TYPE"] = 57] = "REALTIME_NETWORK_TYPE";
    /**
     * REALTIME_NETWORK_IS_METERED - If the network is currently a metered connection.
     * This is evaluated in realtime by CirumsstanceEngine.
     * e.g. TRUE, FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REALTIME_NETWORK_IS_METERED"] = 58] = "REALTIME_NETWORK_IS_METERED";
    /**
     * REALTIME_NETWORK_IS_ROAMING - Tells if the current network connection is roaming.
     * This is evaluated in realtime by CirumsstanceEngine.
     * e.g. TRUE, FALSE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REALTIME_NETWORK_IS_ROAMING"] = 59] = "REALTIME_NETWORK_IS_ROAMING";
    /**
     * LENS_CLUSTER_BIASED - The lens cluster of the device based on the biased algorithm.
     * e.g. [-1..6]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_CLUSTER_BIASED"] = 60] = "LENS_CLUSTER_BIASED";
    /**
     * LENS_CLUSTER_LOG - The lens cluster of the device based on a log distribution
     * e.g. [-1..6]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_CLUSTER_LOG"] = 61] = "LENS_CLUSTER_LOG";
    /**
     * LENS_CLUSTER_ORIG - The lens cluster of the device based on a normal distribution
     * e.g. [-1..6]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_CLUSTER_ORIG"] = 62] = "LENS_CLUSTER_ORIG";
    /**
     * LENS_CLUSTER_ORIG_4 - The lens cluster of the device based on a normal distribution
     * e.g. [-1..4]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_CLUSTER_ORIG_4"] = 63] = "LENS_CLUSTER_ORIG_4";
    /**
     * LENS_CLUSTER_BIASED_LOG - The lens cluster of the device based on a biased log distribution
     * e.g. [-1..6]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_CLUSTER_BIASED_LOG"] = 64] = "LENS_CLUSTER_BIASED_LOG";
    /**
     * APP_LOCALE - The Locale of that our app is currently using. This is a subset of the locales that the device may be in
     * e.g. en_us, en_gb, zh_cn
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_LOCALE"] = 65] = "APP_LOCALE";
    /**
     * DEVICE_COMMON_NAME - The common name of the device that is mapped from the DEVICE_MODEL
     * e.g. Galaxy S7, Galaxy S7 Edge, P20 Lite
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_COMMON_NAME"] = 66] = "DEVICE_COMMON_NAME";
    /**
     * PICTURE_MODE - The mode in which the camera will return images from the takepicture command.
     * This requires FeaatureProvidedSignals for evaluation
     * e.g. GPU, JPEG
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PICTURE_MODE"] = 67] = "PICTURE_MODE";
    /**
     * USER_FRIEND_COUNT - The number of friends a user has.
     * e.g. [0 .. 5000]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_FRIEND_COUNT"] = 68] = "USER_FRIEND_COUNT";
    /**
     * BOLT_USE_CASE - The current usecase of the request to BOLT, this requires feature provided signals
     * e.g. int values
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BOLT_USE_CASE"] = 69] = "BOLT_USE_CASE";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["L90_COUNTRY"] = 70] = "L90_COUNTRY";
    /** STUB - Deprecated */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STUB"] = 71] = "STUB";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_TRUE"] = 72] = "IS_TRUE";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STORY_POST_RATIO"] = 73] = "STORY_POST_RATIO";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GENDER"] = 74] = "GENDER";
    /** INFERRED_AGE_BUCKET - Inferred age bucket of the user */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["INFERRED_AGE_BUCKET"] = 75] = "INFERRED_AGE_BUCKET";
    /** STORIES - Number of posted stories in the last 7 days */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STORIES"] = 76] = "STORIES";
    /** SENDS - Total number of send snaps plus number of posted stories in the last 7 days */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SENDS"] = 77] = "SENDS";
    /** SNAP_CREATE_L7 - L7 level for snap creation engagements */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAP_CREATE_L7"] = 78] = "SNAP_CREATE_L7";
    /** STORY_POST_L7 - L7 level for story post engagements */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STORY_POST_L7"] = 79] = "STORY_POST_L7";
    /** COMMUNICATION_L7 - L7 level for communication engagements */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["COMMUNICATION_L7"] = 80] = "COMMUNICATION_L7";
    /** DF_L7 - L7 level for discover feed engagements */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DF_L7"] = 81] = "DF_L7";
    /** APP_L7 - L7 level for app engagements */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_L7"] = 82] = "APP_L7";
    /** DAYS_SINCE_CREATION_BUCKET - Bucket for days since creation. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_CREATION_BUCKET"] = 83] = "DAYS_SINCE_CREATION_BUCKET";
    /** BIDIRECTIONAL_FRIEND_STATUS - bucket for number of bidirectional friends. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BIDIRECTIONAL_FRIEND_STATUS"] = 84] = "BIDIRECTIONAL_FRIEND_STATUS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STORY_POST_PREDICTION"] = 85] = "STORY_POST_PREDICTION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BOLT_CDN_EXPERIMENTATION_ID"] = 86] = "BOLT_CDN_EXPERIMENTATION_ID";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MINS_SINCE_LAST_LENS_ACTIVATION"] = 87] = "MINS_SINCE_LAST_LENS_ACTIVATION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPENS"] = 88] = "APP_OPENS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_SWIPES"] = 89] = "LENS_SWIPES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_SNAPS"] = 90] = "LENS_SNAPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_OPS"] = 91] = "LENS_OPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W1_APP_OPENS"] = 92] = "W1_APP_OPENS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W1_LENS_SWIPES"] = 93] = "W1_LENS_SWIPES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W1_LENS_SNAPS"] = 94] = "W1_LENS_SNAPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W1_LENS_OPS"] = 95] = "W1_LENS_OPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W2_APP_OPENS"] = 96] = "W2_APP_OPENS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W2_LENS_SWIPES"] = 97] = "W2_LENS_SWIPES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W2_LENS_SNAPS"] = 98] = "W2_LENS_SNAPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W2_LENS_OPS"] = 99] = "W2_LENS_OPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W34_APP_OPENS"] = 100] = "W34_APP_OPENS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W34_LENS_SWIPES"] = 101] = "W34_LENS_SWIPES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W34_LENS_SNAPS"] = 102] = "W34_LENS_SNAPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["W34_LENS_OPS"] = 103] = "W34_LENS_OPS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENS_SWIPES_PREDICTION"] = 104] = "LENS_SWIPES_PREDICTION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REGISTRATION_COUNTRY"] = 105] = "REGISTRATION_COUNTRY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IP_ASN"] = 106] = "IP_ASN";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IP_REGION"] = 107] = "IP_REGION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IP_CITY"] = 108] = "IP_CITY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_USER_ID"] = 109] = "HAS_USER_ID";
    /**
     * HAS_BITMOJI - Whether a user has bitmoji
     * e.g. TRUE, FALSE.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_BITMOJI"] = 110] = "HAS_BITMOJI";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NUM_APP_OPENS_LAST_8_DAYS"] = 111] = "NUM_APP_OPENS_LAST_8_DAYS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MINS_SINCE_LAST_SNAPPABLE_LENS_ACTIVATION"] = 112] = "MINS_SINCE_LAST_SNAPPABLE_LENS_ACTIVATION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NUM_FEATURE_STORY_VIEW_DAYS_L7"] = 113] = "NUM_FEATURE_STORY_VIEW_DAYS_L7";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NUM_FEATURE_STORY_SYNC_DAYS_L7"] = 114] = "NUM_FEATURE_STORY_SYNC_DAYS_L7";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HEXAGON_NN_SUPPORTED_VERSION"] = 115] = "HEXAGON_NN_SUPPORTED_VERSION";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_LAST_LOGIN_OR_OPEN"] = 116] = "DAYS_SINCE_LAST_LOGIN_OR_OPEN";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SPECTACLES_VERSION"] = 117] = "SPECTACLES_VERSION";
    /** NETWORK_QUALITY - Quality of the network. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NETWORK_QUALITY"] = 118] = "NETWORK_QUALITY";
    /**
     * DEVICE_MEMORY_MB - Device memory in MB.
     * e.g. 4000, 8000
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_MEMORY_MB"] = 119] = "DEVICE_MEMORY_MB";
    /**
     * DEVICE_GL_VERSION - Device OpenGL version, value as (major * 100 + minor). Example: 3.2 is reported as 302
     * e.g. 302, 301
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_GL_VERSION"] = 120] = "DEVICE_GL_VERSION";
    /** SNAP_PRO_STATUS - Snap pro status of the user. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAP_PRO_STATUS"] = 121] = "SNAP_PRO_STATUS";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_VP9_DECODING_SUPPORT"] = 122] = "DEVICE_VP9_DECODING_SUPPORT";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["AVG_FRIEND_STORY_VIEW_COUNT_L7"] = 123] = "AVG_FRIEND_STORY_VIEW_COUNT_L7";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GAME_JOIN_TIME"] = 124] = "GAME_JOIN_TIME";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GAME_LAST_ACTIVE_TIME"] = 125] = "GAME_LAST_ACTIVE_TIME";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_FIRST_GAME_ACTIVITY"] = 126] = "DAYS_SINCE_FIRST_GAME_ACTIVITY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_LAST_GAME_ACTIVITY"] = 127] = "DAYS_SINCE_LAST_GAME_ACTIVITY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PF_PAGE_SESSIONS_WITH_LONG_IMP"] = 128] = "PF_PAGE_SESSIONS_WITH_LONG_IMP";
    /**
     * STICKY_MAX_CONNECTION_TYPE - Client side property that is based on the fastest connection type that the device has used in the last week. On the client, the device updates and persists the network type in use with a timestamp. During retrieval that data is used to determine the fastest connection type that was used within the last week.
     * e.g. 4G, 5G
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STICKY_MAX_CONNECTION_TYPE"] = 129] = "STICKY_MAX_CONNECTION_TYPE";
    /**
     * WIRELESS_CARRIER - Server side property that is evaluated based based on the wireless carrier that the device has during sync.
     * e.g. GoogleFi, Verizon ...
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["WIRELESS_CARRIER"] = 130] = "WIRELESS_CARRIER";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["WITH_ANIMATED_OVERLAY"] = 131] = "WITH_ANIMATED_OVERLAY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MINIS_JOIN_TIME"] = 132] = "MINIS_JOIN_TIME";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MINIS_LAST_ACTIVE_TIME"] = 133] = "MINIS_LAST_ACTIVE_TIME";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_FIRST_MINIS_ACTIVITY"] = 134] = "DAYS_SINCE_FIRST_MINIS_ACTIVITY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_SINCE_LAST_MINIS_ACTIVITY"] = 135] = "DAYS_SINCE_LAST_MINIS_ACTIVITY";
    /**
     * DEVICE_AV1_DECODING_SUPPORT - Server side property that is built from codec benchmarks to determine if a device supports av1
     * e.g. SOFTWARE, HARDWARE
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DEVICE_AV1_DECODING_SUPPORT"] = 136] = "DEVICE_AV1_DECODING_SUPPORT";
    /**
     * APP_PACKAGE_INSTALLER - Server side property that targets how an app was installed. This data is provided in the targetingQuery request.
     * e.g. com.android.vending, com.google.android.packageinstaller, com.lenovo.anyshare.gps, com.huawei.appmarket, cn.xender
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_PACKAGE_INSTALLER"] = 137] = "APP_PACKAGE_INSTALLER";
    /** URL - A client-determined URL */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["URL"] = 138] = "URL";
    /**
     * ESTIMATED_DURATION_FOR_EVENT_MS - The estimated duration for a config-specific event in milliseconds
     * For example, for a config that chooses upload location, use the estimated time to upload a file
     * e.g. 100
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ESTIMATED_DURATION_FOR_EVENT_MS"] = 139] = "ESTIMATED_DURATION_FOR_EVENT_MS";
    /**
     * MEDIA_SOURCE - Content's origin prior to being injected into the MDP stack.
     * e.g. CHAT, MEMORIES, LENS, MAPS
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MEDIA_SOURCE"] = 140] = "MEDIA_SOURCE";
    /**
     * ASSET_TYPE - How to compose an individual asset into one larger logical asset.
     * e.g. BLOOPS_STICKER, MUSIC, RAW_MEDIA, CANVAS_STICKER, etc.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ASSET_TYPE"] = 141] = "ASSET_TYPE";
    /**
     * STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL - This is based on percentile of number of story views in 5th tab user falls into
     * [-1 (unknown), 0 (no views), 1 (casual: view=1), 2 (regular: view=[2,3]), 3 (power: view>=4)]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL"] = 142] = "STORY_VIEWS_5TH_TAB_ENGAGEMENT_LEVEL";
    /**
     * REPORTED_AGE - This age based on user reported birthday.
     * e.g. 18
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["REPORTED_AGE"] = 143] = "REPORTED_AGE";
    /**
     * ANDROID_MOBILE_SERVICES_PROVIDER - Android has multiple sources of mobile services which provide functionality like GPS and Firebase APIs.
     * e.g. GMS, HMS
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ANDROID_MOBILE_SERVICES_PROVIDER"] = 144] = "ANDROID_MOBILE_SERVICES_PROVIDER";
    /** IS_ACQUIRED_USER - true if the user comes from a paid channel */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_ACQUIRED_USER"] = 145] = "IS_ACQUIRED_USER";
    /** YDPI - Physical pixels per inch of the screen in the Y dimension e.g. 440 */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["YDPI"] = 146] = "YDPI";
    /** BOLT_IS_CONTENT_POPULAR - A boolean representing whether the content being resolved is popular or not. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BOLT_IS_CONTENT_POPULAR"] = 147] = "BOLT_IS_CONTENT_POPULAR";
    /** CAPTURE_MODE - e.g. Default, Timeline */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAPTURE_MODE"] = 148] = "CAPTURE_MODE";
    /**
     * BIDIRECTIONAL_FRIEND_STATUS_VELLUM - This new breakdown is the same as what has been used for husky runbook
     * e.g. '0-5', '6-30', etc
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BIDIRECTIONAL_FRIEND_STATUS_VELLUM"] = 149] = "BIDIRECTIONAL_FRIEND_STATUS_VELLUM";
    /** ORIGIN - Origin or “application name” of user agent. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ORIGIN"] = 150] = "ORIGIN";
    /** LENSCORE_VERSION - Lens core version which will be passed by all clients */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LENSCORE_VERSION"] = 151] = "LENSCORE_VERSION";
    /** SNAPKIT_APP_ID - SnapKit app id from SnapKit Dev Portal */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAPKIT_APP_ID"] = 152] = "SNAPKIT_APP_ID";
    /** GPU - gpu as string e.g. Adreno 650 */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GPU"] = 153] = "GPU";
    /** CHIPSET_NAME - Chipset name as string  e.g. Qualcomm Snapdragon */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CHIPSET_NAME"] = 154] = "CHIPSET_NAME";
    /** CHIPSET_VERSION - Chipset version as int  e.g. 865 */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CHIPSET_VERSION"] = 155] = "CHIPSET_VERSION";
    /** HAS_ZERO_IDFA - True if user has a zero-ed out IDFA */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_ZERO_IDFA"] = 156] = "HAS_ZERO_IDFA";
    /** LIMIT_AD_TRACKING - True if user has limit ad tracking enabled */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LIMIT_AD_TRACKING"] = 157] = "LIMIT_AD_TRACKING";
    /**
     * ATT_AUTH_STATUS - Users app tracking transparency authentication status
     * values = [RESTRICTED, NOT_DETERMINED, DENIED, AUTHORIZED]
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ATT_AUTH_STATUS"] = 158] = "ATT_AUTH_STATUS";
    /** VP9_SOFTWARE_DECODING_SUPPORTED - A boolean representing if VP9 software decoding is supported */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["VP9_SOFTWARE_DECODING_SUPPORTED"] = 159] = "VP9_SOFTWARE_DECODING_SUPPORTED";
    /** AV1_SOFTWARE_DECODING_SUPPORTED - A boolean representing if AV1 software decoding is supported */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["AV1_SOFTWARE_DECODING_SUPPORTED"] = 160] = "AV1_SOFTWARE_DECODING_SUPPORTED";
    /** WITH_MUSIC - A boolean to indicate whether there is music edits (e.g. music sticker). */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["WITH_MUSIC"] = 161] = "WITH_MUSIC";
    /** CAMERA2_FRONT_SS_GAIN_OVER_TPA - An integer for front camera2. 100 * (snap send image using screenshot - snap send image using TPA) / snap send image using TPA */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA2_FRONT_SS_GAIN_OVER_TPA"] = 162] = "CAMERA2_FRONT_SS_GAIN_OVER_TPA";
    /** ATTACHMENT_TOOL_V2 - A boolean to indicate whether attachment tool v2 is enabled for this user. True is enabled */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ATTACHMENT_TOOL_V2"] = 163] = "ATTACHMENT_TOOL_V2";
    /**
     * MEDIA_TYPE - Type of media currently being processed.
     * e.g. IMAGE, VIDEO, GIF, AUDIO
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MEDIA_TYPE"] = 164] = "MEDIA_TYPE";
    /**
     * USER_PERSONA_V3 - User persona V3 defined in https://github.sc-corp.net/Snapchat/ab-test-console/blob/master/CircumstancesCMS/circumstances-proto/src/main/proto/user_property.proto
     * e.g. STORY_POSTER_V3, MAPPER_V3, CAMERA_LOVER_V3
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_PERSONA_V3"] = 165] = "USER_PERSONA_V3";
    /**
     * FEATURE_PROVIDED_SIZE - A client provided integer representing size. The units is decided by the caller.
     * This will be evaluated using FeatureProvidedSignals.size
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FEATURE_PROVIDED_SIZE"] = 166] = "FEATURE_PROVIDED_SIZE";
    /**
     * SNAPS_SEND_WITH_HEVC - An Int property showing the number of snaps the user sent in last 7 days, which the receivers had HEVC support.
     * The receiver HEVC support is defined by min of HEVC support, if multiple devices are used in the last 7 days.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAPS_SEND_WITH_HEVC"] = 167] = "SNAPS_SEND_WITH_HEVC";
    /**
     * SNAPS_SEND_WITHOUT_HEVC - An Int property showing the number of snaps the user sent in last 7 days, which the receivers did nor have HEVC support.
     * The receiver HEVC support is defined by min of HEVC support, if multiple devices are used in the last 7 days.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAPS_SEND_WITHOUT_HEVC"] = 168] = "SNAPS_SEND_WITHOUT_HEVC";
    /** CAMERA2_NATIVE_CRASH_OVER_CAMERA1 - An integer value. 100 * (camera 2 native crash rate - camera 1 native crash rate) / camera 1 native crash rate */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA2_NATIVE_CRASH_OVER_CAMERA1"] = 169] = "CAMERA2_NATIVE_CRASH_OVER_CAMERA1";
    /** CAMERA2_G2S_LATENCY_OVER_CAMERA1 - An integer value. 100 * (camera 2 G2S latency - camera 1 G2S latency) / camera 1 G2S latencyTargetingExpressionValidator.java */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CAMERA2_G2S_LATENCY_OVER_CAMERA1"] = 170] = "CAMERA2_G2S_LATENCY_OVER_CAMERA1";
    /** SCANNED_CATEGORY_IDS - The ids of the categories that are currently being scanned for (e.g. Amazon, Photomath, etc) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SCANNED_CATEGORY_IDS"] = 171] = "SCANNED_CATEGORY_IDS";
    /**
     * CONTENT_VIEW_SOURCE - ContentViewSource (mirrored from Blizzard enum) which identified Opera session
     * e.g. DF_FRIENDS, NYC, DF_FOR_YOU.  See https://github.sc-corp.net/Snapchat/snapchat/blob/master/blizzard/schema/blizzard-schema/codeGen/src/main/java/com/snapchat/analytics/schema/events/ContentViewSource.java
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CONTENT_VIEW_SOURCE"] = 172] = "CONTENT_VIEW_SOURCE";
    /**
     * IS_INTERNAL - A combination of the IS_EMPLOYEE, IS_TEST_USER, and BUILD_FLAVOR for internal builds
     * Used to align INTERNAL status of configs with develop mode of AB studies
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_INTERNAL"] = 173] = "IS_INTERNAL";
    /** IS_WEB - A boolean to indicate whether there is web browser request */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_WEB"] = 174] = "IS_WEB";
    /** RECIPIENTS_SUPPORT_HEVC - A boolean indicating whether a group of recipients support High Efficiency Video Encoding (HEVC) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["RECIPIENTS_SUPPORT_HEVC"] = 175] = "RECIPIENTS_SUPPORT_HEVC";
    /**
     * APP_OPEN_TO_MAP - Probabilty [0,100] of a user going to X page after oppening the app
     * X IN (map, lenses, friendfeeds, memories, community, spotlight) pages
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_MAP"] = 176] = "APP_OPEN_TO_MAP";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_FRIENDSFEED"] = 177] = "APP_OPEN_TO_FRIENDSFEED";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_LENSES"] = 178] = "APP_OPEN_TO_LENSES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_MEMORIES"] = 179] = "APP_OPEN_TO_MEMORIES";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_COMMUNITY"] = 180] = "APP_OPEN_TO_COMMUNITY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_OPEN_TO_SPOTLIGHT"] = 181] = "APP_OPEN_TO_SPOTLIGHT";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["IS_IMPACTED_BY_PINC_893"] = 182] = "IS_IMPACTED_BY_PINC_893";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["DAYS_BEFORE_BIRTHDAY"] = 183] = "DAYS_BEFORE_BIRTHDAY";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_BIPA"] = 184] = "HAS_BIPA";
    /** EMAIL_VERIFIED - Whether user's email has verified */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["EMAIL_VERIFIED"] = 185] = "EMAIL_VERIFIED";
    /** PHONE_VERIFIED - Whether user's phone number has verified */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PHONE_VERIFIED"] = 186] = "PHONE_VERIFIED";
    /** NOTIFICATION_PERM_GRANTED - Whether notification permission has granted */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["NOTIFICATION_PERM_GRANTED"] = 187] = "NOTIFICATION_PERM_GRANTED";
    /** CONTACT_PERM_GRANTED - Whether contact permission has granted */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CONTACT_PERM_GRANTED"] = 188] = "CONTACT_PERM_GRANTED";
    /** MUTUAL_FRIENDS_COUNT - Mutual friends count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MUTUAL_FRIENDS_COUNT"] = 189] = "MUTUAL_FRIENDS_COUNT";
    /** GROUPS_COUNT - Groups count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GROUPS_COUNT"] = 190] = "GROUPS_COUNT";
    /** SPOTLIGHT_STORY_ENGAGEMENT_STATUS - Spotlight user cohort */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SPOTLIGHT_STORY_ENGAGEMENT_STATUS"] = 191] = "SPOTLIGHT_STORY_ENGAGEMENT_STATUS";
    /** HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS - Whether user has non-team snapchat conversations */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS"] = 192] = "HAS_NON_TEAM_SNAPCHAT_CONVERSATIONS";
    /** COGNAC_APP_ID - A Cognac game/mini id that a user is playing */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["COGNAC_APP_ID"] = 193] = "COGNAC_APP_ID";
    /** PLAYBACK_ITEM_TYPE - Opera playback item type */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["PLAYBACK_ITEM_TYPE"] = 194] = "PLAYBACK_ITEM_TYPE";
    /**
     * SUP_IS_BIRTHDAY_PARTY_ENABLED - SUP's birthday party enabled:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/3554d12/proto/sup/sup.proto#L87
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_IS_BIRTHDAY_PARTY_ENABLED"] = 195] = "SUP_IS_BIRTHDAY_PARTY_ENABLED";
    /**
     * SUP_FHP_LAST_INTERACTION_TIME_MILLIS_TO_NOW - SUP's fhp last interaction time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L990
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_LAST_INTERACTION_TIME_MILLIS_TO_NOW"] = 196] = "SUP_FHP_LAST_INTERACTION_TIME_MILLIS_TO_NOW";
    /**
     * SUP_FHP_CONTACT_SYNC_INTERACTION_COUNT - SUP's fhp contact sync interaction count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L992
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_CONTACT_SYNC_INTERACTION_COUNT"] = 197] = "SUP_FHP_CONTACT_SYNC_INTERACTION_COUNT";
    /**
     * SUP_FHP_CONTACT_SYNC_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's fhp contact sync prompt first shown time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L994
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_CONTACT_SYNC_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 198] = "SUP_FHP_CONTACT_SYNC_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /**
     * SUP_FHP_NOTIFICATION_PERMISSION_INTERACTION_COUNT - SUP's fhp notification permission prompt interaction counter, including dismisses and clicks:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L996
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_NOTIFICATION_PERMISSION_INTERACTION_COUNT"] = 199] = "SUP_FHP_NOTIFICATION_PERMISSION_INTERACTION_COUNT";
    /**
     * SUP_FHP_NOTIFICATION_PERMISSION_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's fhp notification permission prompt first shown time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L998
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_NOTIFICATION_PERMISSION_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 200] = "SUP_FHP_NOTIFICATION_PERMISSION_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /**
     * SUP_FHP_PHONE_VERIFICATION_INTERACTION_COUNT - SUP's fhp phone verification prompt interaction counter, including dismisses and clicks:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1000
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_PHONE_VERIFICATION_INTERACTION_COUNT"] = 201] = "SUP_FHP_PHONE_VERIFICATION_INTERACTION_COUNT";
    /**
     * SUP_FHP_PHONE_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's fhp phone verification prompt first shown time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1002
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_PHONE_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 202] = "SUP_FHP_PHONE_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /**
     * SUP_FHP_BIRTHDAY_PARTY_INTERACTION_COUNT - SUP's fhp birthday party prompt interaction counter, including dismisses and clicks:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1004
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_BIRTHDAY_PARTY_INTERACTION_COUNT"] = 203] = "SUP_FHP_BIRTHDAY_PARTY_INTERACTION_COUNT";
    /**
     * SUP_FHP_BIRTHDAY_PARTY_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's fhp birthday party prompt first shown time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1006
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_BIRTHDAY_PARTY_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 204] = "SUP_FHP_BIRTHDAY_PARTY_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /**
     * SUP_FHP_EMAIL_VERIFICATION_INTERACTION_COUNT - SUP's fhp email verification prompt interaction counter, including dismisses and clicks:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1008
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_EMAIL_VERIFICATION_INTERACTION_COUNT"] = 205] = "SUP_FHP_EMAIL_VERIFICATION_INTERACTION_COUNT";
    /**
     * SUP_FHP_EMAIL_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's fhp email verification prompt first shown time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1010
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_EMAIL_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 206] = "SUP_FHP_EMAIL_VERIFICATION_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /** HAS_USED_SPECTACLES - Whether the user has used Spectacles (incl. Cheerios) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_USED_SPECTACLES"] = 207] = "HAS_USED_SPECTACLES";
    /** BOLT_CLIENT_APP_STATE - Enum indicating client App State (Used by BOLT to create targeted network rules) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BOLT_CLIENT_APP_STATE"] = 208] = "BOLT_CLIENT_APP_STATE";
    /** EMAIL_SET - Whether user's email has been set (might or might not be verified) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["EMAIL_SET"] = 209] = "EMAIL_SET";
    /** BITMOJI_SET - Whether user's bitmoji has been set */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BITMOJI_SET"] = 210] = "BITMOJI_SET";
    /** QUICK_TAP_CAMERA_SUPPORTED_ENABLED - Quick tap on camera is supported and enabled */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["QUICK_TAP_CAMERA_SUPPORTED_ENABLED"] = 211] = "QUICK_TAP_CAMERA_SUPPORTED_ENABLED";
    /** HAS_ADD_FRIENDS_REQUEST - Whether user has add friends request */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_ADD_FRIENDS_REQUEST"] = 212] = "HAS_ADD_FRIENDS_REQUEST";
    /** APP_LAUNCH_FROM_PUSH - App launched from push notification */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_LAUNCH_FROM_PUSH"] = 213] = "APP_LAUNCH_FROM_PUSH";
    /** APP_LAUNCH_TYPE - App launch type: cold start, warm start, registration and login */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["APP_LAUNCH_TYPE"] = 214] = "APP_LAUNCH_TYPE";
    /** BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT - Billboard campaign level locally tracked impression count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT"] = 215] = "BILLBOARD_CAMPAIGN_LOCAL_IMPRESSION_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked last impression time in secs to now */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 216] = "BILLBOARD_CAMPAIGN_LOCAL_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /** BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT - Billboard campaign level locally tracked continuous dismiss count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT"] = 217] = "BILLBOARD_CAMPAIGN_LOCAL_CONTINUOUS_DISMISS_COUNT";
    /** FST_NUM_IN_APP_WARNINGS - FST number of in app warnings to show */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FST_NUM_IN_APP_WARNINGS"] = 218] = "FST_NUM_IN_APP_WARNINGS";
    /** FST_SHOULD_CHANGE_PASSWORD - FST user should change password */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FST_SHOULD_CHANGE_PASSWORD"] = 219] = "FST_SHOULD_CHANGE_PASSWORD";
    /** FST_IS_BACKGROUND_CHECK - Whether it's a background check or foreground check. Android specific */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FST_IS_BACKGROUND_CHECK"] = 220] = "FST_IS_BACKGROUND_CHECK";
    /**
     * SUP_FST_IDENTITY_VERIFICATION_IMPRESSION_COUNT - SUP's fst identity verification prompt impression count
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L648
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_IDENTITY_VERIFICATION_IMPRESSION_COUNT"] = 221] = "SUP_FST_IDENTITY_VERIFICATION_IMPRESSION_COUNT";
    /**
     * SUP_FST_IDENTITY_VERIFICATION_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's fst identity verification prompt last impression time in secs to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L647
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_IDENTITY_VERIFICATION_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 222] = "SUP_FST_IDENTITY_VERIFICATION_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * SUP_FST_FRIEND_ADD_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's fst friend reciprocation prompt last impression time in secs to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L482
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_FRIEND_ADD_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 223] = "SUP_FST_FRIEND_ADD_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * SUP_FST_CONTACT_SYNC_IMPRESSION_COUNT - SUP's fst contact sync impression count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L536
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_CONTACT_SYNC_IMPRESSION_COUNT"] = 224] = "SUP_FST_CONTACT_SYNC_IMPRESSION_COUNT";
    /**
     * SUP_FST_CONTACT_SYNC_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's fst contact sync last impression time in secs to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L537
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_CONTACT_SYNC_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 225] = "SUP_FST_CONTACT_SYNC_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * SUP_FST_BITMOJI_CREATION_IMPRESSION_COUNT - SUP's fst bitmoji creation impression count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L841
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_BITMOJI_CREATION_IMPRESSION_COUNT"] = 226] = "SUP_FST_BITMOJI_CREATION_IMPRESSION_COUNT";
    /**
     * SUP_FST_BITMOJI_CREATION_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's fst bitmoji creation last impression time in secs to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L828
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_BITMOJI_CREATION_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 227] = "SUP_FST_BITMOJI_CREATION_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * SUP_FST_USER_REACHABILITY_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's fst user reachability last impression time in secs to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L986
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_USER_REACHABILITY_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 228] = "SUP_FST_USER_REACHABILITY_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * SUP_FST_BIPA_HAS_CLICKED - SUP's fst bipa has been accepted:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L908
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_BIPA_HAS_CLICKED"] = 229] = "SUP_FST_BIPA_HAS_CLICKED";
    /**
     * INCLUSION_PANEL_MEMBER - Flag to indicate that the user took part in the inclusion (Made for me) panel.
     * True if the user did, false otherwise.
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["INCLUSION_PANEL_MEMBER"] = 230] = "INCLUSION_PANEL_MEMBER";
    /** AUDIO_RECORD_PERM_GRANTED - Whether audio record permission has granted */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["AUDIO_RECORD_PERM_GRANTED"] = 231] = "AUDIO_RECORD_PERM_GRANTED";
    /** MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT - Mutual Friends with birthday count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT"] = 232] = "MUTUAL_FRIENDS_WITH_BIRTHDAY_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT - Billboard campaign level locally tracked dismiss count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT"] = 233] = "BILLBOARD_CAMPAIGN_LOCAL_DISMISS_COUNT";
    /**
     * SUP_PAC_PHONE_VERIFICATION_DISMISS_COUNT - SUP's pac phone verification dismiss count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L1092
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_PHONE_VERIFICATION_DISMISS_COUNT"] = 234] = "SUP_PAC_PHONE_VERIFICATION_DISMISS_COUNT";
    /**
     * SUP_PAC_BIRTHDAY_MINI_IMPRESSION_COUNT - SUP's pac birthday mini impression count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L754
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_BIRTHDAY_MINI_IMPRESSION_COUNT"] = 235] = "SUP_PAC_BIRTHDAY_MINI_IMPRESSION_COUNT";
    /**
     * SUP_PAC_BIRTHDAY_MINI_DISMISS_COUNT - SUP's pac birthday mini dismiss count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L1088
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_BIRTHDAY_MINI_DISMISS_COUNT"] = 236] = "SUP_PAC_BIRTHDAY_MINI_DISMISS_COUNT";
    /**
     * SUP_PAC_RUN_FOR_OFFICE_MINI_IMPRESSION_COUNT - SUP's pac run for office mini impression count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L760
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_RUN_FOR_OFFICE_MINI_IMPRESSION_COUNT"] = 237] = "SUP_PAC_RUN_FOR_OFFICE_MINI_IMPRESSION_COUNT";
    /**
     * SUP_PAC_RUN_FOR_OFFICE_MINI_DISMISS_COUNT - SUP's pac run for office mini dismiss count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L1090
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_RUN_FOR_OFFICE_MINI_DISMISS_COUNT"] = 238] = "SUP_PAC_RUN_FOR_OFFICE_MINI_DISMISS_COUNT";
    /**
     * SUP_PAC_FRIEND_CHECKUP_LAST_SHOWN_TIME_MILLIS_TO_NOW - SUP's pac friend checkup card was last shown in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L931
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_FRIEND_CHECKUP_LAST_SHOWN_TIME_MILLIS_TO_NOW"] = 239] = "SUP_PAC_FRIEND_CHECKUP_LAST_SHOWN_TIME_MILLIS_TO_NOW";
    /**
     * SUP_PAC_FRIEND_CHECKUP_IMPRESSION_COUNT - SUP's pac friend checkup card's impression count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L933
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_FRIEND_CHECKUP_IMPRESSION_COUNT"] = 240] = "SUP_PAC_FRIEND_CHECKUP_IMPRESSION_COUNT";
    /**
     * SUP_PAC_FRIEND_CHECKUP_CLICK_COUNT - SUP's friend checkup card's click count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L935
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_FRIEND_CHECKUP_CLICK_COUNT"] = 241] = "SUP_PAC_FRIEND_CHECKUP_CLICK_COUNT";
    /**
     * SUP_PAC_FRIEND_CHECKUP_DISMISS_COUNT - SUP's friend checkup card's dismiss count:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/34ca2a6/proto/sup/sup.proto#L937
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_FRIEND_CHECKUP_DISMISS_COUNT"] = 242] = "SUP_PAC_FRIEND_CHECKUP_DISMISS_COUNT";
    /** USE_VERTICAL_NAVIGATION - A boolean to indicate whether Opera uses vertical navigation or not. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USE_VERTICAL_NAVIGATION"] = 243] = "USE_VERTICAL_NAVIGATION";
    /**
     * HEVC_HW_DECODER - Whether a device supports HEVC decoding using hardware and what max resolution width it should support
     * e.g. 720, 540, 1080
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HEVC_HW_DECODER"] = 244] = "HEVC_HW_DECODER";
    /**
     * HEVC_SW_DECODER - Whether a device supports HEVC decoding using hardware/software and what max resolution width it should support
     * e.g. 720, 540, 1080
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HEVC_SW_DECODER"] = 245] = "HEVC_SW_DECODER";
    /**
     * SUP_FHP_CONTACT_SYNC_IMPRESSION_COUNT - SUP's FHP contact sync prompt impression counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/fc53ac7/proto/sup/sup.proto#L1134
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_CONTACT_SYNC_IMPRESSION_COUNT"] = 246] = "SUP_FHP_CONTACT_SYNC_IMPRESSION_COUNT";
    /**
     * SUP_FHP_NOTIFICATION_PERMISSION_IMPRESSION_COUNT - SUP's FHP notification permission prompt impression counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/fc53ac7/proto/sup/sup.proto#L1136
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_NOTIFICATION_PERMISSION_IMPRESSION_COUNT"] = 247] = "SUP_FHP_NOTIFICATION_PERMISSION_IMPRESSION_COUNT";
    /**
     * SUP_FHP_PHONE_VERIFICATION_IMPRESSION_COUNT - SUP's FHP phone verification prompt impression counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/fc53ac7/proto/sup/sup.proto#L1138
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_PHONE_VERIFICATION_IMPRESSION_COUNT"] = 248] = "SUP_FHP_PHONE_VERIFICATION_IMPRESSION_COUNT";
    /**
     * SUP_FHP_EMAIL_VERIFICATION_IMPRESSION_COUNT - SUP's FHP email verification prompt impression counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/fc53ac7/proto/sup/sup.proto#L1140
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_EMAIL_VERIFICATION_IMPRESSION_COUNT"] = 249] = "SUP_FHP_EMAIL_VERIFICATION_IMPRESSION_COUNT";
    /** MEMORIES_COUNT - Num of memories snaps user has */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MEMORIES_COUNT"] = 250] = "MEMORIES_COUNT";
    /**
     * SUP_FHP_CONTACT_SYNC_DISMISS_COUNT - SUP's FHP contact sync prompt dismiss counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1160
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_CONTACT_SYNC_DISMISS_COUNT"] = 251] = "SUP_FHP_CONTACT_SYNC_DISMISS_COUNT";
    /**
     * SUP_FHP_NOTIFICATION_PERMISSION_DISMISS_COUNT - SUP's FHP notification permission prompt dismiss counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1162
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_NOTIFICATION_PERMISSION_DISMISS_COUNT"] = 252] = "SUP_FHP_NOTIFICATION_PERMISSION_DISMISS_COUNT";
    /**
     * SUP_FHP_PHONE_VERIFICATION_DISMISS_COUNT - SUP's FHP phone verification prompt dismiss counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1164
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_PHONE_VERIFICATION_DISMISS_COUNT"] = 253] = "SUP_FHP_PHONE_VERIFICATION_DISMISS_COUNT";
    /**
     * SUP_FHP_EMAIL_VERIFICATION_DISMISS_COUNT - SUP's FHP email verification prompt dismiss counter:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/29934fd/proto/sup/sup.proto#L1166
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_EMAIL_VERIFICATION_DISMISS_COUNT"] = 254] = "SUP_FHP_EMAIL_VERIFICATION_DISMISS_COUNT";
    /** SERVER_ONLY - Server side property to mark a config is meant to be consumed by server callers (enforce serving platform initiative) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SERVER_ONLY"] = 255] = "SERVER_ONLY";
    /** HAS_ACTIVE_SNAPCHAT_PLUS - Whether user has active Snapchat+ subscription in Atlas's UserData plus_subscription_info property */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["HAS_ACTIVE_SNAPCHAT_PLUS"] = 256] = "HAS_ACTIVE_SNAPCHAT_PLUS";
    /** SUP_FST_SNAP_PLUS_IMPRESSION_COUNT - Client Property of FST snap plus takeover impression count by the SUP property (ID: 672) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_SNAP_PLUS_IMPRESSION_COUNT"] = 257] = "SUP_FST_SNAP_PLUS_IMPRESSION_COUNT";
    /** SUP_FST_SNAP_PLUS_LAST_IMPRESSION_TIME_SECS_TO_NOW - Client Property of FST snap plus takeover last impression time in secs to now by the SUP property (ID: 673) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_SNAP_PLUS_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 258] = "SUP_FST_SNAP_PLUS_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /**
     * CONFIG_TE_ENABLED - Server side property to enable gradual rollout of config level targeting expression.
     * Property will be used only in the backend and excluded from CMS
     * Property will be deprecated after rollout is 100%
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CONFIG_TE_ENABLED"] = 259] = "CONFIG_TE_ENABLED";
    /** SNAP_KIT_OAUTH_ID - OAuth Client ID identifying the source third party application for Snap Kit content */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAP_KIT_OAUTH_ID"] = 260] = "SNAP_KIT_OAUTH_ID";
    /** ORGANIZATION_TYPE - Organization type of advertiser purchasing ads */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ORGANIZATION_TYPE"] = 261] = "ORGANIZATION_TYPE";
    /**
     * CHUNK_UPLOAD_SUPPORT_REQUIRED - Client provided boolean that represent whether the config
     * needs to support uploading in a chunked/streaming manner
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CHUNK_UPLOAD_SUPPORT_REQUIRED"] = 262] = "CHUNK_UPLOAD_SUPPORT_REQUIRED";
    /** SUP_FST_IOS_LOCKSCREEN_WIDGET_IMPRESSION_COUNT - SUP's FST iOS lock screen widgets impression count (ID: 696) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_IOS_LOCKSCREEN_WIDGET_IMPRESSION_COUNT"] = 263] = "SUP_FST_IOS_LOCKSCREEN_WIDGET_IMPRESSION_COUNT";
    /** SUP_FST_IOS_LOCKSCREEN_WIDGET_LAST_IMPRESSION_TIME_SECS_TO_NOW - SUP's FST iOS lock screen widgets last impression time in seconds to now (ID: 697) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_IOS_LOCKSCREEN_WIDGET_LAST_IMPRESSION_TIME_SECS_TO_NOW"] = 264] = "SUP_FST_IOS_LOCKSCREEN_WIDGET_LAST_IMPRESSION_TIME_SECS_TO_NOW";
    /** SUP_FST_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT - SUP's FST iOS lock screen widgets prompt interaction counter (ID: 698) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FST_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT"] = 265] = "SUP_FST_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT";
    /** SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT - SUP's PAC my profile iOS lock screen widgets prompt interaction count (ID: 699) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT"] = 266] = "SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_CLICK_COUNT";
    /** SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_DISMISS_COUNT - SUP's PAC my profile iOS lock screen widgets dismiss count (ID: 700) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_DISMISS_COUNT"] = 267] = "SUP_PAC_MY_PROFILE_IOS_LOCKSCREEN_WIDGET_DISMISS_COUNT";
    /** SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED - SUP's iOS lock screen widget has been enabled (ID: 701) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED"] = 268] = "SUP_IOS_LOCK_SCREEN_WIDGET_ENABLED";
    /** CLOUDFRONT_POP - The last known CloudFront Point of Presence used */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["CLOUDFRONT_POP"] = 269] = "CLOUDFRONT_POP";
    /** BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked first impresstion time to now */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW"] = 270] = "BILLBOARD_CAMPAIGN_LOCAL_FIRST_IMPRESSION_TIME_SECS_TO_NOW";
    /** BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT - Billboard campaign level locally tracked click count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT"] = 271] = "BILLBOARD_CAMPAIGN_LOCAL_CLICK_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT - Billboard campaign level locally tracked interaction count */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT"] = 272] = "BILLBOARD_CAMPAIGN_LOCAL_INTERACTION_COUNT";
    /** BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW - Billboard campaign level locally tracked last interaction time to now */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW"] = 273] = "BILLBOARD_CAMPAIGN_LOCAL_LAST_INTERACTION_TIME_SECS_TO_NOW";
    /** SNAP_PRIVACY - Atlas enum property that indicates a user's snap privacy settings (Friends vs. Everyone) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SNAP_PRIVACY"] = 274] = "SNAP_PRIVACY";
    /** FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH - Lock screen widgets billboard - app open via push notification */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH"] = 275] = "FST_LOCK_SCREEN_WIDGET_BILLBOARD_LAUNCHED_FROM_PUSH";
    /** WEB_SCREEN_WIDTH - Web browser screen width in CSS pixels */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["WEB_SCREEN_WIDTH"] = 276] = "WEB_SCREEN_WIDTH";
    /** WEB_SCREEN_HEIGHT - Web browser screen height in CSS pixels */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["WEB_SCREEN_HEIGHT"] = 277] = "WEB_SCREEN_HEIGHT";
    /** ADS_INTERFACES_IS_NEW_ORGANIZATION - Is a new organization in ads interfaces UI */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["ADS_INTERFACES_IS_NEW_ORGANIZATION"] = 278] = "ADS_INTERFACES_IS_NEW_ORGANIZATION";
    /**
     * SUP_FHP_REGISTER_TO_VOTE_CAMPAIGN_LAST_DISMISS_TIME_MILLIS_TO_NOW - SUP's fhp register to vote campaign dismiss time in millis to now:
     * https://github.sc-corp.net/Snapchat/pb_schema/blob/ab264e8/proto/sup/sup.proto#L1304
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["SUP_FHP_REGISTER_TO_VOTE_CAMPAIGN_LAST_DISMISS_TIME_MILLIS_TO_NOW"] = 279] = "SUP_FHP_REGISTER_TO_VOTE_CAMPAIGN_LAST_DISMISS_TIME_MILLIS_TO_NOW";
    /** GHE_ORGANIZATION - The Github Enterprise organization */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GHE_ORGANIZATION"] = 280] = "GHE_ORGANIZATION";
    /** GHE_REPOSITORY - The Github Enterprise Repository, which is contained by a GHE organization */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GHE_REPOSITORY"] = 281] = "GHE_REPOSITORY";
    /** GHE_USER_EMAIL - In the context of Machamp, the user identifier (email) */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["GHE_USER_EMAIL"] = 282] = "GHE_USER_EMAIL";
    /** BUILD_DEFINITION_ID - In the context of Machamp, the build definition identifier is a UUID hash of the GHE organization and and repository. */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BUILD_DEFINITION_ID"] = 283] = "BUILD_DEFINITION_ID";
    /** BUILD_DEFINITION_NAME - In the context of Machamp, the build definition name is name of the build from the build.yaml */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["BUILD_DEFINITION_NAME"] = 284] = "BUILD_DEFINITION_NAME";
    /** LCA_PRINCIPAL - LCA Principal of the service calling Circumstance Engine */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["LCA_PRINCIPAL"] = 285] = "LCA_PRINCIPAL";
    /**
     * MEDIA_PERFORMANCE_CLASS - A performance class defines a set of device capabilities by Android
     * e.g. 30, 31, See https://android-developers.googleblog.com/2022/03/using-performance-class-to-optimize.html
     */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["MEDIA_PERFORMANCE_CLASS"] = 286] = "MEDIA_PERFORMANCE_CLASS";
    /** COMMUNITY_COUNT - Number of Communities user is in */
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["COMMUNITY_COUNT"] = 287] = "COMMUNITY_COUNT";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["USER_GROUP"] = 9999] = "USER_GROUP";
    StudyMetadata_TargetingExpression_Property[StudyMetadata_TargetingExpression_Property["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(StudyMetadata_TargetingExpression_Property || (StudyMetadata_TargetingExpression_Property = {}));
var globalThis$1 = (function () {
    if (typeof globalThis$1 !== "undefined")
        return globalThis$1;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

/** ProtoBuf API definition for AB config service. */
/** Type of id */
var IdType;
(function (IdType) {
    /** UNKNOWN_UNSET - Default, please don't use. */
    IdType[IdType["UNKNOWN_UNSET"] = 0] = "UNKNOWN_UNSET";
    /** USER - User Id. */
    IdType[IdType["USER"] = 1] = "USER";
    /** SNAPCHAT_CLIENT - Snapchat Client Id. */
    IdType[IdType["SNAPCHAT_CLIENT"] = 2] = "SNAPCHAT_CLIENT";
    /** WEB_CLIENT - Web Client Id. */
    IdType[IdType["WEB_CLIENT"] = 3] = "WEB_CLIENT";
    /** WEB_SNAPCHAT_USER - Web Snapchat User Id. */
    IdType[IdType["WEB_SNAPCHAT_USER"] = 4] = "WEB_SNAPCHAT_USER";
    /** AD_ACCOUNT - Ad account ID */
    IdType[IdType["AD_ACCOUNT"] = 5] = "AD_ACCOUNT";
    /** AM_ORGANIZATION - Ads Manager organization ID */
    IdType[IdType["AM_ORGANIZATION"] = 6] = "AM_ORGANIZATION";
    /** AM_MEMBER - Ads Manager member ID */
    IdType[IdType["AM_MEMBER"] = 7] = "AM_MEMBER";
    /** AM_SESSION - Ads Manager session ID */
    IdType[IdType["AM_SESSION"] = 8] = "AM_SESSION";
    /** AM_PROFILE - Ads Manager profile ID */
    IdType[IdType["AM_PROFILE"] = 9] = "AM_PROFILE";
    /** AM_SNAPCHAT_USER - Snapchat user ID on Ads Manager */
    IdType[IdType["AM_SNAPCHAT_USER"] = 10] = "AM_SNAPCHAT_USER";
    /** SNAPCHAT_ADVERTISING - Snapchat Advertising ID for Budget A/B */
    IdType[IdType["SNAPCHAT_ADVERTISING"] = 11] = "SNAPCHAT_ADVERTISING";
    /** AM_CLIENT - Ads Manager client ID */
    IdType[IdType["AM_CLIENT"] = 12] = "AM_CLIENT";
    /** CONFIG_DEVICE - Permanent device ID */
    IdType[IdType["CONFIG_DEVICE"] = 13] = "CONFIG_DEVICE";
    IdType[IdType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(IdType || (IdType = {}));
/** Type of logging */
var LoggingType;
(function (LoggingType) {
    /** UNSET - Default, please don't use. */
    LoggingType[LoggingType["UNSET"] = 0] = "UNSET";
    /** SERVER_TRIGGER - Server trigger. */
    LoggingType[LoggingType["SERVER_TRIGGER"] = 1] = "SERVER_TRIGGER";
    /** CLIENT_TRIGGER - Client trigger. */
    LoggingType[LoggingType["CLIENT_TRIGGER"] = 2] = "CLIENT_TRIGGER";
    /** ALLOCATION - Allocation logging. */
    LoggingType[LoggingType["ALLOCATION"] = 3] = "ALLOCATION";
    LoggingType[LoggingType["UNRECOGNIZED"] = -1] = "UNRECOGNIZED";
})(LoggingType || (LoggingType = {}));
var baseStudyTreatmentInfo = { studyName: "", treatmentId: 0 };
var StudyTreatmentInfo = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.studyName !== "") {
            writer.uint32(10).string(message.studyName);
        }
        if (message.treatmentId !== 0) {
            writer.uint32(16).uint32(message.treatmentId);
        }
        if (message.expGuid !== undefined) {
            Guid.encode(message.expGuid, writer.uint32(26).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseStudyTreatmentInfo);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.studyName = reader.string();
                    break;
                case 2:
                    message.treatmentId = reader.uint32();
                    break;
                case 3:
                    message.expGuid = Guid.decode(reader, reader.uint32());
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseStudyTreatmentInfo);
        if (object.studyName !== undefined && object.studyName !== null) {
            message.studyName = object.studyName;
        }
        else {
            message.studyName = "";
        }
        if (object.treatmentId !== undefined && object.treatmentId !== null) {
            message.treatmentId = object.treatmentId;
        }
        else {
            message.treatmentId = 0;
        }
        if (object.expGuid !== undefined && object.expGuid !== null) {
            message.expGuid = Guid.fromPartial(object.expGuid);
        }
        else {
            message.expGuid = undefined;
        }
        return message;
    },
};
var baseId = { value: "", type: 0 };
var Id = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.value !== "") {
            writer.uint32(10).string(message.value);
        }
        if (message.type !== 0) {
            writer.uint32(16).int32(message.type);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseId);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.value = reader.string();
                    break;
                case 2:
                    message.type = reader.int32();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseId);
        if (object.value !== undefined && object.value !== null) {
            message.value = object.value;
        }
        else {
            message.value = "";
        }
        if (object.type !== undefined && object.type !== null) {
            message.type = object.type;
        }
        else {
            message.type = 0;
        }
        return message;
    },
};
var baseGeoData = { countryCode: "", city: "", region: "" };
var GeoData = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.countryCode !== "") {
            writer.uint32(10).string(message.countryCode);
        }
        if (message.city !== "") {
            writer.uint32(18).string(message.city);
        }
        if (message.region !== "") {
            writer.uint32(26).string(message.region);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseGeoData);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.countryCode = reader.string();
                    break;
                case 2:
                    message.city = reader.string();
                    break;
                case 3:
                    message.region = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseGeoData);
        if (object.countryCode !== undefined && object.countryCode !== null) {
            message.countryCode = object.countryCode;
        }
        else {
            message.countryCode = "";
        }
        if (object.city !== undefined && object.city !== null) {
            message.city = object.city;
        }
        else {
            message.city = "";
        }
        if (object.region !== undefined && object.region !== null) {
            message.region = object.region;
        }
        else {
            message.region = "";
        }
        return message;
    },
};
var baseProperties = {
    userName: "",
    userAgentString: "",
    os: "",
    deviceName: "",
    browserName: "",
    locale: "",
    osVersion: "",
    appVersion: "",
    appBuild: "",
};
var Properties = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.userName !== "") {
            writer.uint32(10).string(message.userName);
        }
        if (message.userAgentString !== "") {
            writer.uint32(18).string(message.userAgentString);
        }
        if (message.geoData !== undefined) {
            GeoData.encode(message.geoData, writer.uint32(26).fork()).ldelim();
        }
        if (message.os !== "") {
            writer.uint32(34).string(message.os);
        }
        if (message.deviceName !== "") {
            writer.uint32(42).string(message.deviceName);
        }
        if (message.browserName !== "") {
            writer.uint32(50).string(message.browserName);
        }
        if (message.locale !== "") {
            writer.uint32(58).string(message.locale);
        }
        if (message.osVersion !== "") {
            writer.uint32(66).string(message.osVersion);
        }
        if (message.appVersion !== "") {
            writer.uint32(74).string(message.appVersion);
        }
        if (message.appBuild !== "") {
            writer.uint32(82).string(message.appBuild);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseProperties);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.userName = reader.string();
                    break;
                case 2:
                    message.userAgentString = reader.string();
                    break;
                case 3:
                    message.geoData = GeoData.decode(reader, reader.uint32());
                    break;
                case 4:
                    message.os = reader.string();
                    break;
                case 5:
                    message.deviceName = reader.string();
                    break;
                case 6:
                    message.browserName = reader.string();
                    break;
                case 7:
                    message.locale = reader.string();
                    break;
                case 8:
                    message.osVersion = reader.string();
                    break;
                case 9:
                    message.appVersion = reader.string();
                    break;
                case 10:
                    message.appBuild = reader.string();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseProperties);
        if (object.userName !== undefined && object.userName !== null) {
            message.userName = object.userName;
        }
        else {
            message.userName = "";
        }
        if (object.userAgentString !== undefined &&
            object.userAgentString !== null) {
            message.userAgentString = object.userAgentString;
        }
        else {
            message.userAgentString = "";
        }
        if (object.geoData !== undefined && object.geoData !== null) {
            message.geoData = GeoData.fromPartial(object.geoData);
        }
        else {
            message.geoData = undefined;
        }
        if (object.os !== undefined && object.os !== null) {
            message.os = object.os;
        }
        else {
            message.os = "";
        }
        if (object.deviceName !== undefined && object.deviceName !== null) {
            message.deviceName = object.deviceName;
        }
        else {
            message.deviceName = "";
        }
        if (object.browserName !== undefined && object.browserName !== null) {
            message.browserName = object.browserName;
        }
        else {
            message.browserName = "";
        }
        if (object.locale !== undefined && object.locale !== null) {
            message.locale = object.locale;
        }
        else {
            message.locale = "";
        }
        if (object.osVersion !== undefined && object.osVersion !== null) {
            message.osVersion = object.osVersion;
        }
        else {
            message.osVersion = "";
        }
        if (object.appVersion !== undefined && object.appVersion !== null) {
            message.appVersion = object.appVersion;
        }
        else {
            message.appVersion = "";
        }
        if (object.appBuild !== undefined && object.appBuild !== null) {
            message.appBuild = object.appBuild;
        }
        else {
            message.appBuild = "";
        }
        return message;
    },
};
var baseAbExposureData = { loggingType: 0 };
var AbExposureData = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        if (message.id !== undefined) {
            Id.encode(message.id, writer.uint32(10).fork()).ldelim();
        }
        for (var _i = 0, _a = message.studyTreatmentInfo; _i < _a.length; _i++) {
            var v = _a[_i];
            StudyTreatmentInfo.encode(v, writer.uint32(18).fork()).ldelim();
        }
        if (message.properties !== undefined) {
            Properties.encode(message.properties, writer.uint32(26).fork()).ldelim();
        }
        if (message.loggingType !== 0) {
            writer.uint32(32).int32(message.loggingType);
        }
        for (var _b = 0, _c = message.secondaryIds; _b < _c.length; _b++) {
            var v = _c[_b];
            Id.encode(v, writer.uint32(42).fork()).ldelim();
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseAbExposureData);
        message.studyTreatmentInfo = [];
        message.secondaryIds = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.id = Id.decode(reader, reader.uint32());
                    break;
                case 2:
                    message.studyTreatmentInfo.push(StudyTreatmentInfo.decode(reader, reader.uint32()));
                    break;
                case 3:
                    message.properties = Properties.decode(reader, reader.uint32());
                    break;
                case 4:
                    message.loggingType = reader.int32();
                    break;
                case 5:
                    message.secondaryIds.push(Id.decode(reader, reader.uint32()));
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseAbExposureData);
        message.studyTreatmentInfo = [];
        message.secondaryIds = [];
        if (object.id !== undefined && object.id !== null) {
            message.id = Id.fromPartial(object.id);
        }
        else {
            message.id = undefined;
        }
        if (object.studyTreatmentInfo !== undefined &&
            object.studyTreatmentInfo !== null) {
            for (var _i = 0, _a = object.studyTreatmentInfo; _i < _a.length; _i++) {
                var e = _a[_i];
                message.studyTreatmentInfo.push(StudyTreatmentInfo.fromPartial(e));
            }
        }
        if (object.properties !== undefined && object.properties !== null) {
            message.properties = Properties.fromPartial(object.properties);
        }
        else {
            message.properties = undefined;
        }
        if (object.loggingType !== undefined && object.loggingType !== null) {
            message.loggingType = object.loggingType;
        }
        else {
            message.loggingType = 0;
        }
        if (object.secondaryIds !== undefined && object.secondaryIds !== null) {
            for (var _b = 0, _c = object.secondaryIds; _b < _c.length; _b++) {
                var e = _c[_b];
                message.secondaryIds.push(Id.fromPartial(e));
            }
        }
        return message;
    },
};
var baseBatchUpdateAbExposureRequest = { disableLogging: false };
var BatchUpdateAbExposureRequest = {
    encode: function (message, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        for (var _i = 0, _a = message.abExposureData; _i < _a.length; _i++) {
            var v = _a[_i];
            AbExposureData.encode(v, writer.uint32(10).fork()).ldelim();
        }
        if (message.disableLogging === true) {
            writer.uint32(16).bool(message.disableLogging);
        }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBatchUpdateAbExposureRequest);
        message.abExposureData = [];
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                case 1:
                    message.abExposureData.push(AbExposureData.decode(reader, reader.uint32()));
                    break;
                case 2:
                    message.disableLogging = reader.bool();
                    break;
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (object) {
        var message = __assign({}, baseBatchUpdateAbExposureRequest);
        message.abExposureData = [];
        if (object.abExposureData !== undefined && object.abExposureData !== null) {
            for (var _i = 0, _a = object.abExposureData; _i < _a.length; _i++) {
                var e = _a[_i];
                message.abExposureData.push(AbExposureData.fromPartial(e));
            }
        }
        if (object.disableLogging !== undefined && object.disableLogging !== null) {
            message.disableLogging = object.disableLogging;
        }
        else {
            message.disableLogging = false;
        }
        return message;
    },
};
var baseBatchUpdateAbExposureResponse = {};
var BatchUpdateAbExposureResponse = {
    encode: function (_, writer) {
        if (writer === void 0) { writer = _m0.Writer.create(); }
        return writer;
    },
    decode: function (input, length) {
        var reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
        var end = length === undefined ? reader.len : reader.pos + length;
        var message = __assign({}, baseBatchUpdateAbExposureResponse);
        while (reader.pos < end) {
            var tag = reader.uint32();
            switch (tag >>> 3) {
                default:
                    reader.skipType(tag & 7);
                    break;
            }
        }
        return message;
    },
    fromPartial: function (_) {
        var message = __assign({}, baseBatchUpdateAbExposureResponse);
        return message;
    },
};
var globalThis = (function () {
    if (typeof globalThis !== "undefined")
        return globalThis;
    if (typeof self !== "undefined")
        return self;
    if (typeof window !== "undefined")
        return window;
    if (typeof global !== "undefined")
        return global;
    throw "Unable to locate global object";
})();
if (_m0.util.Long !== Long) {
    _m0.util.Long = Long;
    _m0.configure();
}

var ExposureServiceBatchUpdateAbExposureDesc = {
    methodName: "BatchUpdateAbExposure",
    service: {
        serviceName: "snapchat.ab.exposure_service.ExposureService",
    },
    requestStream: false,
    responseStream: false,
    requestType: {
        serializeBinary: function () {
            return BatchUpdateAbExposureRequest.encode(this).finish();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    },
    responseType: {
        deserializeBinary: function (data) {
            return __assign(__assign({}, BatchUpdateAbExposureResponse.decode(data)), { toObject: function () {
                    return this;
                } });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    },
};
/**
 * Logs a batch of A/B exposures.
 * Wrapper around https://github.sc-corp.net/Snapchat/pb_schema/blob/aada2286467319341cec0759f6d8e7d45e1b0307/proto/ab_service/exposure_service.proto#L16
 */
function batchUpdateAbExposure(request, metadata) {
    var rpc = getContextValidated().rpc;
    return rpc.unary(ExposureServiceBatchUpdateAbExposureDesc, BatchUpdateAbExposureRequest.fromPartial(request), metadata);
}

var ConfigContext = createContext(null);
var ConfigContextProvider = ConfigContext.Provider;
var useConfigFromContext = function (configId) {
    var context = useContext(ConfigContext);
    if (!context) {
        throw new Error("Context is not set. Please add a ConfigProvider in your app");
    }
    var potentialConfigs = context.configs[configId];
    return potentialConfigs
        ? getEvaluatedCofConfig(potentialConfigs, configId)
        : undefined;
};
var useOverride = function (configId) {
    var context = useContext(ConfigContext);
    if (!context) {
        throw new Error("Context is not set. Please add a ConfigProvider in your app");
    }
    return context.overrides.get(configId);
};
var useIsExposureLoggingDisabled = function () {
    var context = useContext(ConfigContext);
    if (!context) {
        throw new Error("Context is not set. Please add a ConfigProvider in your app");
    }
    return context.isExposureLoggingDisabled;
};
var useCustomExposureLogger = function () {
    var context = useContext(ConfigContext);
    if (!context) {
        throw new Error("Context is not set. Please add a ConfigProvider in your app");
    }
    return context.customExposureLogger;
};

/**
 * NOOP = default value doesn't do anything
 * CONSOLE = log to console, useful for debugging
 */
var CustomExposureLogger;
(function (CustomExposureLogger) {
    CustomExposureLogger[CustomExposureLogger["NOOP"] = 0] = "NOOP";
    CustomExposureLogger[CustomExposureLogger["CONSOLE"] = 1] = "CONSOLE";
})(CustomExposureLogger || (CustomExposureLogger = {}));
var ConfigProvider = function (_a) {
    var configs = _a.configs, children = _a.children, _b = _a.disableExposureLogging, disableExposureLogging = _b === void 0 ? false : _b, _c = _a.customExposureLogger, customExposureLogger = _c === void 0 ? CustomExposureLogger.NOOP : _c, enableOverrides = _a.enableOverrides;
    var normalizedConfigs = useMemo(function () {
        var configMap = {};
        configs.forEach(function (config) {
            if (!configMap[config.configId]) {
                configMap[config.configId] = [config];
            }
            else {
                configMap[config.configId].push(config);
            }
        });
        return configMap;
    }, [configs]);
    var overrides = useMemo(function () {
        var _a;
        if (!enableOverrides) {
            return new Map();
        }
        var queryParams = new URLSearchParams(window.location.search);
        var overrides = (_a = queryParams.get("overrides")) !== null && _a !== void 0 ? _a : "";
        var overridePairs = overrides
            .split(",")
            .map(function (pairs) { return pairs.split(":"); });
        return new Map(overridePairs);
    }, [enableOverrides, window.location.search]);
    return (React.createElement(ConfigContextProvider, { value: {
            configs: normalizedConfigs,
            overrides: overrides,
            isExposureLoggingDisabled: disableExposureLogging,
            customExposureLogger: customExposureLogger,
        } }, children));
};

function isConfigAnExperiment(config) {
    return Boolean(config && config.studyName && config.experimentId);
}
function isRuidMissing(config) {
    // 0 is a valid ruidType => RuidType.UNSET, empty string is not a valid ruidValue
    return config.ruidType == null || !config.ruidValue;
}
function convertRuidToIdType(ruidType) {
    switch (ruidType) {
        case Ruid_Type.AD_ACCOUNT:
            return IdType.AD_ACCOUNT;
        case Ruid_Type.AD_MODERATION_AD:
        case Ruid_Type.AM_MEMBER:
            return IdType.AM_MEMBER;
        case Ruid_Type.AM_CLIENT:
            return IdType.AM_CLIENT;
        case Ruid_Type.AM_ORGANIZATION:
            return IdType.AM_ORGANIZATION;
        case Ruid_Type.AM_PROFILE:
            return IdType.AM_PROFILE;
        case Ruid_Type.AM_SESSION:
            return IdType.AM_SESSION;
        case Ruid_Type.AM_SNAPCHAT_USER:
            return IdType.AM_SNAPCHAT_USER;
        case Ruid_Type.SNAPCHAT_ADVERTISING:
            return IdType.SNAPCHAT_ADVERTISING;
        case Ruid_Type.USER:
        case Ruid_Type.USERNAME:
            return IdType.WEB_SNAPCHAT_USER;
        case Ruid_Type.WEB_CLIENT:
            return IdType.WEB_CLIENT;
        case Ruid_Type.WEB_SNAPCHAT_USER:
            return IdType.WEB_SNAPCHAT_USER;
        case Ruid_Type.DEVICE:
        case Ruid_Type.INTERNAL:
        case Ruid_Type.UNRECOGNIZED:
        case Ruid_Type.UNSET:
            return IdType.UNRECOGNIZED;
        default: {
            return IdType.UNRECOGNIZED;
        }
    }
}
var useConfigValue = function (configId) {
    var override = useOverride(configId);
    var config = useConfigFromContext(configId);
    var isExposureLoggingDisabled = useIsExposureLoggingDisabled();
    var customExposureLogger = useCustomExposureLogger();
    useEffect(function () {
        if (override ||
            isExposureLoggingDisabled ||
            !isConfigAnExperiment(config) ||
            isRuidMissing(config)) {
            // skip logging the exposure
            return;
        }
        var request = {
            abExposureData: [
                {
                    id: {
                        type: convertRuidToIdType(config.ruidType),
                        value: config.ruidValue,
                    },
                    properties: {
                        userAgentString: navigator.userAgent,
                    },
                    loggingType: LoggingType.CLIENT_TRIGGER,
                    studyTreatmentInfo: [
                        { studyName: config.studyName, treatmentId: config.experimentId },
                    ],
                },
            ],
        };
        if (customExposureLogger === CustomExposureLogger.CONSOLE) {
            console.log("Logging exposure event to console " + JSON.stringify(request));
        }
        batchUpdateAbExposure(request);
    }, [override, config, isExposureLoggingDisabled, customExposureLogger]);
    // TODO: improve the useOverride hook to recognize multiple data types and map then to the Values object
    if (override) {
        return { stringValue: override };
    }
    return config === null || config === void 0 ? void 0 : config.value;
};

export { BenchmarkName, ClientTargetingExpression_Operator as ClientTargetingExpressionOperator, ClientTargetingExpression_PredicateOperator as ClientTargetingExpressionPredicateOperator, ClientTargetingExpression_Property as ClientTargetingExpressionProperty, ConfigProvider, ConfigResult_ServePlatform as ConfigResultServePlatform, CustomExposureLogger, IdType, LoggingType, Namespace, Ruid_Type as RuidType, batchUpdateAbExposure, getEvaluatedCofConfig, initConfigsClient, registerPropertyValueProvider, targetingQuery, useConfigValue };
//# sourceMappingURL=index.esm.js.map
